import { markMirrorOnlyCybos } from "./core/cybo-roster-provisional.svelte.js";
import { agentCreateInput } from "./agent-create-input.js";
import {
  listAccountSessions,
  revokeOtherDeviceSessions,
  revealDeviceSessionIp,
  listSecurityEvents,
  type SecurityEventsQuery,
  revokeDeviceSession,
} from "./device-sessions-api.js";
import type {
  ProviderAccountSwitchResponse,
  ProviderUsageRefreshResponse,
  ProviderUsageResponse,
} from "./components/providers-usage/provider-usage-types.js";
import type { RelayCapabilities } from "./ws-client-types.js";
import { decidePairing, getPairing, startPairing } from "./pairing-api.js";
import type { ProviderSignInRequest, ProviderSignInState } from "./provider-sign-in.js";
import type { GhAuthStatus, GhLoginRequest, GhLoginState } from "./gh-login.js";
// cyborg:list_schedules: each flag is absent from an older relay, which reads as unsupported.
export interface ScheduleListing {
  schedules: ScheduleView[];
  supportsScheduleDestination?: boolean;
  supportsContinueSession?: boolean;
}

// Mirrors CyboMovePreviewResponse payload in server/cyborg/cyborg-messages.ts
// (the UI does not import server types). providerAvailable null = unknown.
export interface CyboMovePreviewResponse {
  requestId: string;
  openSessionCount: number;
  scheduleCount: number;
  targetOnline: boolean;
  provider: string | null;
  model: string | null;
  providerAvailable: boolean | null;
  // Server-decided ownership outcome; all optional (an older relay omits them).
  ownerIsMember?: boolean;
  ownershipChanges?: boolean;
  newOwnerId?: string;
  newOwnerName?: string;
  usesOwnerLogin?: boolean;
  fromUsesOwnerLogin?: boolean;
  losesAccess?: boolean;
  // The move would be refused: the agent's owner can't run agents on that Worker.
  refusal?: "worker_access_required";
  // The move turns the agent's "Bypass all permissions" off on that Worker.
  clearsBypass?: boolean;
}
import { SlackClient, TransportError } from "./core/client.js";
import { fetchOrTransportError } from "./core/transport-failure.js";
import { CLOUD_RELAY_HTTP_BASE } from "./core/relay-config.js";
import { reportUiFailure, shouldReport } from "./report-failure.js";
import {
  confirmGithubInstall,
  type GithubConfirmOutcome,
  type PendingGithubInstall,
} from "./integrations/github-install-return.js";
import type {
  LicensePayload,
  BillingIntentPayload,
  BillingPlatformPayload,
} from "./core/client.js";
import {
  listAccountEmails,
  startAddEmail,
  verifyAddEmail,
  removeAccountEmail,
  setPrimaryAccountEmail,
} from "./user-emails.js";
import type { AccountEmail } from "./user-emails.js";
import type {
  Agent,
  AgentPermissionResponse,
  AgentPromptReceipt,
  AgentSlashCommand,
  ProviderInfo,
  Cybo,
  CyboReadiness,
} from "./plugins/agents/types.js";
import type { ProviderSubagentDescriptor } from "./plugins/agents/provider-subagents.js";
import type { AgentActivity } from "./plugins/agents/agent-activity.js";
import type { AgentProcessTail } from "./plugins/agents/agent-process-output.svelte.js";
import type { DaemonUpdateResponse } from "./components/daemon/daemon-update-model.js";
import { expandCoalescedRows } from "./plugins/agents/coalesced-rows.js";
import type {
  InstallableProviderId,
  ProviderInstallResult,
  ProviderVersionResult,
} from "./provider-setup.js";

// Includes the private runtime download, provider install, and verification.
// The client waits longer than the daemon's ten-minute setup watchdog.
const PROVIDER_INSTALL_TIMEOUT_MS = 630_000;
// A check probes a binary and one registry lookup; the daemon bounds both under 20 s.
const PROVIDER_CHECK_TIMEOUT_MS = 30_000;
import type {
  Channel,
  ChannelMember,
  ChannelSlackGuest,
  Attachment,
  ScheduledMessage,
  Message,
  Cycle,
  Module,
  TaskView,
  TaskLink,
  TaskAttachment,
  MessageCard,
} from "./core/types.js";
import type { ChannelRegime, AutonomyLevel } from "./core/autonomy.js";
import type { TerminalSocket } from "./components/terminal/terminal-transport.js";

// Wire type declarations live in the sibling ws-client-types.ts (knowledge/87
// Phase 0). Imported here for the class body's own use; re-exported below so
// existing `import type { X } from "../ws-client.js"` call-sites are unchanged.
import type {
  CyboPromiseWire,
  MemoryComplianceCounts,
  CyboMemoryFileWire,
  MemoryVersionWire,
  IntegrationDecline,
  CyboMemoryFileDetailWire,
  CyboMemoryVenue,
  CyboOrderWire,
  ScheduleView,
  ScheduleRunView,
  ScheduleMutatedPayload,
  RecipeView,
  ProviderCredentialPayload,
  ProviderAuthMeta,
  SharedFilesPage,
  RecentSession,
  ProjectCyboRole,
  ProjectAutonomySettings,
  CyboLoopChannel,
  CyboLoopSelfStart,
  PmRunLedgerEvent,
  SessionContextBundle,
  TerminalDirectoryEntry,
  TerminalDirectoryPull,
  BrowserSessionDirEntry,
  ArchivedSession,
  ResumeOverrides,
  Project,
  ChannelProject,
  UserStatusEntry,
  CyborgEventMap,
  LicensePoolResponse,
  LicenseAllocationResponse,
  BillingDetailsResponse,
  GithubRepoSync,
  GithubInstallationRepo,
  GithubInstallation,
  GithubPrStateMapping,
  GithubUserConnection,
  SlackInstallation,
  SlackInviteBulkResult,
  SlackInviteCandidatesResponse,
  SlackPostAsMe,
  SlackPostAsMeReason,
  SlackUserConnection,
  SlackChannelLink,
  SlackLinkPrivacyAudit,
  SlackConversation,
  IntegrationStatusMappingInput,
  JiraInstallation,
  JiraProjectSync,
  JiraStatusesResult,
  JiraBindResult,
  JiraImportResult,
  ClickUpInstallation,
  ClickUpProjectSync,
  ClickUpStatusMapping,
  ClickUpStatusesResult,
  ClickUpBindResult,
  ClickUpImportResult,
  LinearInstallation,
  LinearTeamPickerEntry,
  LinearProjectSync,
  LinearStatusMapping,
  LinearStatusesResult,
  LinearBindResult,
  AsanaInstallation,
  AsanaProjectSync,
  AsanaStatusMapping,
  AsanaStatusesResult,
  AsanaBindResult,
  TeamsInstallation,
  TeamsJoinedTeam,
  TeamsChannelList,
  TeamsConsentState,
  TeamsChannelLink,
  EverhourStatus,
  EverhourConnectedStatus,
  SetupFlowOp,
  SetupFlowResponse,
  MemberSetupSignals,
  OwnerDigestSettings, // [lane:w2-owner-digest]
  OwnerDigestSettingsView, // [lane:w2-owner-digest]
  OwnerDigestCardData, // [lane:w2-owner-digest]
  OwnerDigestCardItem, // [lane:w2-owner-digest]
  CyboCiWatchView, // [lane:w3-ci-triggers]
  AboutYouNoteWire, // [lane:w3-memory-review]
  MemoryDiaryDayWire, // [lane:w3-memory-review]
  MemoryDiaryEntryWire, // [lane:w3-memory-review]
  MemoryDiaryKind, // [lane:w3-memory-review]
  SkillSuggestionWire, // [lane:w3-memory-review]
  SkillBundleWire,
  AgentActivityHistory, // [lane:w3-activity]
  AgentActivityRange, // [lane:w3-activity]
  CompanyHit,
  GuestActionAdminView,
  AgentLiveState,
  HeldPromptOp,
  HeldPromptOpResult,
} from "./ws-client-types.js";
export type {
  CyboPromiseWire,
  MemoryComplianceCounts,
  CyboMemoryFileWire,
  MemoryVersionWire,
  IntegrationDecline,
  CyboMemoryFileDetailWire,
  CyboMemoryVenue,
  CyboOrderWire,
  ScheduleView,
  ScheduleRunView,
  ScheduleMutationOp,
  ScheduleMutatedPayload,
  RecipeView,
  ProviderCredentialPayload,
  ProviderAuthMeta,
  SharedFile,
  SharedFilesPage,
  RecentSession,
  ProjectCyboRole,
  ProjectAutonomySettings,
  CyboLoopChannel,
  CyboLoopSelfStart,
  PmRunLedgerEvent,
  SessionContextMcpServer,
  SessionContextBundle,
  TerminalDirectoryEntry,
  TerminalDirectoryPull,
  BrowserSessionDirEntry,
  ArchivedSession,
  ResumeOverrides,
  Project,
  ChannelProject,
  PresenceUpdatePayload,
  UserStatusEntry,
  UserStatusChangedPayload,
  ActivityReadChangedPayload,
  MessageUnfurledPayload,
  MessageCardUpdatedPayload,
  SaveMessagePayload,
  TaskEventPayload,
  AuditCategory,
  AuditEventPayload,
  CyborgEventMap,
  LicensePoolPayload,
  LicenseAllocationEntry,
  OwnedWorkspaceEntry,
  LicensePoolResponse,
  LicenseAllocationResponse,
  BillingDetailsResponse,
  BillingInvoicePayload,
  GithubRepoSync,
  GithubInstallationRepo,
  GithubInstallation,
  GithubPrStateMapping,
  GithubUserConnection,
  SlackInstallation,
  SlackInviteBulkResult,
  SlackInviteCandidate,
  SlackInviteCandidatesResponse,
  SlackPostAsMe,
  SlackPostAsMeReason,
  SlackUserConnection,
  SlackChannelLink,
  SlackLinkPrivacyAudit,
  SlackConversation,
  IntegrationSourceStatus,
  IntegrationStatusMappingInput,
  JiraInstallation,
  JiraProjectSync,
  JiraStatusMapping,
  JiraStatusesResult,
  JiraWebhookResult,
  JiraBindResult,
  JiraImportResult,
  ClickUpInstallation,
  ClickUpProjectSync,
  ClickUpStatusMapping,
  ClickUpStatusesResult,
  ClickUpBindResult,
  ClickUpImportResult,
  LinearInstallation,
  LinearTeamPickerEntry,
  LinearProjectSync,
  LinearStatusMapping,
  LinearStatusesResult,
  LinearBindResult,
  AsanaInstallation,
  AsanaProjectSync,
  AsanaStatusMapping,
  AsanaStatusesResult,
  AsanaBindResult,
  TeamsInstallation,
  TeamsJoinedTeam,
  TeamsChannel,
  TeamsChannelList,
  TeamsConsentState,
  TeamsChannelLink,
  EverhourAccount,
  EverhourStatus,
  EverhourConnectedStatus,
  SetupFlowOp,
  SetupFlowResponse,
  MemberSetupSignals,
  OwnerDigestSettings, // [lane:w2-owner-digest]
  OwnerDigestSettingsView, // [lane:w2-owner-digest]
  OwnerDigestCardData, // [lane:w2-owner-digest]
  OwnerDigestCardItem, // [lane:w2-owner-digest]
  CyboCiWatchView, // [lane:w3-ci-triggers]
  AboutYouNoteWire, // [lane:w3-memory-review]
  MemoryDiaryDayWire, // [lane:w3-memory-review]
  MemoryDiaryEntryWire, // [lane:w3-memory-review]
  MemoryDiaryKind, // [lane:w3-memory-review]
  SkillSuggestionWire, // [lane:w3-memory-review]
  SkillBundleWire,
  AgentActivityHistory, // [lane:w3-activity]
  AgentActivityRange, // [lane:w3-activity]
  CompanyHit,
  GuestActionAdminView,
  GuestActionLogEntry,
} from "./ws-client-types.js";

/** update_cybo's answer. The post-write grants/bypass/autonomy (C2) let the client settle a toggle
 *  from the reply alone; absent (an older relay) = unchanged. */
export interface UpdatedCybo {
  id: string;
  slug: string;
  name: string;
  provider: string;
  model?: string | null;
  platformPermissions?: string[];
  bypassPermissions?: boolean | null;
  autonomyLevel?: string | null;
}

export type {
  LicensePayload,
  BillingIntentPayload,
  BillingActionPayload,
  BillingPlatformPayload,
} from "./core/client.js";
export { LicenseRequiredError, UpgradeRequiredError } from "./core/client.js";

/** Result of `reconcileIap`: the fresh license, or why there is none (already reported). */
export type IapReconcileResult =
  | { ok: true; license: LicensePayload | null }
  | { ok: false; reason: "http"; status: number }
  | { ok: false; reason: "exception" };

/** Result of `cyborg:clone_agent` — see server/cyborg/agent-clone.ts. */
export interface CloneAgentResult {
  agentId: string;
  sourceAgentId: string;
  providerSessionId?: string;
  /** The clone carries the visible history but no provider-side context. */
  historyOnly: boolean;
  timelineRows: number;
}

/** `cyborg:list_directory`'s answer. Everything past the original three fields is
 *  OPTIONAL: absent means an older daemon that cannot report it, which is never the
 *  same as `false` — that distinction is what keeps each affordance dark. */
export interface DirectoryListingResponse {
  path: string;
  parent: string | null;
  entries: Array<{ name: string; path: string; isGitRepo?: boolean }>;
  /** Dot-directories WITHHELD from `entries`; 0 when they were included. */
  hiddenCount?: number;
  canCreateFolder?: boolean;
}

/**
 * Where the Slack OAuth callback may land the browser, by NAME. Mirrors RETURN_PATHS in
 * routes/slack-oauth.ts, which is a fixed lookup table and not a path parameter: the
 * callback is unauthenticated and issues a 302, so a caller-authored path there is an open
 * redirect. An unrecognised name is simply dropped by the relay.
 *
 * "onboarding" and "settings" land the same static "you can close this window" page; they
 * are two names because the SIGNED STATE records which flow asked. "session" / "popup" land
 * it too, told how its window was opened (OAuthReturnName).
 */
export type SlackReturnTarget = OAuthReturnName | "settings";

/** A sign-in's return page by NAME (oauth-return.ts): "onboarding" the close-this page, "session"
 *  the Mac sign-in window's cyborg7://connect-done, "popup" the web popup that closes itself. */
export type OAuthReturnName = "onboarding" | "session" | "popup";

interface MirrorCoverage {
  life?: string;
  verified?: boolean;
  coverage?: { unguaranteedBelow: number };
  reset?: boolean;
}

/** A non-2xx from the Slack/Teams routes, keeping the relay's machine `code` and `current`. */

// What an agent page says about work sessions (ws-unify B2); absent from older peers.
function workSessionPageFields(resp: {
  workSessionTags?: boolean;
  workSessionAgents?: unknown;
  workSessionFirstSeq?: unknown;
}): { workSessionTags?: boolean; workSessionAgents?: string[]; workSessionFirstSeq?: number } {
  return {
    ...(resp.workSessionTags === true ? { workSessionTags: true } : {}),
    ...(Array.isArray(resp.workSessionAgents)
      ? { workSessionAgents: resp.workSessionAgents.filter((a) => typeof a === "string") }
      : {}),
    ...(typeof resp.workSessionFirstSeq === "number"
      ? { workSessionFirstSeq: resp.workSessionFirstSeq }
      : {}),
  };
}

export class ApiCodedError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly current?: { cyborgChannelId: string; name: string } | null,
  ) {
    super(message);
    this.name = "ApiCodedError";
  }
}

// A mirror page's fields (knowledge/125). A life counts only when an owner announcement
// vouches for it: an unverified page names none, so it can never replace a life on screen.
// What a first read's page says of itself: the prompts it served, and whether it is a forward
// page (newer rows follow from nextCursor). Absent from an older daemon.
function openPageFields(resp: { recentPrompts?: unknown; hasNewer?: unknown }): {
  recentPrompts?: number;
  hasNewer?: boolean;
} {
  return {
    ...(typeof resp.recentPrompts === "number" ? { recentPrompts: resp.recentPrompts } : {}),
    ...(typeof resp.hasNewer === "boolean" ? { hasNewer: resp.hasNewer } : {}),
  };
}

export function mirrorCoverageFields(resp: {
  life?: string;
  verified?: boolean;
  coverage?: { unguaranteedBelow?: unknown };
  reset?: boolean;
}): MirrorCoverage {
  const below = resp.coverage?.unguaranteedBelow;
  const vouched = typeof resp.life === "string" && resp.life !== "" && resp.verified !== false;
  return {
    ...(vouched ? { life: resp.life } : {}),
    ...(typeof resp.verified === "boolean" ? { verified: resp.verified } : {}),
    ...(typeof below === "number" && below > 0 ? { coverage: { unguaranteedBelow: below } } : {}),
    ...(resp.reset === true ? { reset: true } : {}),
  };
}

// Straight-through wire→event map for handleExtensionMessage (#995): a
// `cyborg:<wire>` frame whose handling is just `emit(<event>, payload)` lives here,
// so the dispatch stays table-driven instead of one branch per type.
const SIMPLE_PASSTHROUGH: Record<string, keyof CyborgEventMap> = {
  "cyborg:agent_stream": "agent_stream",
  "cyborg:agent_timeline_reset": "agent_timeline_reset",
  // The daemon's authoritative per-agent lifecycle push (idle|running|error).
  // It was already broadcast and already scoped/fanned out to guests — the UI
  // just never listened, leaving the roster's `lifecycle` with NO live source
  // and a stale listAgents snapshot able to pin an agent at "running" forever.
  // Lands here (before seq-gap detection, like every passthrough) which is
  // correct for it: the frame is a latest-wins per-agent state, not an ordered
  // stream, so a gap costs nothing — the next transition re-reports the truth.
  "cyborg:agent_status": "agent_status",
  // CYBORG-1416: goal / background tasks / retry of a session, pushed to its owner on change.
  "cyborg:agent_activity": "agent_activity",
  // A background process's output lines, to the session owner while its modal is open (#4861).
  "cyborg:agent_process_output": "agent_process_output",
  // CYBORG-1416: a provider notice worth a toast (context nearly full, model switched, login required).
  "cyborg:agent_notice": "agent_notice",
  // Provider-subagent updates (Codex/OpenCode/Claude). The daemon forwards its
  // AgentManager `provider_subagent` events as the protocol-native
  // `agent.provider_subagents.update` frame (session.ts) whose payload IS the
  // discriminated ProviderSubagentUpdate the store's applyUpdate consumes.
  "agent.provider_subagents.update": "provider_subagent",
  // A provider subagent asked for a tool permission (#1612): the daemon surfaces
  // it on the workspace attributed to the child, so the UI can label + answer it
  // (routed back via cyborg:subagent_permission_response). Payload is
  // { workspaceId, parentAgentId, subagentId, request }.
  "cyborg:subagent_permission_request": "subagent_permission_request",
  // CYBORG-884: the daemon's verdict on one of THIS client's prompts (see the type).
  "cyborg:agent_prompt_verdict": "agent_prompt_verdict",
  // PR #3402: the daemon holds this prompt behind the running turn (not a verdict; one follows).
  "cyborg:agent_prompt_queued": "agent_prompt_queued",
  // The relay changed the state of a message between two nodes (sessions / agents).
  "cyborg:message_edge_changed": "message_edge_changed",
  "cyborg:agent_prompt_queue": "agent_prompt_queue",
  "cyborg:agent_usage_limit": "agent_usage_limit",
  "cyborg:task_event": "task_event",
  "cyborg:audit_event": "audit_event",
  "cyborg:agent_not_run": "agent_not_run",
  "cyborg:presence_update": "presence",
  "cyborg:user_status_changed": "user_status_changed",
  "cyborg:memory_changed": "memory_changed",
  "cyborg:activity_read_changed": "activity_read_changed",
  "cyborg:message_unfurled": "message_unfurled",
  // A signed card action settled server-side (#600): the re-rendered card arrives
  // here so the interactive card updates live instead of staying stale until reopen.
  "cyborg:message_card_updated": "message_card_updated",
  // The posting turn ended: its summary (and any missing-permission notice) lands on its posts.
  "cyborg:message_work_updated": "message_work_updated",
  // A cybo work_session was created / status-changed / agent-changed (CYBORG-791).
  // The relay mirrored it to PG and fans this out (DM sessions only to the initiator)
  // so the sidebar/chips update live. Payload is { session }.
  "cyborg:work_session_changed": "work_session_changed",
  // Personal save toggled on another of my devices (#609) — keep the Saved view
  // in sync. Per-user only; never carries another user's bookmarks.
  "cyborg:save_message_broadcast": "save_message",
  // A built-in recipe install changed — re-list the integrations "Built-in" section.
  "cyborg:recipes_changed": "recipes_changed",
  // A schedule run fired / changed status — merged into the run-history store so an
  // open schedule detail reflects "Running" → "Succeeded" live (no reopen/poll).
  "cyborg:schedule_run_recorded": "schedule_run_recorded",
  // A provider CLI install's live stage, so the install step shows which one is running.
  "cyborg:install_provider_cli_progress": "provider_install_progress",
};

// Resuming an archived session (restore_session / import_session) is far slower
// than a normal RPC: on the daemon, handleRestoreSession SPAWNS the provider
// (Claude SDK process boot) and replays the WHOLE transcript
// (hydrateTimelineFromProvider) BEFORE it emits restore_session_response. For a
// real, day-old Claude session that easily exceeds the 15s default request
// timeout — so a slow-but-successful resume rejected with "Request
// cyborg:restore_session timed out" while the daemon was still resuming
// (orphaning the now-live agent the user never navigated to). Give these two RPCs
// a generous window so the success response can land. 90s comfortably covers a
// cold provider boot + long-transcript hydrate without hanging the UI forever on
// a genuinely dead resume.
const RESUME_SESSION_TIMEOUT_MS = 90_000;
// The agent DM open's read to the first unread (untilTs) carries the whole unread
// history in one page: ~860KB for 31 tool-heavy turns, ~17s on a 50KB/s link, so the
// default 15s wait dropped a page that was arriving and the "New" line opened mid-reply.
const UNREAD_READ_TIMEOUT_MS = 60_000;
// Six provider calls at their 10 s limit, per organization provider, plus the relay's own reads.
const PROVIDER_USAGE_READ_TIMEOUT_MS = 70_000;
const PROVIDER_SWITCH_TIMEOUT_MS = 45_000;
function unreadReadTimeout(untilTs: number | undefined): [] | [number] {
  return untilTs === undefined ? [] : [UNREAD_READ_TIMEOUT_MS];
}
// The relay's terminal start (15s) plus its shell proof (20s), with slack.
const REPAIR_INSTALL_TIMEOUT_MS = 45_000;

/** POST /api/teams/install-app. `installed:false` (+ `review`, `message`) means the app was
 *  queued for the tenant admin's review and is NOT in the team yet. */
export interface TeamsInstallAppResult {
  ok: true;
  appId: string;
  alreadyInstalled: boolean;
  installed?: boolean;
  review?: "new" | "update";
  message?: string;
}

const REAUTH_TIMEOUT = "reauth_timeout";
// Terminal frames are high-frequency: a throwing listener reports once per window.
const LISTENER_REPORT_WINDOW_MS = 60_000;

export class CyborgClient extends SlackClient<CyborgEventMap> {
  // Per-type listeners for the terminal stream (#673). The terminal protocol is
  // high-frequency and keyed by terminalId, not a request/response, so it can't
  // ride the typed SlackEventMap; TerminalView subscribes through terminalSocket()
  // and handleExtensionMessage fans the inbound frames out here.
  private readonly terminalListeners = new Map<
    string,
    Set<(payload: Record<string, unknown>) => void>
  >();

  // A minimal TerminalSocket (terminal-transport.ts shape) over this client, so
  // relayTerminalTransport can drive a cloud terminal without the transport
  // module importing the concrete client.
  terminalSocket(): TerminalSocket {
    return {
      send: (message) => this.send(message),
      on: (type, handler) => {
        let set = this.terminalListeners.get(type);
        if (!set) {
          set = new Set();
          this.terminalListeners.set(type, set);
        }
        set.add(handler);
        return () => {
          set?.delete(handler);
          // Drop the empty key so the map doesn't accrue dead Sets over many
          // terminal sessions.
          if (set && set.size === 0) this.terminalListeners.delete(type);
        };
      },
      // Fire when the socket RE-establishes after a drop (#48 BUG-2). The base
      // client emits `connection: connected` on every open INCLUDING the first,
      // so we gate on having seen a prior down (`disconnected`/`reconnecting`).
      // The daemon-scoped terminal stream is ephemeral (not part of seq-based
      // history sync), so on reconnect the daemon never re-binds the pty to the
      // new socket — the view must re-issue attach(). We defer the handler until
      // the socket re-authenticates, otherwise the relay drops the attach send
      // and it times out into a frozen (or worse, freshly re-spawned) terminal.
      onReconnect: (handler) => {
        let down = false;
        // Keep waiting through a slow re-auth while the socket stays up.
        const fireWhenAuthenticated = (): void => {
          void this.whenAuthenticated(15_000)
            .then(() => handler())
            .catch((err: unknown) => {
              reportUiFailure(
                "terminal.reconnect_auth_timeout",
                err,
                {},
                { code: REAUTH_TIMEOUT, failureKind: "transient" },
              );
              if (!down && subscribed) fireWhenAuthenticated();
            });
        };
        let subscribed = true;
        const off = this.on("connection", (data) => {
          if (data.status === "disconnected" || data.status === "reconnecting") {
            down = true;
            return;
          }
          if (data.status === "connected" && down) {
            down = false;
            fireWhenAuthenticated();
          }
        });
        return () => {
          subscribed = false;
          off();
        };
      },
      // Fire on a DAEMON↔relay flap (offline→online) for `daemonId` while THIS
      // view's guest socket stays OPEN (knowledge/54 FIX-1, the headline fix). On a
      // daemon-side flap `onReconnect` above NEVER fires (the guest socket never
      // dropped), yet the daemon stopped streaming during the gap and never
      // re-pushes a snapshot — so the terminal freezes / dead-ends. The relay
      // already broadcasts cyborg:daemon_status_broadcast → the typed `daemon_status`
      // event; we fire `handler` on the offline→online edge (a first `online` after
      // any `offline`) for THIS daemonId so the view re-subscribes (reattachLive).
      // Gated on whenAuthenticated for symmetry with onReconnect: the guest socket
      // usually stayed up, but if BOTH flapped this keeps the re-subscribe send from
      // racing re-auth and being dropped by the relay.
      onDaemonReconnect: (daemonId, handler, startOffline) => {
        let sawOffline = startOffline ?? false;
        return this.on("daemon_status", (data) => {
          if (data.daemonId !== daemonId) return;
          if (data.status === "offline") {
            sawOffline = true;
            return;
          }
          if (data.status === "online" && sawOffline) {
            sawOffline = false;
            void this.whenAuthenticated(15_000)
              .then(() => handler())
              .catch((err: unknown) => {
                // Re-auth never landed within the window — the next status flip or
                // a manual nav retries. Never throw into the emitter.
                reportUiFailure(
                  "terminal.daemon_reconnect_auth_timeout",
                  err,
                  { terminalDaemonId: daemonId },
                  { code: REAUTH_TIMEOUT },
                );
              });
          }
        });
      },
      // Fire the instant the guest socket DROPS (disconnected/reconnecting) so the
      // terminal view disables its accessory bar honestly instead of pretending the
      // pty is still interactive while sends throw "Not connected". Recovery is
      // driven by onReconnect above (which re-subscribes once re-authenticated).
      onDisconnect: (handler) => {
        return this.on("connection", (data) => {
          if (data.status === "disconnected" || data.status === "reconnecting") handler();
        });
      },
    };
  }

  // Resolve once the socket is authenticated on the CURRENT connection, or
  // reject after `timeoutMs`. The base client's waitForAuth is private, so this
  // mirrors it via the public `authenticated` flag for the terminal reconnect
  // gate (terminalSocket().onReconnect). Polls rather than event-subscribes
  // because there is no public auth-success event; the cadence is coarse since
  // it only runs on the reconnect path, not in steady state.
  private whenAuthenticated(timeoutMs: number): Promise<void> {
    if (this.authenticated) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      const startedAt = Date.now();
      const tick = () => {
        if (this.authenticated) return resolve();
        if (Date.now() - startedAt >= timeoutMs) {
          return reject(new Error("terminal reconnect: re-auth timed out"));
        }
        setTimeout(tick, 100);
      };
      setTimeout(tick, 100);
    });
  }

  // ─── Agent methods ──────────────────────────────────────────────

  async createAgent(
    workspaceId: string,
    provider: string,
    cwd: string,
    opts?: {
      model?: string;
      systemPrompt?: string;
      channelId?: string;
      title?: string;
      daemonId?: string;
    },
  ): Promise<Agent> {
    const resp = await this.request<{ agent: Agent }>("cyborg:create_agent", {
      workspaceId,
      provider,
      cwd,
      ...opts,
    });
    return resp.agent;
  }

  async listAgents(workspaceId: string): Promise<Agent[]> {
    const resp = await this.request<{ agents: Agent[] }>("cyborg:list_agents", { workspaceId });
    return resp.agents;
  }

  // Same RPC as listAgents, but keeps the relay's ADDITIVE `noDaemonAnswered`
  // flag (task_3c5914ed): true when the fan-out reached daemons but NONE
  // answered within the relay's fallback window (all hung/dead) — so an empty
  // list is a timeout, not a genuinely empty workspace, and re-asking can't
  // help until a daemon comes back. Used by the cold-start retry loop
  // (retryAgentsOnEmpty) to stop instead of storming a hung daemon. Absent
  // (→ false) on older relays and on daemon-answered/PG-fallback responses.
  // `pendingDaemons` is the PARTIAL-answer signal (2026-08-20): daemons the
  // fan-out reached that stayed silent past the relay's window, so their agents
  // are missing from `agents` even though the list is non-empty. Callers that
  // REPLACE the roster must union on it (nextAgentSnapshot) or they wipe every
  // row homed on a merely-slow daemon. Absent (→ []) on older relays.
  // `daemonId`: that daemon's rows only (the relay aggregates a fan-out of one).
  async listAgentsWithMeta(
    workspaceId: string,
    daemonId?: string,
  ): Promise<{ agents: Agent[]; noDaemonAnswered: boolean; pendingDaemons: string[] }> {
    const resp = await this.request<{
      agents: Agent[];
      noDaemonAnswered?: boolean;
      pendingDaemons?: string[];
    }>("cyborg:list_agents", daemonId ? { workspaceId, daemonId } : { workspaceId });
    return {
      agents: resp.agents,
      noDaemonAnswered: resp.noDaemonAnswered === true,
      pendingDaemons: Array.isArray(resp.pendingDaemons)
        ? resp.pendingDaemons.filter((d): d is string => typeof d === "string")
        : [],
    };
  }

  // The relay's mirror answers these two at once, without a daemon: a fresh browser's first rows.
  async listAgentsMirror(workspaceId: string): Promise<Agent[]> {
    const resp = await this.request<{ agents: Agent[] }>("cyborg:list_agents", { workspaceId, mirrorOnly: true }); // prettier-ignore
    return resp.agents;
  }

  async fetchCybosMirror(workspaceId: string): Promise<Cybo[]> {
    const resp = await this.request<{ cybos: Cybo[]; fromMirrorOnly?: boolean }>("cyborg:fetch_cybos", { workspaceId, mirrorOnly: true }); // prettier-ignore
    return resp.fromMirrorOnly === true ? markMirrorOnlyCybos(resp.cybos) : resp.cybos;
  }

  // Daemon-owner audit listing (#993): ALL sessions on ONE daemon (incl. ephemeral
  // + other users'), gated on the `admin` daemon scope (owner implicit). Distinct
  // from listAgents — the result must stay in the daemon-detail's LOCAL state, never
  // the global agents store that feeds the chat sidebar. Rows carry ephemeral /
  // internal badges absent on the scoped list.
  async listDaemonSessions(workspaceId: string, daemonId: string): Promise<Agent[]> {
    const resp = await this.request<{ sessions: Agent[] }>("cyborg:list_daemon_sessions", {
      workspaceId,
      daemonId,
    });
    return resp.sessions;
  }

  // Start a daemon-scoped terminal session (terminal epic part 3, #656).
  //
  // Matches the merged #657 contract exactly:
  //   send    cyborg:start_terminal { workspaceId, daemonId?, cwd?, cols, rows }
  //   receive cyborg:start_terminal_response { ok, terminalId?, error? }
  // The session is daemon-scoped (NOT persisted to PG); its I/O streams over
  // cyborg:terminal_output / _input / _resize / _kill (wired by TerminalView,
  // #655), keyed by terminalId. cols/rows here are a seed — TerminalView sends a
  // real resize once its xterm fit() runs.
  async startTerminal(
    workspaceId: string,
    opts: { daemonId?: string; cwd?: string; cols: number; rows: number },
  ): Promise<{ terminalId: string }> {
    // start_terminal_response is fanned out to terminalListeners (see
    // handleExtensionMessage), NOT to the request() resolver — so using
    // this.request() here hangs forever ("Request cyborg:start_terminal timed
    // out"): the interceptor consumes the response before the pending request can
    // see it. Correlate it through the terminal socket instead, exactly like
    // relayTerminalTransport.start() does (#656/#673 path collision).
    const requestId = `term_${crypto.randomUUID()}`;
    const sock = this.terminalSocket();
    return new Promise<{ terminalId: string }>((resolve, reject) => {
      let off: (() => void) | undefined;
      const timer = setTimeout(() => {
        off?.();
        reject(new Error("Failed to start terminal session (timed out)"));
      }, 15_000);
      off = sock.on("cyborg:start_terminal_response", (payload) => {
        if (payload.requestId !== requestId) return;
        clearTimeout(timer);
        off?.();
        // Untrusted WS JSON — validate the fields we consume, not just the cast.
        if (payload.ok === true && typeof payload.terminalId === "string") {
          resolve({ terminalId: payload.terminalId });
        } else {
          reject(
            new Error(
              typeof payload.error === "string"
                ? payload.error
                : "Failed to start terminal session",
            ),
          );
        }
      });
      sock.send({ type: "cyborg:start_terminal", requestId, workspaceId, ...opts });
    });
  }

  // Pull the daemon's tracked terminal DIRECTORY for a workspace (terminal CLI-UI
  // unification). Unlike startTerminal (whose *_response is intercepted by the
  // terminalListeners fan-out), list_terminals_response is NOT in that whitelist,
  // so the normal request() correlation resolves it. Returns the owner's live
  // sessions — including any started out-of-band (CLI, another client) — so the
  // sidebar can render terminals it didn't itself open.
  async listTerminals(
    workspaceId: string,
    opts?: { daemonId?: string },
  ): Promise<TerminalDirectoryPull> {
    const resp = await this.request<{
      terminals: TerminalDirectoryEntry[];
      answeredDaemonIds?: string[];
      completeDaemonIds?: string[];
      incompleteDaemonIds?: string[];
      sparedIds?: string[];
      workspaceDaemonIds?: string[];
      complete?: boolean;
    }>("cyborg:list_terminals", {
      workspaceId,
      ...(opts?.daemonId ? { daemonId: opts.daemonId } : {}),
    });
    const terminals: TerminalDirectoryPull = [...(resp.terminals ?? [])];
    // A targeted pull is one daemon's own reply, which the relay forwards unmerged.
    if (Array.isArray(resp.answeredDaemonIds)) terminals.answeredDaemonIds = resp.answeredDaemonIds;
    else if (opts?.daemonId) terminals.answeredDaemonIds = resp.complete === false ? [] : [opts.daemonId]; // prettier-ignore
    if (Array.isArray(resp.completeDaemonIds)) terminals.completeDaemonIds = resp.completeDaemonIds;
    else if (opts?.daemonId && resp.complete === true)
      terminals.completeDaemonIds = [opts.daemonId];
    if (Array.isArray(resp.incompleteDaemonIds))
      terminals.incompleteDaemonIds = resp.incompleteDaemonIds;
    if (Array.isArray(resp.sparedIds)) terminals.sparedIds = resp.sparedIds;
    if (Array.isArray(resp.workspaceDaemonIds))
      terminals.workspaceDaemonIds = resp.workspaceDaemonIds;
    return terminals;
  }

  // #browser-agent — pull the daemon's live BROWSER session directory for a
  // workspace, the Browser tab's list. Sessions live on the daemon (never in PG),
  // so this is a daemon round-trip like listTerminals; the relay records the
  // requester and stamps the response back to them alone, and the daemon
  // owner-scopes what it returns.
  async listBrowserSessions(
    workspaceId: string,
    opts?: { daemonId?: string },
  ): Promise<BrowserSessionDirEntry[]> {
    const resp = await this.request<{ sessions: BrowserSessionDirEntry[] }>(
      "cyborg:list_browser_sessions",
      { workspaceId, ...(opts?.daemonId ? { daemonId: opts.daemonId } : {}) },
    );
    return resp.sessions ?? [];
  }

  // Subscribe to the PUSH half of that feed (cyborg:browser_sessions_changed): the
  // daemon re-pushes the owner's full snapshot whenever a session opens or ends —
  // including the ends nobody asked for (tab closed, consent revoked, daemon
  // stopped) — so the list never shows a session that is already gone. Returns an
  // unsubscribe fn; the caller filters by workspaceId.
  onBrowserSessionsChanged(
    handler: (payload: { workspaceId: string; sessions: BrowserSessionDirEntry[] }) => void,
  ): () => void {
    return this.on("browser_sessions_changed", (payload) =>
      handler(payload as { workspaceId: string; sessions: BrowserSessionDirEntry[] }),
    );
  }

  // Fires when the terminal directory may have moved while nobody could tell us: this
  // socket came back, or a daemon went online. The caller bounds the pulls it causes.
  onTerminalDirectoryStale(handler: () => void): () => void {
    const offSocket = this.terminalSocket().onReconnect?.(handler);
    const online = new Set<string>();
    const offDaemon = this.on("daemon_status", (data) => {
      if (data.status !== "online") {
        online.delete(data.daemonId);
      } else if (!online.has(data.daemonId)) {
        online.add(data.daemonId);
        handler();
      }
    });
    return () => {
      offSocket?.();
      offDaemon();
    };
  }

  // Subscribe to the daemon's PUSH directory feed (cyborg:terminals_changed): the
  // daemon broadcasts the full workspace snapshot to the owner on every terminal
  // start/exit, so a session opened or killed out-of-band updates the sidebar live.
  // Returns an unsubscribe fn. Rides the terminalSocket() fan-out (the frame is
  // whitelisted in handleExtensionMessage). The caller filters by workspaceId.
  onTerminalsChanged(
    handler: (payload: {
      workspaceId: string;
      terminals: TerminalDirectoryEntry[];
      daemonId?: string;
      complete?: boolean;
      sparedIds?: string[];
    }) => void,
  ): () => void {
    return this.terminalSocket().on("cyborg:terminals_changed", (payload) =>
      handler(
        payload as unknown as {
          workspaceId: string;
          terminals: TerminalDirectoryEntry[];
          daemonId?: string;
          complete?: boolean;
          sparedIds?: string[];
        },
      ),
    );
  }

  // Subscribe to the per-user terminal-alias change feed (cyborg:terminal_alias_changed):
  // the server fans a rename/clear out to the user's OTHER clients (owner-scoped),
  // so a rename done on one device updates the rest live. An empty `alias` means
  // the alias was cleared. Returns an unsubscribe fn; rides the terminalSocket()
  // fan-out (the frame is whitelisted in handleExtensionMessage).
  onTerminalAliasChanged(
    handler: (payload: { terminalId: string; alias: string }) => void,
  ): () => void {
    return this.terminalSocket().on("cyborg:terminal_alias_changed", (payload) =>
      handler(payload as unknown as { terminalId: string; alias: string }),
    );
  }

  // `workspaceId` is the VIEWED workspace (CYBORG-682): the relay reads it as
  // `inner.workspaceId` and uses it as the daemon-in-workspace target, instead of
  // defaulting to the first-subscribed workspace (which doesn't track the viewed
  // one, so a daemon in the viewed workspace was rejected). The base client holds
  // no ambient workspace, so it must be passed in. Omit it (undefined) to keep the
  // relay's first-subscribed fallback — JSON.stringify drops the key off the frame.
  async listProviders(opts?: { daemonId?: string; workspaceId?: string }): Promise<ProviderInfo[]> {
    const resp = await this.request<{ providers: ProviderInfo[] }>("cyborg:list_providers", opts);
    return resp.providers;
  }

  // N12 Providers tab. The relay serves the read; a refresh is forwarded to that one daemon, whose
  // answer (or the relay's daemon_timeout at 10 s) lands before the client's own 15 s limit.
  async fetchProviderUsage(p: {
    workspaceId: string;
    daemonId?: string;
    includeOrganization?: boolean;
    organizationOnly?: boolean;
  }): Promise<ProviderUsageResponse> {
    // The page shows slowness itself at 15 s; the request stays open past the relay's worst provider path.
    return this.request<ProviderUsageResponse>("cyborg:fetch_provider_usage", p, PROVIDER_USAGE_READ_TIMEOUT_MS); // prettier-ignore
  }

  async refreshProviderUsage(p: {
    workspaceId: string;
    daemonId: string;
  }): Promise<ProviderUsageRefreshResponse> {
    return this.request<ProviderUsageRefreshResponse>("cyborg:refresh_provider_usage", p);
  }

  // The daemon's 30 s switcher limit plus its re-read land before this one.
  async switchProviderAccount(p: {
    workspaceId: string;
    daemonId: string;
    accountKey: string;
    confirmed: boolean;
  }): Promise<ProviderAccountSwitchResponse> {
    return this.request<ProviderAccountSwitchResponse>("cyborg:switch_provider_account", p, PROVIDER_SWITCH_TIMEOUT_MS); // prettier-ignore
  }

  // Turn an ACP catalog row into a real provider on ONE machine.
  //
  // `catalogId` is the row's id and nothing else. The daemon looks the command up
  // in its OWN compile-time catalog, so no argv crosses this wire — the same rule
  // installProviderCli's `provider` follows above, and for the same reason: a
  // client-supplied string that reached a command line would be arbitrary code
  // execution on the daemon owner's machine.
  //
  // `daemonId` is non-optional so a caller with no effective daemon is a COMPILE
  // error here rather than a confusing relay rejection at runtime — this writes a
  // SPECIFIC host's persisted config and the relay will not pick one for you.
  async addAcpProvider(
    workspaceId: string,
    daemonId: string,
    catalogId: string,
  ): Promise<{
    ok: boolean;
    providerId: string;
    available?: boolean;
    /** The binary looked for on PATH. `null` = nothing to look for (npx-style);
     *  absent = an older daemon that does not report it. */
    binary?: string | null;
    error?: string | null;
  }> {
    return this.request("cyborg:add_acp_provider", { workspaceId, daemonId, catalogId });
  }

  // addAcpProvider's other half, under the same rules: a CATALOG ID on the wire,
  // never argv, and a non-optional `daemonId` because it rewrites ONE host's config.
  // Gate the call on daemonSupportsAcpRemove — an older daemon answers nothing at
  // all, so an ungated call sits on the 15 s timer instead of refusing. An override
  // the daemon did not write is refused with `cyborg:error`, i.e. a REJECTION here.
  async removeAcpProvider(
    workspaceId: string,
    daemonId: string,
    catalogId: string,
  ): Promise<{
    ok: boolean;
    providerId: string;
    /** False when there was nothing persisted to remove — a second click, or another
     *  client got there first. The asked-for end state holds, so `ok` is still true. */
    removed: boolean;
    error?: string | null;
  }> {
    return this.request("cyborg:remove_acp_provider", { workspaceId, daemonId, catalogId });
  }

  async listRecentCwds(opts?: { daemonId?: string }): Promise<{ home: string; recent: string[] }> {
    return this.request<{ home: string; recent: string[] }>("cyborg:list_recent_cwds", opts);
  }

  /** Settings -> Integrations: the integration offers this person tapped "Not now" on
   *  that are STILL suppressing cards, newest first. Pass `clearToolkit` to release one
   *  first — the refreshed list comes back either way, so a clear needs no second read.
   *  The record lives in the answering daemon's own settings, never in the cloud, and
   *  the rows are selected server-side by the caller's own identity. */
  async fetchIntegrationDeclines(
    workspaceId: string,
    clearToolkit?: string,
  ): Promise<{ declines: IntegrationDecline[]; cleared?: number }> {
    return this.request<{ declines: IntegrationDecline[]; cleared?: number }>(
      "cyborg:integration_declines",
      clearToolkit ? { workspaceId, clearToolkit } : { workspaceId },
    );
  }

  // Browse subdirectories of a path on the daemon's host (folder picker in the
  // Create Session dialog). `path: ""`/`"~"` resolves to host $HOME; `entries`
  // are subdirectories only; `parent` is the up-one-level path (null at a root).
  // Dot-directories are withheld SERVER-side unless `includeHidden` asks for them —
  // a client cannot reveal rows it was never sent.
  async listDirectory(opts: {
    path: string;
    daemonId?: string;
    includeHidden?: boolean;
  }): Promise<DirectoryListingResponse> {
    return this.request<DirectoryListingResponse>("cyborg:list_directory", opts);
  }

  // The browser's New folder. `name` is ONE segment and never a path — the daemon
  // refuses a separator, `..` or a NUL before the filesystem sees it — and `parent`
  // resolves exactly like listDirectory's `path`. `created: false` means it already
  // existed, which IS the asked-for end state, so `ok` stays true.
  async createDirectory(opts: { parent: string; name: string; daemonId?: string }): Promise<{
    ok: boolean;
    /** The new folder's absolute path, so the browser can navigate straight into it. */
    path: string | null;
    created: boolean;
    error?: string | null;
  }> {
    return this.request("cyborg:create_directory", opts);
  }

  async sendAgentPrompt(
    workspaceId: string,
    agentId: string,
    prompt: string,
    attachments?: Attachment[],
    // /pages parity (CYBORG-309): a client-selected page id reference. The relay
    // resolves it server-side to a PageCard (transcript) + body (agent context).
    pageRef?: string,
    // The work session this prompt belongs to (CYBORG-794): when set, the relay routes
    // the prompt straight to the session's agent so the reply stays in the session and
    // nothing is posted to the anchor channel/thread.
    workSessionId?: string,
    // Active-turn steering (CYBORG-823): when the target agent is BUSY, "steer" JOINs
    // the live turn (message injected into the running turn) and "interrupt" cancels
    // the current turn and starts fresh; absent = a busy daemon holds it (CYBORG-1560).
    // If the server can't honor a steer it falls through to the normal (busy) path.
    activeTurnBehavior?: "interrupt" | "steer",
    // CYBORG-884: a client-generated id for THIS prompt. The owning daemon echoes it
    // back on cyborg:agent_prompt_verdict with "accepted" or "unavailable", which is how
    // a join learns whether it landed. The request's own requestId cannot serve — the
    // ack is sent before routing and the relay's forward carries no request id.
    promptId?: string,
    // CYBORG-884: draw and echo nothing before the verdict — no bubble here, no DM row at
    // the relay. Set by Stop's wind-down instruction AND by a send-now.
    silent?: boolean,
    // CYBORG-1016: the send-now's other half — the human DID write this, so the owning
    // daemon persists it as their own message once the verdict says it reached the model.
    persistOnVerdict?: boolean,
    // The relay's ack carries the attempt it minted for this send (P03). The caller
    // stores it beside its own promptId so a reload can ask the owning daemon what
    // became of the prompt. Absent from a relay older than the mint, and from a
    // direct daemon connection, where the ack is the daemon's own.
    // `messageId`: the DM row the relay wrote for this prompt — what a reaction to it
    // addresses. Absent when no row is written (session-continue, silent) or from an
    // older relay.
    // `redirectedTo` (#4166 M1): the relay delivered the prompt to the sender's own DM session
    // because this one belongs to a channel; the caller moves there. Absent from an older relay.
  ): Promise<{
    promptAttemptId?: string;
    messageId?: string;
    redirectedTo?: { agentId: string; reason: "channel_bound_session"; cyboId?: string };
  }> {
    return await this.request<{
      promptAttemptId?: string;
      messageId?: string;
      redirectedTo?: { agentId: string; reason: "channel_bound_session"; cyboId?: string };
    }>("cyborg:send_agent_prompt", {
      workspaceId,
      agentId,
      prompt,
      ...(attachments && attachments.length > 0 ? { attachments } : {}),
      ...(pageRef ? { pageRef } : {}),
      ...(workSessionId ? { workSessionId } : {}),
      ...(activeTurnBehavior ? { activeTurnBehavior } : {}),
      ...(promptId ? { promptId } : {}),
      ...(silent ? { silent } : {}),
      ...(persistOnVerdict ? { persistOnVerdict } : {}),
    });
  }

  /**
   * Forward a message into one of your own sessions: a reference only. The relay builds the card
   * from what you may read and the session's daemon fences it as somebody else's words; `note`
   * is the only instruction. The original's files go with it.
   */
  async forwardToSession(
    workspaceId: string,
    agentId: string,
    messageId: string,
    note: string,
  ): Promise<{ messageId?: string }> {
    return await this.request<{ messageId?: string }>("cyborg:send_agent_prompt", {
      workspaceId,
      agentId,
      prompt: note,
      forwardRef: { messageId },
    });
  }

  /**
   * Ask the OWNING daemon what became of a prompt (P03). The answer is the admission
   * ledger's own row, so a reload can tell "it landed" from "it never left" instead
   * of offering Retry on a message the agent already answered. `null` means the
   * daemon holds no record for this attempt.
   */
  async fetchAgentPromptReceipt(
    workspaceId: string,
    agentId: string,
    promptId: string,
    promptAttemptId?: string,
    workSessionId?: string,
  ): Promise<AgentPromptReceipt | null> {
    const response = await this.request<{ receipt: AgentPromptReceipt | null }>(
      "cyborg:fetch_agent_prompt_receipt",
      {
        workspaceId,
        agentId,
        promptId,
        ...(promptAttemptId ? { promptAttemptId } : {}),
        ...(workSessionId ? { workSessionId } : {}),
      },
    );
    return response.receipt ?? null;
  }

  // Invoke a signed card button (#600): echo the opaque token back; the server
  // verifies signature + actor + expiry before executing. The RESPONSE carries
  // the server's resolved `card` on success (and on already-resolved/expired) —
  // the caller settles from it immediately so the button never waits solely on
  // the (raceable) message_card_updated broadcast (CYBORG-792).
  async sendMessageAction(
    workspaceId: string,
    messageId: string,
    actionId: string,
    token: string,
    // what the clicker typed with the click (a decline note, a typed confirm)
    input?: string,
  ): Promise<{ ok: boolean; error?: string; card?: MessageCard | null }> {
    return this.request<{ ok: boolean; error?: string; card?: MessageCard | null }>(
      "cyborg:message_action",
      {
        workspaceId,
        messageId,
        actionId,
        token,
        ...(input === undefined ? {} : { input }),
      },
    );
  }

  // Guest asks: an agent's log and mutes.
  async guestActionsAdmin(workspaceId: string, cyboId: string): Promise<GuestActionAdminView> {
    return this.request<GuestActionAdminView>("cyborg:guest_actions_admin", {
      workspaceId,
      cyboId,
    });
  }

  async guestActionMute(
    workspaceId: string,
    cyboId: string,
    guestUserId: string,
    muted: boolean,
  ): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>("cyborg:guest_action_mute", {
      workspaceId,
      cyboId,
      guestUserId,
      muted,
    });
  }

  // One op on a setup_* card in #onboarding; `{op:"get"}` creates it (if eligible) and answers
  // the caller's state.
  async setupFlow(workspaceId: string, op: SetupFlowOp): Promise<SetupFlowResponse> {
    return this.request<SetupFlowResponse>("cyborg:setup_flow", { workspaceId, op });
  }

  // W1-G4: this browser's IANA zone, so my agents say the time where I am (relay-answered; it
  // writes my own row only when the zone changed). Sent on every sign-in; the latest wins.
  reportMyTimeZone(): void {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timeZone) return;
    this.request("cyborg:set_my_time_zone", { timeZone }).catch((err: unknown) => {
      // intentional: an older relay or a daemon-only connection has no answer; my agents then say
      // my zone is unknown, the honest fallback
      console.debug("[time-zone] not reported", err);
    });
  }

  /** My zone, chosen by me in Settings: a device's automatic report never overwrites it. */
  async setMyTimeZone(
    timeZone: string,
  ): Promise<{ ok: boolean; timeZone?: string; error?: string }> {
    return this.request("cyborg:set_my_time_zone", { timeZone, manual: true });
  }

  // [lane:w2-owner-digest] My check-ins (relay-answered; my own rows only).
  async getOwnerDigestSettings(workspaceId: string): Promise<OwnerDigestSettingsView> {
    return this.request<OwnerDigestSettingsView>("cyborg:get_owner_digest_settings", {
      workspaceId,
    });
  }

  async ownerDigestVote(
    workspaceId: string,
    messageId: string,
    itemKey: string,
    vote: 1 | -1,
  ): Promise<{ ok: true; vote: 1 | -1 }> {
    return this.request("cyborg:owner_digest_vote", { workspaceId, messageId, itemKey, vote });
  }

  async setCyboCheckin(
    workspaceId: string,
    cyboId: string,
    everyMin: 0 | 30 | 60,
  ): Promise<{ ok: true; everyMin: number }> {
    return this.request("cyborg:set_cybo_checkin", { workspaceId, cyboId, everyMin });
  }

  // [lane:w3-ci-triggers] The agent's owner only: which repo's pull requests it watches.
  async getCyboCiWatch(workspaceId: string, cyboId: string): Promise<CyboCiWatchView> {
    return this.request<CyboCiWatchView>("cyborg:get_cybo_ci_watch", { workspaceId, cyboId });
  }

  async setCyboCiWatch(
    workspaceId: string,
    cyboId: string,
    repoId: string,
    enabled: boolean,
  ): Promise<CyboCiWatchView> {
    return this.request("cyborg:set_cybo_ci_watch", { workspaceId, cyboId, repoId, enabled });
  }

  // [lane:w3-activity] One agent's activity for the profile Activity tab (relay-answered, gated
  // per viewer there). `cursor` pages the Did list.
  async fetchAgentActivityHistory(
    workspaceId: string,
    cyboId: string,
    range: AgentActivityRange,
    cursor?: string,
  ): Promise<AgentActivityHistory> {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.request<AgentActivityHistory>("cyborg:fetch_agent_activity", {
      workspaceId,
      cyboId,
      range,
      timeZone,
      ...(cursor ? { cursor } : {}),
    });
  }

  // Facts about ME in this workspace (relay-answered): the DM suggestions a new member starts
  // with. A daemon-only connection has no answer.
  async memberSetupSignals(workspaceId: string): Promise<MemberSetupSignals> {
    return this.request<MemberSetupSignals>("cyborg:member_setup_signals", { workspaceId });
  }

  // "Name your workspace" company search, answered by the relay (it asks Brandfetch). Short wait:
  // the screen stays a plain input whenever this rejects (an older relay has no answer).
  async companySearch(query: string): Promise<CompanyHit[]> {
    const res = await this.request<{ companies?: unknown }>(
      "cyborg:company_search",
      { query },
      4_000,
    );
    return Array.isArray(res.companies) ? (res.companies as CompanyHit[]) : []; // cast: the relay's own rows
  }

  // The picked company becomes the workspace logo + website (owner/admin; claims no domain).
  async companyPick(workspaceId: string, hit: CompanyHit): Promise<{ avatarUrl: string | null }> {
    return this.request("cyborg:company_pick", { workspaceId, domain: hit.domain, icon: hit.icon });
  }

  // The setup cards' calls into RPCs other surfaces own, by name (the role write; the
  // daemon's GitHub sign-in). A relay or daemon without one rejects or times out, and the
  // card falls back to Settings.
  async setupRpc<T>(
    type:
      | "cyborg:set_functional_role"
      | "cyborg:set_work_areas"
      | "cyborg:gh_login_start"
      | "cyborg:gh_auth_status",
    params: Record<string, unknown>,
    timeoutMs = 10_000,
  ): Promise<T> {
    return this.request<T>(type, params, timeoutMs);
  }

  // ─── Scheduled messages (#607 — user send-later) ────────────────
  // Queue a chat message to send at a future time. EXACTLY ONE of channelId /
  // toId targets a channel or a DM peer; `sendAt` is epoch ms and must be in the
  // future (the server re-validates). Returns the created row on success.
  async scheduleMessageCreate(params: {
    workspaceId: string;
    channelId?: string;
    toId?: string;
    text: string;
    sendAt: number;
    mentions?: string[];
  }): Promise<{ ok: boolean; op: "create"; message?: ScheduledMessage; error?: string }> {
    return this.request<{
      ok: boolean;
      op: "create";
      message?: ScheduledMessage;
      error?: string;
    }>("cyborg:schedule_message_create", { ...params });
  }

  // List the caller's scheduled messages for a workspace (pending, sent, failed).
  async scheduleMessageList(workspaceId: string): Promise<{ messages: ScheduledMessage[] }> {
    return this.request<{ messages: ScheduledMessage[] }>("cyborg:schedule_message_list", {
      workspaceId,
    });
  }

  // Cancel a still-pending scheduled message. No-op server-side for an already
  // sent/failed row (the response carries the error then).
  async scheduleMessageCancel(
    workspaceId: string,
    id: string,
  ): Promise<{ ok: boolean; op: "cancel"; id: string; error?: string }> {
    return this.request<{ ok: boolean; op: "cancel"; id: string; error?: string }>(
      "cyborg:schedule_message_cancel",
      { workspaceId, id },
    );
  }

  // ─── Recurring cybo schedules (#611/#613/#619) ──────────────────────
  // CRUD + run-history wrappers over the per-daemon ScheduleRunner. All seven
  // RPCs are dual-routed server-side (local dispatcher + cloud relay); the
  // mutating four resolve with the `cyborg:schedule_mutated` ack, list resolves
  // with `cyborg:schedule_list_response`, and run-history with
  // `cyborg:schedule_runs_response`.

  // Create a recurring run for a cybo. `cyboIdOrSlug` picks the cybo (same
  // convention as spawnCybo); `cron` is the cadence; `prompt` is what it runs.
  // `maxRuns: 1` makes a one-shot. Returns the mutation ack (schedule on ok).
  private async taskScheduleDeliveryRefusal(input: {
    workspaceId: string;
    op: "create" | "update";
    taskId?: string | null;
    scheduleId?: string;
  }): Promise<ScheduleMutatedPayload | undefined> {
    const listing = await this.listSchedules(input.workspaceId);
    const taskId =
      input.taskId === undefined
        ? listing.schedules.find((row) => row.id === input.scheduleId)?.taskId
        : input.taskId;
    if (!taskId || listing.supportsScheduleDestination === true) return undefined;
    const err = new Error("Task schedule delivery requires an updated relay and home daemon.");
    reportUiFailure(
      "schedule.delivery_unsupported",
      err,
      { workspaceId: input.workspaceId },
      { code: "unsupported", failureKind: "refusal" },
    );
    return {
      ok: false,
      op: input.op,
      scheduleId: input.scheduleId ?? null, // null: no schedule was created
      error: err.message,
    };
  }

  async createSchedule(params: {
    workspaceId: string;
    cyboIdOrSlug: string;
    cron: string;
    prompt: string;
    destination?: "channel" | "dm";
    channelId?: string | null;
    // Bind this schedule to a task (fired as its assignee cybo, unattended).
    // Omitted/null = a raw-prompt cybo schedule (unchanged).
    taskId?: string | null;
    // Opt in to continuing one of the creator's own sessions (CLI --continue-session).
    continueSession?: string | null;
    timezone?: string | null;
    maxRuns?: number | null;
    catchUp?: boolean;
    daemonId?: string;
  }): Promise<ScheduleMutatedPayload> {
    if (params.taskId) {
      const refusal = await this.taskScheduleDeliveryRefusal({
        workspaceId: params.workspaceId,
        op: "create",
        taskId: params.taskId,
      });
      if (refusal) return refusal;
    }
    return this.request<ScheduleMutatedPayload>("cyborg:create_schedule", { ...params });
  }

  // List the workspace's schedules. `cyboId` optionally narrows to one cybo's
  // schedules (the agent-editor section); omit it for the Tasks-tab board.
  async listSchedules(workspaceId: string, cyboId?: string): Promise<ScheduleListing> {
    return this.request<ScheduleListing>("cyborg:list_schedules", {
      workspaceId,
      ...(cyboId ? { cyboId } : {}),
    });
  }

  // Edit an existing schedule. Only the provided fields change (a cron change
  // recomputes the next run server-side).
  async updateSchedule(params: {
    workspaceId: string;
    scheduleId: string;
    cron?: string;
    prompt?: string;
    destination?: "channel" | "dm";
    channelId?: string | null;
    // Rebind (or unbind, via null) the schedule's task.
    taskId?: string | null;
    // A session id to continue, or null to go back to the short excerpt.
    continueSession?: string | null;
    timezone?: string | null;
    daemonId?: string;
  }): Promise<ScheduleMutatedPayload> {
    if (params.destination !== undefined || params.taskId !== undefined) {
      const refusal = await this.taskScheduleDeliveryRefusal({
        workspaceId: params.workspaceId,
        op: "update",
        taskId: params.taskId,
        scheduleId: params.scheduleId,
      });
      if (refusal) return refusal;
    }
    return this.request<ScheduleMutatedPayload>("cyborg:update_schedule", { ...params });
  }

  // Pause (enabled=false) or resume (enabled=true) a schedule in one RPC.
  async setScheduleEnabled(
    workspaceId: string,
    scheduleId: string,
    enabled: boolean,
    daemonId?: string,
  ): Promise<ScheduleMutatedPayload> {
    return this.request<ScheduleMutatedPayload>("cyborg:set_schedule_enabled", {
      workspaceId,
      scheduleId,
      enabled,
      ...(daemonId ? { daemonId } : {}),
    });
  }

  // Permanently delete a schedule.
  async deleteSchedule(
    workspaceId: string,
    scheduleId: string,
    daemonId?: string,
  ): Promise<ScheduleMutatedPayload> {
    return this.request<ScheduleMutatedPayload>("cyborg:delete_schedule", {
      workspaceId,
      scheduleId,
      ...(daemonId ? { daemonId } : {}),
    });
  }

  // Fire a schedule immediately ("Run now"), independent of its cron clock.
  async runScheduleOnce(
    workspaceId: string,
    scheduleId: string,
    daemonId?: string,
  ): Promise<ScheduleMutatedPayload> {
    return this.request<ScheduleMutatedPayload>("cyborg:run_schedule_once", {
      workspaceId,
      scheduleId,
      ...(daemonId ? { daemonId } : {}),
    });
  }

  // Load a schedule's run history (newest first), capped by `limit` (server max 100).
  async listScheduleRuns(
    workspaceId: string,
    scheduleId: string,
    limit?: number,
    daemonId?: string,
  ): Promise<{ scheduleId: string; runs: ScheduleRunView[] }> {
    return this.request<{ scheduleId: string; runs: ScheduleRunView[] }>(
      "cyborg:list_schedule_runs",
      {
        workspaceId,
        scheduleId,
        ...(limit ? { limit } : {}),
        ...(daemonId ? { daemonId } : {}),
      },
    );
  }

  // CYBORG-1179: run history of several schedules in one call (PG mirror, newest first per
  // schedule). Every asked id has a key; one the caller may not see maps to [].
  async listScheduleRunsBatch(
    workspaceId: string,
    scheduleIds: readonly string[],
    limit?: number,
  ): Promise<{ runs: Record<string, ScheduleRunView[]> }> {
    return this.request<{ runs: Record<string, ScheduleRunView[]> }>(
      "cyborg:list_schedule_runs_batch",
      { workspaceId, scheduleIds: [...scheduleIds], ...(limit ? { limit } : {}) },
    );
  }

  // ─── Built-in integrations (recipes) ────────────────────────────────
  // Enable/disable/list the workspace's built-in automation recipes. enable +
  // disable are forwarded to the daemon (they create/destroy cybos + schedules);
  // list is answered PG-direct on the relay from the mirror, so cloud users see
  // installs even when the daemon is asleep. Mutations also broadcast
  // `cyborg:recipes_changed` so every open client re-lists.

  // Enable (or re-configure) a recipe. `config` carries the recipe's config keys
  // (channel ids, crons, timezone — see lib/integrations/recipes-catalog.ts).
  // Resolves with the resulting install row. Fails with "no daemon connected" if
  // no daemon is hosting the workspace (the recipe's cybo needs a daemon host).
  async enableRecipe(
    workspaceId: string,
    recipeId: string,
    config: Record<string, unknown>,
  ): Promise<{ recipe: RecipeView }> {
    return this.request<{ recipe: RecipeView }>("cyborg:enable_recipe", {
      workspaceId,
      recipeId,
      config,
    });
  }

  // Disable a recipe: tears down its cybo (cascade removes schedules + channel
  // memberships) and marks the install row disabled.
  async disableRecipe(
    workspaceId: string,
    recipeId: string,
  ): Promise<{ recipeId: string; disabled: true }> {
    return this.request<{ recipeId: string; disabled: true }>("cyborg:disable_recipe", {
      workspaceId,
      recipeId,
    });
  }

  // List the workspace's installed recipes (enabled + disabled rows).
  async listRecipes(workspaceId: string): Promise<{ recipes: RecipeView[] }> {
    return this.request<{ recipes: RecipeView[] }>("cyborg:list_recipes", { workspaceId });
  }

  respondToPermission(
    workspaceId: string,
    agentId: string,
    permissionRequestId: string,
    response: AgentPermissionResponse,
  ): void {
    this.send({
      type: "cyborg:agent_permission_response",
      workspaceId,
      agentId,
      permissionRequestId,
      response,
    });
  }

  // Approve/deny a SURFACED provider-subagent permission (#1612); the daemon routes the
  // reply by parentAgentId. CORRELATED, not fire-and-forget (Codex audit, knowledge/113
  // §13.2): the relay acks with `delivered` (owning daemon connected) or errors on a gate
  // denial, so the caller clears the card only on confirmed delivery — not optimistically.
  async respondToSubagentPermission(input: {
    workspaceId: string;
    parentAgentId: string;
    subagentId: string;
    permissionRequestId: string;
    response: AgentPermissionResponse;
  }): Promise<{ delivered: boolean }> {
    if (!this.connected) throw new TransportError("not_connected", "Not connected");
    const ack = await this.request<{ delivered?: boolean }>("cyborg:subagent_permission_response", {
      workspaceId: input.workspaceId,
      parentAgentId: input.parentAgentId,
      subagentId: input.subagentId,
      permissionRequestId: input.permissionRequestId,
      response: input.response,
    });
    return { delivered: ack.delivered === true };
  }

  // Capability negotiation (knowledge/113 E11 / §13.2): ask the relay which optional actions
  // it can serve, so the UI hides ones an old relay can't. An old relay doesn't know this
  // type and answers `cyborg:error` → caught and reported as all-unsupported (fail closed).
  async getRelayCapabilities(): Promise<RelayCapabilities> {
    if (!this.connected) return { detachSubagent: false, legacyDesktopUpdate: false };
    try {
      const r = await this.request<Partial<RelayCapabilities>>("cyborg:get_relay_capabilities", {});
      return {
        detachSubagent: r.detachSubagent === true,
        legacyDesktopUpdate: r.legacyDesktopUpdate === true,
        ...(r.deviceSessions === true ? { deviceSessions: true } : {}),
        ...(r.pairing === true ? { pairing: true } : {}),
        ...(r.externalAgents === true ? { externalAgents: true } : {}),
      };
    } catch {
      // intentional: unknown-type (old relay) or transient failure → fail closed.
      return { detachSubagent: false, legacyDesktopUpdate: false };
    }
  }

  // CORRELATED, not fire-and-forget. The relay has always answered this frame —
  // `cyborg:cancel_agent_ack` on success, `cyborg:error` on every denial — but the
  // old raw `send` carried NO requestId, so both answers were uncorrelatable and
  // landed nowhere: a stop the relay refused (no daemon access, cross-tenant) or
  // could not deliver (owning daemon unreachable) looked exactly like a stop that
  // worked. request() resolves on the ack and rejects on cyborg:error, so the
  // caller can finally tell the difference and say so.
  //
  // The `connected` short-circuit keeps the INSTANT "Not connected" failure the
  // composer relies on: request() would otherwise sit in waitForOpen/waitForAuth
  // for 15s first, and a dropped socket is the most common reason a turn looks
  // wedged in the first place.
  // Take a held prompt back out of a busy agent's shared queue (its author, or the session's owner).
  async withdrawQueuedPrompt(
    workspaceId: string,
    daemonId: string,
    agentId: string,
    promptId: string,
  ): Promise<"withdrawn" | "not_found" | "forbidden"> {
    const res = await this.request<{ outcome: "withdrawn" | "not_found" | "forbidden" }>(
      "cyborg:withdraw_queued_prompt",
      { workspaceId, daemonId, agentId, promptId },
    );
    return res.outcome;
  }

  // Send now, edit or remove a prompt a busy agent holds, by its promptId (CYBORG-1560).
  async heldPromptOp(
    target: { workspaceId: string; daemonId: string; agentId: string; promptId: string },
    op: HeldPromptOp,
  ): Promise<HeldPromptOpResult> {
    return this.request<HeldPromptOpResult>("cyborg:held_prompt_op", { ...target, op });
  }

  async cancelAgent(workspaceId: string, agentId: string): Promise<void> {
    if (!this.connected) throw new TransportError("not_connected", "Not connected");
    const ack = await this.request<{ status?: string; error?: string }>("cyborg:cancel_agent", {
      workspaceId,
      agentId,
    });
    // "canceling" is the ONLY status that means the relay reached the owning
    // daemon. Anything else (today: "undeliverable") is an honest failure the
    // caller must surface, carrying the relay's own reason text.
    if (ack.status !== "canceling") {
      throw new Error(
        typeof ack.error === "string" && ack.error
          ? ack.error
          : "The stop could not be delivered to the agent's daemon.",
      );
    }
  }

  // #591: clear an agent's derived "needs attention" flag (sent when the agent
  // is viewed). Fire-and-forget — the owning daemon clears the authoritative
  // flag and re-broadcasts the agent state.
  clearAgentAttention(workspaceId: string, agentId: string): void {
    if (!this.canSend()) return; // runs from the agent page's effect: never throw into the flush
    this.send({ type: "cyborg:clear_attention", workspaceId, agentId });
  }

  // Cockpit takeover (#cockpit): a human watching THIS session's live agent loop
  // grabs the wheel. STEER interrupts the in-flight turn and redirects it with a
  // new human instruction; STOP hard-cancels it. Keyed on agentId + workspaceId
  // exactly like cancelAgent — fire-and-forget over the same send path. Payloads
  // match CyborgCockpitSteerSchema / CyborgCockpitStopSchema (server
  // cyborg-messages.ts); the daemon gates + audits (initiator-or-owner/admin).
  cockpitSteer(workspaceId: string, agentId: string, instruction: string): void {
    this.send({ type: "cyborg:cockpit_steer", workspaceId, agentId, instruction });
  }

  cockpitStop(workspaceId: string, agentId: string): void {
    this.send({ type: "cyborg:cockpit_stop", workspaceId, agentId });
  }

  // Cockpit SHARE (#cockpit): the session OWNER invites a teammate to co-watch +
  // co-control THIS live session (keyed on the same agentId as steer/stop), or
  // revokes. Unlike the fire-and-forget steer/stop these use request() — the relay
  // is triple-gated + fail-closed (owner-auth + invitee membership + invitee
  // daemon-drive), so the caller NEEDS the ack: request() correlation resolves on
  // `cyborg:cockpit_grant_response`/`cyborg:cockpit_revoke_response { ok, agentId,
  // inviteeUserId }` and REJECTS on the fail-closed `cyborg:error` (e.g. the
  // invitee lacks daemon access) — the caller rolls back its optimistic add.
  // Payloads match CyborgCockpitGrantSchema / CyborgCockpitRevokeSchema (server
  // cyborg-messages.ts: { workspaceId, agentId, inviteeUserId }).
  cockpitGrant(
    workspaceId: string,
    agentId: string,
    inviteeUserId: string,
  ): Promise<{ ok: boolean; agentId: string; inviteeUserId: string }> {
    return this.request("cyborg:cockpit_grant", { workspaceId, agentId, inviteeUserId });
  }

  cockpitRevoke(
    workspaceId: string,
    agentId: string,
    inviteeUserId: string,
  ): Promise<{ ok: boolean; agentId: string; inviteeUserId: string }> {
    return this.request("cyborg:cockpit_revoke", { workspaceId, agentId, inviteeUserId });
  }

  // daemonId targets the agent's OWNING daemon directly (#843 Gap B). Without it
  // the relay must resolve the owner from the daemon_agents PG binding, which is
  // written async on agent_status — so a control RPC fired right after create can
  // race ahead of the binding and hit an arbitrary daemon ("Agent not found").
  // When omitted the relay still falls back to the binding (backward-compatible).
  async setAgentModel(
    workspaceId: string,
    agentId: string,
    modelId: string | null,
    daemonId?: string,
  ): Promise<{ status?: string }> {
    // "superseded": a newer pick replaced this one, whose model must not reach the label.
    return this.request<{ status?: string }>("cyborg:set_agent_model", {
      workspaceId,
      agentId,
      modelId,
      ...(daemonId ? { daemonId } : {}),
    });
  }

  // Reload/restart a wedged session in place (#592): keeps the same agentId and
  // identity, recreating the live session. `rehydrateFromDisk` re-streams the
  // provider history (for a desynced session) instead of preserving the
  // in-memory timeline. Recovers a hung agent without archive/delete.
  async reloadSession(
    workspaceId: string,
    agentId: string,
    opts?: { rehydrateFromDisk?: boolean; cwd?: string },
  ): Promise<{ cwd?: string }> {
    return this.request("cyborg:reload_session", {
      workspaceId,
      agentId,
      rehydrateFromDisk: opts?.rehydrateFromDisk ?? false,
      ...(opts?.cwd === undefined ? {} : { cwd: opts.cwd }),
    });
  }

  async setAgentMode(
    workspaceId: string,
    agentId: string,
    modeId: string,
    daemonId?: string,
  ): Promise<void> {
    await this.request("cyborg:set_agent_mode", {
      workspaceId,
      agentId,
      modeId,
      ...(daemonId ? { daemonId } : {}),
    });
  }

  async setAgentThinking(
    workspaceId: string,
    agentId: string,
    thinkingOptionId: string | null,
    daemonId?: string,
  ): Promise<void> {
    await this.request("cyborg:set_agent_thinking", {
      workspaceId,
      agentId,
      thinkingOptionId,
      ...(daemonId ? { daemonId } : {}),
    });
  }

  // 'Rewind to here' (#649): roll the agent/cybo session back to before this turn.
  // `messageId` is the Paseo timeline user-message id carried on the stream entry.
  // mode defaults server-side to "conversation" (the only mode cybos/pi support).
  async rewindAgent(workspaceId: string, agentId: string, messageId: string): Promise<void> {
    await this.request("cyborg:rewind_agent", { workspaceId, agentId, messageId });
  }

  // CYBORG-1410: run a native agent command (/goal, /compact, ...) on your own session. The
  // name and args are typed fields; the daemon refuses anyone but the session owner.
  async sendAgentCommand(
    workspaceId: string,
    agentId: string,
    name: string,
    args: string,
    clientMsgId: string,
    dmPeerId?: string,
    // CYBORG-1416: "pill" when the header's Clear goal button sent it, for telemetry.
    origin?: "pill",
  ): Promise<void> {
    await this.request("cyborg:agent_command", {
      ...(dmPeerId ? { dmPeerId } : {}),
      ...(origin ? { origin } : {}),
      workspaceId,
      agentId,
      name,
      ...(args ? { args } : {}),
      clientMsgId,
    });
  }

  async listAgentCommands(workspaceId: string, agentId: string): Promise<AgentSlashCommand[]> {
    const resp = await this.request<{ commands: AgentSlashCommand[] }>("cyborg:list_commands", {
      workspaceId,
      agentId,
    });
    return resp.commands;
  }

  // #581: @-file/dir autocomplete. `query` is the text after the `@`; the daemon
  // searches the agent's workspace cwd and returns matching file/dir entries.
  async getAgentDirectorySuggestions(
    workspaceId: string,
    agentId: string,
    query: string,
  ): Promise<{ path: string; kind: "file" | "directory" }[]> {
    const resp = await this.request<{
      entries: { path: string; kind: "file" | "directory" }[];
      error: string | null;
    }>("cyborg:directory_suggestions", { workspaceId, agentId, query });
    return resp.entries;
  }

  async fetchAgentTimeline(
    workspaceId: string,
    agentId: string,
    // `daemonId` (audit viewer, #994): the KNOWN owning daemon of the session the
    // read is opened for. Threaded so the relay routes the fetch to that daemon in
    // cloud multi-daemon mode — required for EPHEMERAL sessions, which the relay
    // can't resolve via daemon_agents. Omitted outside a daemon-audit context.
    // `lastPrompts` (CYBORG-1180): on a first read, ask for the last N prompts in one page.
    opts?: {
      cursor?: string;
      limit?: number;
      direction?: "older" | "newer";
      daemonId?: string;
      lastPrompts?: number;
      workSessionId?: string;
      around?: string; // the page centred on this messageId (deep link); older daemons ignore it
      untilTs?: number; // an older read reaching the first row after it (an unread cursor)
      pagesNewer?: boolean; // the open may answer a forward page from the read turn (hasNewer)
      life?: string | null; // the life on screen (null: none); another life answers `reset`
    },
  ): Promise<{
    items: Record<string, unknown>[];
    nextCursor: string | null;
    hasMore: boolean;
    olderCursor: string | null;
    hasOlder: boolean;
    // The wire stated `hasOlder` (the field above defaults to false when an older daemon omits it).
    hasOlderStated?: boolean;
    // Committed high-water seq of this snapshot for the client's monotonic-read guard;
    // undefined from an older daemon that doesn't send it.
    maxSeq?: number;
    // A forward page: newer rows follow from `nextCursor`; undefined from an older daemon.
    hasNewer?: boolean;
    // Each seq'd item names its work session; the agents holding the asked one (B2).
    workSessionTags?: boolean;
    workSessionAgents?: string[];
    workSessionFirstSeq?: number;
    // Set only when the server answered `lastPrompts`; undefined from an older daemon.
    recentPrompts?: number;
    // The page's first rows are the tail of a reply whose start it could not include;
    // undefined from an older daemon that does not say.
    headPartial?: boolean;
    // The byte budget dropped rows below the page's first row; undefined from an older daemon.
    budgetCut?: boolean;
    // "mirror": served from the relay's PG copy while the owning daemon was away;
    // it can lag, and the daemon's own page is the authority.
    // "blind": the relay could not resolve the owning daemon AND had nothing durable
    // to serve — this empty page is not evidence of end-of-history (CYBORG-1056).
    source?: "mirror" | "blind";
    // The daemon's durable life for this timeline; absent from the mirror and older daemons.
    life?: string;
    // A mirror page's coverage (knowledge/125); absent from daemons and older relays.
    verified?: boolean;
    coverage?: { unguaranteedBelow: number };
    reset?: boolean;
  }> {
    const resp = await this.request<{
      items: Record<string, unknown>[];
      nextCursor: string | null;
      hasMore: boolean;
      olderCursor?: string | null;
      hasOlder?: boolean;
      maxSeq?: number;
      headPartial?: boolean;
      budgetCut?: boolean;
      source?: "mirror" | "blind";
      recentPrompts?: number;
      hasNewer?: boolean;
      life?: string;
      verified?: boolean;
      coverage?: { unguaranteedBelow?: unknown };
      reset?: boolean;
      workSessionTags?: boolean;
      workSessionAgents?: unknown;
      workSessionFirstSeq?: unknown;
    }>(
      "cyborg:fetch_agent_timeline",
      {
        workspaceId,
        agentId,
        cursor: opts?.cursor,
        limit: opts?.limit ?? 100,
        ...(opts?.direction ? { direction: opts.direction } : {}),
        ...(opts?.daemonId ? { daemonId: opts.daemonId } : {}),
        ...(opts?.lastPrompts ? { lastPrompts: opts.lastPrompts } : {}),
        ...(opts?.workSessionId ? { workSessionId: opts.workSessionId } : {}),
        ...(opts && {
          around: opts.around,
          untilTs: opts.untilTs,
          pagesNewer: opts.pagesNewer,
          life: opts.life, // undefined: an older reader's request, no life check
        }),
      },
      ...unreadReadTimeout(opts?.untilTs),
    );
    const page = {
      items: expandCoalescedRows(resp.items),
      ...openPageFields(resp),
      nextCursor: resp.nextCursor,
      hasMore: resp.hasMore,
      olderCursor: resp.olderCursor ?? null,
      hasOlder: resp.hasOlder ?? false,
      hasOlderStated: typeof resp.hasOlder === "boolean",
      maxSeq: resp.maxSeq,
      ...(typeof resp.headPartial === "boolean" ? { headPartial: resp.headPartial } : {}),
      ...(typeof resp.budgetCut === "boolean" ? { budgetCut: resp.budgetCut } : {}),
      ...(resp.source === "mirror" || resp.source === "blind" ? { source: resp.source } : {}),
      ...mirrorCoverageFields(resp),
      ...workSessionPageFields(resp),
    };
    // A page whose cursor named another life (knowledge/125) carries `reset: true` as it
    // came; the cursor reader that asked branches on it (olderPagination, #3629).
    return page;
  }

  // Session-scoped work timeline (cyborg:fetch_session_work): the durable
  // agent_timeline_rows attributed to a work_session, over time. Keyed by `sessionId`
  // (not agentId) and paged with the SAME direction/cursor/limit shape as
  // fetchAgentTimeline, returning the SAME page shape (AgentTimelineItem[] + cursors)
  // so the agent-timeline renderer is unchanged.
  async fetchSessionWork(
    workspaceId: string,
    sessionId: string,
    opts?: { cursor?: string; limit?: number; direction?: "older" | "newer"; daemonId?: string },
  ): Promise<{
    items: Record<string, unknown>[];
    nextCursor: string | null;
    hasMore: boolean;
    olderCursor: string | null;
    hasOlder: boolean;
    // Committed high-water seq of this snapshot for the client's monotonic-read guard;
    // undefined from an older relay/daemon that doesn't send it.
    maxSeq?: number;
  }> {
    const resp = await this.request<{
      items: Record<string, unknown>[];
      nextCursor: string | null;
      hasMore: boolean;
      olderCursor?: string | null;
      hasOlder?: boolean;
      maxSeq?: number;
    }>("cyborg:fetch_session_work", {
      workspaceId,
      sessionId,
      cursor: opts?.cursor,
      limit: opts?.limit ?? 100,
      ...(opts?.direction ? { direction: opts.direction } : {}),
      ...(opts?.daemonId ? { daemonId: opts.daemonId } : {}),
    });
    return {
      items: resp.items,
      nextCursor: resp.nextCursor,
      hasMore: resp.hasMore,
      olderCursor: resp.olderCursor ?? null,
      hasOlder: resp.hasOlder ?? false,
      maxSeq: resp.maxSeq,
    };
  }

  // Read-only session viewer (#994): fetch the captured INJECTED CONTEXT bundle
  // for an ephemeral session (system prompt + tools made available + routed/raw
  // prompt). Returns null for an ordinary (non-ephemeral) agent. Pure read —
  // never attaches/revives the session.
  async fetchSessionContext(
    workspaceId: string,
    agentId: string,
    // `daemonId` (audit viewer, #994): same role as fetchAgentTimeline's — routes
    // the read to the daemon that owns the (possibly ephemeral) session. Omitted
    // outside a daemon-audit context.
    opts?: { daemonId?: string },
  ): Promise<SessionContextBundle | null> {
    const resp = await this.request<{ context: SessionContextBundle | null }>(
      "cyborg:fetch_session_context",
      { workspaceId, agentId, ...(opts?.daemonId ? { daemonId: opts.daemonId } : {}) },
    );
    return resp.context;
  }

  // The relay's message edges of one session/agent (latest 200 it sent or received), under the same
  // read authorization as its timeline. Untyped rows on purpose: message-edge.ts parses and drops what
  // this client does not understand. An older relay answers "unsupported".
  async fetchMessageEdges(workspaceId: string, agentId: string): Promise<{ edges: unknown[] }> {
    return this.request<{ edges: unknown[] }>("cyborg:fetch_message_edges", {
      workspaceId,
      agentId,
    });
  }

  // ─── Cybo methods ───────────────────────────────────────────────

  async createCybo(
    workspaceId: string,
    opts: {
      slug: string;
      name: string;
      soul: string;
      provider: string;
      model?: string;
      description?: string;
      avatar?: string;
      role?: string;
      llmAuthMode?: string;
      behaviorMode?: string;
      homeDaemonId?: string | null;
      autonomyLevel?: string | null;
      monthlySpendCap?: number | null;
      platformPermissions?: string[];
      toolGrants?: Record<string, unknown> | null;
      mcpServers?: Record<string, unknown> | null;
      // Danger toggle: run every tool without approval prompts (trusted
      // sandboxes only). Server support lands in a parallel lane — an older
      // server simply strips the field.
      bypassPermissions?: boolean;
      // Picking "CEO" as the job title sends this true — the server enforces
      // one CEO (isDefault) per workspace. Omitted/false for every other title.
      isDefault?: boolean;
    },
  ): Promise<{
    id: string;
    slug: string;
    name: string;
    provider: string;
    model?: string | null;
    // #636/CYBORG-686: the relay already computes whether anything in the
    // WORKSPACE can run this cybo (cybo-readiness.ts) and sends it back on
    // create_cybo_response. Omitting it here silently dropped the verdict on
    // the floor, so the wizard could only ever say "created" — never "created,
    // but nothing can run it". Optional: an older relay doesn't send it.
    readiness?: CyboReadiness;
  }> {
    agentCreateInput.parse(opts);
    const resp = await this.request<{
      cybo: {
        id: string;
        slug: string;
        name: string;
        provider: string;
        model?: string | null;
        readiness?: CyboReadiness;
      };
    }>("cyborg:create_cybo", { workspaceId, ...opts });
    return resp.cybo;
  }

  async fetchCybos(workspaceId: string): Promise<Cybo[]> {
    const resp = await this.request<{ cybos: Cybo[]; fromMirrorOnly?: boolean }>(
      "cyborg:fetch_cybos",
      { workspaceId },
    );
    return resp.fromMirrorOnly === true ? markMirrorOnlyCybos(resp.cybos) : resp.cybos;
  }

  // ─── Per-cybo memory FILES (S3 — a memory is a file, not a row) ────────────
  // The Memory pane's real data source, and the replacement for the four
  // `*_cybo_memory` row RPCs. S3 moved every memory write to markdown under the
  // cybo's own memory home and left the hand-off explicit: the PG RPCs "still
  // exist and the Memory pane still reads them — it will now read an empty table
  // until it is repointed at this directory." These are that repoint.
  //
  // All four are forwarded by the relay to the cybo's HOME DAEMON (that is where
  // the files are) and answered off its disk. There is no PG mirror in either
  // direction, deliberately — mirroring would rebuild the round-trip whose
  // failure mode S3 replaces.
  //
  // `venue` selects one of the cybo's TWO roots: "shared" (channel memory, every
  // member may read it) or "private" (the CALLER'S OWN DM notes). The daemon
  // resolves "private" from the authenticated caller's address, so no parameter
  // here can reach another person's memory.
  async listCyboMemoryFiles(workspaceId: string, cyboId: string): Promise<CyboMemoryFileWire[]> {
    const resp = await this.request<{ files: CyboMemoryFileWire[] }>(
      "cyborg:list_cybo_memory_files",
      { workspaceId, cyboId },
    );
    return resp.files;
  }

  /** One memory WITH its prose. `null` when it is no longer on disk. */
  async readCyboMemoryFile(
    workspaceId: string,
    cyboId: string,
    venue: CyboMemoryVenue,
    topic: string,
  ): Promise<CyboMemoryFileDetailWire | null> {
    const resp = await this.request<{ file: CyboMemoryFileDetailWire | null }>(
      "cyborg:read_cybo_memory_file",
      { workspaceId, cyboId, venue, topic },
    );
    return resp.file;
  }

  /**
   * The HUMAN write leg (T3), landing in the SAME file the agent's own
   * `cyborg7_remember` writes — so what a person types here is in the agent's
   * context on its next turn. `ifAbsent` marks a CREATE: the daemon refuses
   * rather than replacing a memory it never showed the human.
   */
  async writeCyboMemoryFile(
    workspaceId: string,
    cyboId: string,
    input: {
      venue: CyboMemoryVenue;
      topic: string;
      content: string;
      description?: string;
      memoryType?: string;
      pinned?: boolean;
      ifAbsent?: boolean;
      /** "This channel": the relay checks you are in it and stamps the audience itself. */
      channelId?: string;
    },
  ): Promise<CyboMemoryFileDetailWire> {
    const resp = await this.request<{ file: CyboMemoryFileDetailWire }>(
      "cyborg:write_cybo_memory_file",
      { workspaceId, cyboId, ...input },
    );
    return resp.file;
  }

  /** `false` = there was nothing by that name. Deletes ONE named file, never a sweep. */
  async deleteCyboMemoryFile(
    workspaceId: string,
    cyboId: string,
    venue: CyboMemoryVenue,
    topic: string,
  ): Promise<boolean> {
    const resp = await this.request<{ deleted: boolean }>("cyborg:delete_cybo_memory_file", {
      workspaceId,
      cyboId,
      venue,
      topic,
    });
    return resp.deleted;
  }

  /** Memory v2 6.2 — restore a note the memory worker replaced. Only its owner can. */
  async undoMemoryReplace(
    workspaceId: string,
    cyboId: string,
    venue: CyboMemoryVenue,
    slug: string,
  ): Promise<"restored" | "not_found" | "name_taken"> {
    const resp = await this.request<{ result: "restored" | "not_found" | "name_taken" }>(
      "cyborg:undo_memory_replace",
      { workspaceId, cyboId, venue, slug },
    );
    return resp.result;
  }

  // [lane:w3-episodes-usage] W3-14: the notes one agent message used that THIS viewer may see
  // (relay-gated per viewer; nothing, not even a count, for a message or note they cannot see).
  async fetchMessageMemoryUses(
    workspaceId: string,
    messageId: string,
  ): Promise<{ total: number; uses: MessageMemoryUseWire[] }> {
    return this.request<{ total: number; uses: MessageMemoryUseWire[] }>(
      "cyborg:fetch_message_memory_uses",
      { workspaceId, messageId },
    );
  }

  // [lane:w3-memory-review] Memory review and skill suggestions (relay-answered).
  /** Keep my own untrusted note: it becomes trusted. Throws when it is not mine or already kept.
   *  A row the daemon served has no index id; its file names it. */
  async memoryKeepNote(
    workspaceId: string,
    note: ({ noteId: string } | { cyboId: string; venue: CyboMemoryVenue; slug: string }) & {
      version?: number;
    }, // prettier-ignore
  ): Promise<void> {
    await this.request("cyborg:memory_keep_note", { workspaceId, ...note });
  }

  /** "Uses my shared memory" for one of my agents; `enabled` absent reads it. */
  async cyboAboutYou(workspaceId: string, cyboId: string, enabled?: boolean): Promise<boolean> {
    const resp = await this.request<{ enabled: boolean }>("cyborg:set_cybo_about_you", {
      workspaceId,
      cyboId,
      ...(enabled === undefined ? {} : { enabled }),
    });
    return resp.enabled;
  }

  async listAboutYouNotes(
    workspaceId: string,
  ): Promise<{ ownedAgents: number; notes: AboutYouNoteWire[] }> {
    return this.request("cyborg:list_about_you_notes", { workspaceId });
  }

  async fetchMemoryDiary(
    workspaceId: string,
    cyboId: string,
    days: number,
    timeZone: string,
  ): Promise<MemoryDiaryDayWire[]> {
    const resp = await this.request<{ days: MemoryDiaryDayWire[] }>("cyborg:fetch_memory_diary", {
      workspaceId,
      cyboId,
      days,
      timeZone,
    });
    return resp.days;
  }

  /** One agent's suggestions, or (no cyboId) the workspace's waiting ones this viewer may see. */
  async listSkillSuggestions(
    workspaceId: string,
    cyboId?: string,
    id?: string, // one suggestion (a DM card's own row)
    opts: { withBundles?: true } = {}, // bundle rows come back apart, in `bundles` (an old relay omits it)
  ): Promise<{
    canApprove: boolean;
    suggestions: SkillSuggestionWire[];
    bundles?: SkillBundleWire[];
  }> {
    return this.request("cyborg:list_skill_suggestions", {
      workspaceId,
      ...(cyboId ? { cyboId } : {}),
      ...(id ? { id } : {}),
      ...opts,
    });
  }

  async decideSkillSuggestion(
    workspaceId: string,
    id: string,
    action: "approve" | "dismiss" | "undo" | "ask_admin",
    editedContent?: string,
    shownHash?: string, // a bundle row: the hash of the rendering the person decided on
  ): Promise<{ ok: true; state: SkillSuggestionWire["state"] | SkillBundleWire["state"] }> {
    return this.request("cyborg:decide_skill_suggestion", {
      workspaceId,
      id,
      action,
      ...(editedContent === undefined ? {} : { editedContent }),
      ...(shownHash === undefined ? {} : { shownHash }),
    });
  }

  /** MEM-UI-010 — the promises of one agent this viewer may see (open, broken, fulfilled). */
  async listCyboPromises(workspaceId: string, cyboId: string): Promise<CyboPromiseWire[]> {
    const resp = await this.request<{ promises: CyboPromiseWire[] }>("cyborg:list_cybo_promises", {
      workspaceId,
      cyboId,
    });
    return resp.promises;
  }

  /** MEM-UI-010 — cancel an open promise (its promisee or an owner/admin). */
  async cancelCyboPromise(
    workspaceId: string,
    cyboId: string,
    promiseId: string,
  ): Promise<"schedule" | "task" | null> {
    const resp = await this.request<{
      result: "cancelled" | "not_found";
      kind?: "schedule" | "task";
    }>("cyborg:cancel_cybo_promise", {
      workspaceId,
      cyboId,
      promiseId,
    });
    return resp.result === "cancelled" ? (resp.kind ?? null) : null; // null: nothing was cancelled
  }

  /** UI-006 — set a note's sensitivity (its owner, or an admin for an ownerless note). */
  async setMemorySensitivity(
    workspaceId: string,
    cyboId: string,
    venue: CyboMemoryVenue,
    slug: string,
    sensitivity: "normal" | "restricted" | "never_hint",
  ): Promise<"ok" | "not_found"> {
    const resp = await this.request<{ result: "ok" | "not_found" }>(
      "cyborg:set_memory_sensitivity",
      { workspaceId, cyboId, venue, slug, sensitivity },
    );
    return resp.result;
  }

  /** UI-006 — who changed a note and when, newest first. Empty for a note you cannot see. */
  async listMemoryVersions(
    workspaceId: string,
    cyboId: string,
    venue: CyboMemoryVenue,
    slug: string,
  ): Promise<MemoryVersionWire[]> {
    const resp = await this.request<{ versions: MemoryVersionWire[] }>(
      "cyborg:list_memory_versions",
      { workspaceId, cyboId, venue, slug },
    );
    return resp.versions;
  }

  /** Memory v2 DEL-013/016 — admin erase of one person's memory; `dryRun` previews the counts. */
  async memoryComplianceDelete(
    workspaceId: string,
    userId: string,
    dryRun: boolean,
  ): Promise<MemoryComplianceCounts> {
    const resp = await this.request<{ counts: MemoryComplianceCounts }>(
      "cyborg:memory_compliance_delete",
      { workspaceId, userId, dryRun },
    );
    return resp.counts;
  }

  /**
   * CYBORG-1354 — what a held "May I share this?" draft says. Answered by the holder
   * daemon only while the hold is pending, and only to the person it was raised for.
   */
  async getHeldShareExcerpt(
    workspaceId: string,
    holdId: string,
  ): Promise<{ excerpt: string; truncated: boolean; fullChars: number; source?: string }> {
    return this.request<{
      excerpt: string;
      truncated: boolean;
      fullChars: number;
      source?: string;
    }>("cyborg:get_held_share_excerpt", { workspaceId, holdId });
  }

  // [lane:w2-approvals] An agent's Always-allow rules, answered by the computer it lives on.
  async listToolAllowRules(
    workspaceId: string,
    cyboId: string,
    daemonId?: string | null,
  ): Promise<Array<{ id: string; rule: string; scopeUserId: string; createdAt: number }>> {
    const resp = await this.request<{
      rules: Array<{ id: string; rule: string; scopeUserId: string; createdAt: number }>;
      error?: string;
    }>("cyborg:list_tool_allow_rules", { workspaceId, cyboId, ...(daemonId ? { daemonId } : {}) });
    if (resp.error) throw new Error(resp.error);
    return resp.rules;
  }

  async revokeToolAllowRule(
    workspaceId: string,
    cyboId: string,
    ruleId: string,
    daemonId?: string | null,
  ): Promise<boolean> {
    const resp = await this.request<{ removed?: boolean; error?: string }>(
      "cyborg:revoke_tool_allow_rule",
      { workspaceId, cyboId, ruleId, ...(daemonId ? { daemonId } : {}) },
    );
    if (resp.error) throw new Error(resp.error);
    return resp.removed === true;
  }

  // ─── Standing orders (M1) ──────────────────────────────────────────────────
  // Human-authored binding directives injected into every spawn of a cybo. Same
  // (workspaceId, cyboId) scoping as memory, but the two WRITES are owner/admin
  // only and the issuer is stamped server-side — there is deliberately no
  // `issuedBy` parameter here, and no agent-facing tool at all.
  //
  // `channelId` is the SCOPE, not a filter: omitted lists the cybo-global orders,
  // passed lists global + that channel's — i.e. exactly the set that would inject
  // into a spawn there. `revokeCyboOrder` passes back the scope the order was
  // listed in so the server can re-resolve and reject an out-of-scope id.
  async listCyboOrders(
    workspaceId: string,
    cyboId: string,
    channelId?: string | null,
  ): Promise<CyboOrderWire[]> {
    const resp = await this.request<{ orders: CyboOrderWire[] }>("cyborg:list_cybo_orders", {
      workspaceId,
      cyboId,
      channelId: channelId ?? null,
    });
    return resp.orders;
  }

  async createCyboOrder(
    workspaceId: string,
    cyboId: string,
    body: string,
    channelId?: string | null,
  ): Promise<CyboOrderWire> {
    const resp = await this.request<{ order: CyboOrderWire }>("cyborg:create_cybo_order", {
      workspaceId,
      cyboId,
      body,
      channelId: channelId ?? null,
    });
    return resp.order;
  }

  async revokeCyboOrder(
    workspaceId: string,
    cyboId: string,
    orderId: string,
    channelId?: string | null,
  ): Promise<void> {
    await this.request("cyborg:revoke_cybo_order", {
      workspaceId,
      cyboId,
      orderId,
      channelId: channelId ?? null,
    });
  }

  // ─── Core blocks (T1 / M5) ─────────────────────────────────────────────────
  // Per-user, display-only session alias. Empty alias clears it. Per-user on the
  // server (keyed by the authed guest), so no userId is sent.
  // `workspaceId` is a routing hint so the relay can update this person's other clients.
  async setSessionAlias(agentId: string, alias: string, workspaceId?: string): Promise<void> {
    await this.request("cyborg:set_session_alias", {
      agentId,
      alias,
      ...(workspaceId ? { workspaceId } : {}),
    });
  }

  // A session alias changed on another of this person's clients (an empty alias = cleared).
  onSessionAliasChanged(
    handler: (payload: { agentId: string; alias: string }) => void,
  ): () => void {
    return this.terminalSocket().on("cyborg:session_alias_changed", (payload) =>
      handler(payload as unknown as { agentId: string; alias: string }),
    );
  }

  // A session's SHARED name changed (its owner renamed it; an empty name = cleared). Sent only
  // to the people who may read that session.
  onSessionNameChanged(
    handler: (payload: { workspaceId: string; agentId: string; name: string }) => void,
  ): () => void {
    return this.terminalSocket().on("cyborg:session_name_changed", (payload) =>
      handler(payload as unknown as { workspaceId: string; agentId: string; name: string }),
    );
  }

  async getSessionAliases(): Promise<Record<string, string>> {
    const resp = await this.request<{ aliases: Record<string, string> }>(
      "cyborg:get_session_aliases",
      {},
    );
    return resp.aliases ?? {};
  }

  // Per-user, display-only terminal alias (cross-device synced). Empty alias
  // clears it. Per-user on the server (keyed by the authed guest), so no userId
  // is sent. workspaceId is an optional routing hint so the server can fan the
  // change out to the user's other clients live.
  async setTerminalAlias(terminalId: string, alias: string, workspaceId?: string): Promise<void> {
    await this.request("cyborg:set_terminal_alias", {
      terminalId,
      alias,
      ...(workspaceId ? { workspaceId } : {}),
    });
  }

  // `backed` is the server's "I have a durable alias store" signal. A solo/no-PG
  // daemon returns `backed: false` with an empty map — the caller must NOT replace
  // its localStorage fallback with it. `backed === false` only when explicitly sent;
  // undefined (older servers / relay PG path) defaults to backed, so behavior there
  // is unchanged.
  async getTerminalAliases(): Promise<{ aliases: Record<string, string>; backed: boolean }> {
    const resp = await this.request<{ aliases?: Record<string, string>; backed?: boolean }>(
      "cyborg:get_terminal_aliases",
      {},
    );
    return { aliases: resp.aliases ?? {}, backed: resp.backed !== false };
  }

  // Start a Composio hosted-OAuth connect for one toolkit + binding. Daemon-
  // forwarded to the daemon that holds the Composio key; returns the redirect URL
  // the UI opens in the system browser. Rejects with a clear "not configured on
  // <daemon>" message when the daemon has no Composio key.
  async composioConnect(input: {
    workspaceId: string;
    toolkit: string;
    binding: "caller" | "service";
    daemonId?: string;
    callbackUrl?: string;
  }): Promise<{
    redirectUrl: string;
    transport: "platform" | "router";
    daemonId?: string | null;
  }> {
    return this.request("cyborg:composio_connect", input);
  }

  // End a Composio toolkit connection — the counterpart of composioConnect, forwarded to
  // the same daemon for the same reason (the revoke call needs the Composio key, and the
  // connection row lives there). `caller` ends the signed-in user's own connection;
  // `service` ends the workspace's shared one and is admin/owner-only.
  //
  // `disconnected: false` means there was nothing to end — the same end state, not an
  // error. `upstreamRevoked` says whether the account was also ended AT COMPOSIO; it is
  // false for a connection that never completed its OAuth, and is reported rather than
  // assumed so nothing tells a person their access was revoked when it was not.
  async composioDisconnect(input: {
    workspaceId: string;
    toolkit: string;
    binding: "caller" | "service";
    daemonId?: string;
  }): Promise<{ disconnected: boolean; upstreamRevoked: boolean; daemonId?: string | null }> {
    return this.request("cyborg:composio_disconnect", input);
  }

  // Per-toolkit connected-state for the cybo editor. `configured` is false when the
  // target daemon has no Composio key wired.
  async composioConnectionStatus(input: {
    workspaceId: string;
    toolkits: Array<{ toolkit: string; binding: "caller" | "service" }>;
    daemonId?: string;
  }): Promise<{
    configured: boolean;
    daemonId?: string | null;
    // Signed-in Composio account email (non-secret), so the editor banner can show
    // "connected as <email>" after reopen. Null/absent when no email was recorded.
    accountEmail?: string | null;
    statuses: Array<{ toolkit: string; binding: "caller" | "service"; connected: boolean }>;
  }> {
    return this.request("cyborg:composio_connection_status", input);
  }

  // The provider's whole toolkit catalog for the integrations picker (slug + display
  // name + blurb + Composio's official logo url). Read on the owning daemon (it holds
  // the API key) and cached there for hours. `available` is false when the daemon has
  // no key wired / the provider listing failed — the caller then keeps the curated
  // client-side catalog rather than showing an empty picker. NEVER throws for
  // "unavailable"; only a transport failure rejects.
  async composioToolkits(input: { workspaceId: string; daemonId?: string }): Promise<{
    available: boolean;
    daemonId?: string | null;
    toolkits: Array<{
      slug: string;
      name: string;
      description?: string | null;
      // Optional AND known to 404 for some toolkits (ComposioHQ/composio#3175) —
      // render behind a monogram fallback.
      logo?: string | null;
      categories?: string[];
      // What it takes to connect this one. OPTIONAL: a daemon older than these
      // fields sends none of them, and absent means UNKNOWN, never "cannot" — see
      // isToolkitUnconnectable (composio-toolkits.ts).
      authSchemes?: string[] | null;
      composioManagedAuthSchemes?: string[] | null;
      noAuth?: boolean | null;
      // The daemon's Composio project registered its own credential (e.g. an OAuth app).
      projectAuthConfigured?: boolean | null;
    }>;
  }> {
    return this.request("cyborg:composio_toolkits", input);
  }

  // One toolkit's action catalog for the per-action picker, split the way the SPAWN
  // path splits it: `defaultActions` is what an `allowedActions: []` grant resolves
  // to today, `droppedByCap` is what the 30-action cap silently leaves out (pick
  // those by hand or the cybo never gets them), `excludedCount` is the write/manage
  // tail no UI-made grant may contain. `available:false` ⇒ no key / provider outage:
  // hide the picker and leave the grant on its default set. Never throws for
  // "unavailable"; only a transport failure rejects.
  async composioToolkitActions(input: {
    workspaceId: string;
    toolkit: string;
    daemonId?: string;
  }): Promise<{
    available: boolean;
    daemonId?: string | null;
    toolkit: string;
    defaultActions: Array<{ slug: string; label: string }>;
    droppedByCap: Array<{ slug: string; label: string }>;
    excludedCount: number;
  }> {
    return this.request("cyborg:composio_toolkit_actions", input);
  }

  // Set the daemon's Composio key from the UI (owner-only). Daemon-forwarded to the
  // cybo's home daemon, where the ENCRYPTED vault lives — the secret never touches PG
  // or the workspace roster. WRITE-ONLY: the key is never echoed back; the response
  // carries only booleans. At least one of consumerKey / apiKey must be provided.
  async composioSetKey(input: {
    workspaceId: string;
    daemonId?: string;
    consumerKey?: string;
    apiKey?: string;
  }): Promise<{ ok: boolean; configured: boolean; daemonId?: string | null }> {
    return this.request("cyborg:composio_set_key", input);
  }

  // Remove the daemon's in-app Composio key (owner-only). Env-provided keys are
  // unaffected; only the in-app vault entry is cleared.
  async composioRemoveKey(input: {
    workspaceId: string;
    daemonId?: string;
  }): Promise<{ ok: boolean; daemonId?: string | null }> {
    return this.request("cyborg:composio_remove_key", input);
  }

  // Start a browser-based Composio sign-in on the target daemon ("Connect
  // Composio" banner): the daemon mints a login session (10-minute TTL), the UI
  // opens loginUrl in the system browser and polls composioLoginPoll until it
  // links. Response payload is unwrapped by request() like composioSetKey's.
  // An older daemon without the handler rejects the type — the caller surfaces
  // that as a clean inline error.
  async composioLoginStart(
    workspaceId: string,
    daemonId?: string,
  ): Promise<{
    sessionId: string;
    loginUrl: string;
    expiresAt?: number;
    daemonId?: string | null;
  }> {
    return this.request("cyborg:composio_login_start", { workspaceId, daemonId });
  }

  // Poll a composioLoginStart session. `linked` means the daemon fetched and
  // stored the key — the UI then re-checks composioConnectionStatus. Linked
  // sessions are consumed ONE-SHOT server-side: never poll again after
  // "linked". An expired/unknown session rejects with code "invalid".
  async composioLoginPoll(
    workspaceId: string,
    daemonId: string | undefined,
    sessionId: string,
  ): Promise<{
    status: "pending" | "linked";
    accountEmail?: string | null;
    configured?: boolean;
    daemonId?: string | null;
  }> {
    return this.request("cyborg:composio_login_poll", { workspaceId, daemonId, sessionId });
  }

  // "Install Claude Code / Codex on this machine" — the create-agent Provider
  // step's escape hatch for someone who has never opened a terminal.
  //
  // `daemonId` is REQUIRED, and that is the point: this runs `npm i -g` on ONE specific machine, and the relay's
  // host-control gate (DAEMON_HOST_CONTROL_TYPES) refuses to pick one for you.
  // Typed non-optional so a caller with no effective daemon is a COMPILE error
  // here rather than a confusing relay rejection at runtime.
  //
  // `provider` is a closed union, mirroring the wire enum
  // (cyborg-messages InstallProviderKeySchema): the slug is an opaque allowlist
  // KEY that the daemon looks up in a fixed table, never a package name.
  //
  async providerSignIn(input: ProviderSignInRequest): Promise<ProviderSignInState> {
    return this.request<ProviderSignInState>("cyborg:provider_sign_in", { ...input });
  }

  // "Sign in to GitHub on this computer" (server cyborg/gh-login.ts): the GitHub CLI's
  // device-code login on ONE machine, owner-only there. Gate on the daemon's
  // `gh_login_start` capability (daemonSupportsGhLogin) — an older daemon drops both.
  async ghAuthStatus(input: { workspaceId: string; daemonId: string }): Promise<GhAuthStatus> {
    return this.request<GhAuthStatus>("cyborg:gh_auth_status", { ...input });
  }

  async ghLogin(input: GhLoginRequest): Promise<GhLoginState> {
    return this.request<GhLoginState>("cyborg:gh_login_start", { ...input });
  }

  // The timeout is the INSTALL's, not the 15s default: with `includePrerequisites`
  // the daemon's setup watchdog is ten minutes, and the default would report
  // "timed out" over an install that is still successfully running.
  async installProviderCli(
    workspaceId: string,
    daemonId: string,
    provider: InstallableProviderId,
  ): Promise<ProviderInstallResult> {
    return this.request<ProviderInstallResult>(
      "cyborg:install_provider_cli",
      { workspaceId, daemonId, provider, includePrerequisites: true },
      PROVIDER_INSTALL_TIMEOUT_MS,
    );
  }

  // The same RPC with `action` (#3712). Ask only about an INSTALLED copy: a daemon
  // older than `action` installs instead, and answers `already_installed`.
  async checkProviderCli(
    workspaceId: string,
    daemonId: string,
    provider: InstallableProviderId,
  ): Promise<ProviderVersionResult> {
    return this.request<ProviderVersionResult>(
      "cyborg:install_provider_cli",
      { workspaceId, daemonId, provider, action: "check" },
      PROVIDER_CHECK_TIMEOUT_MS,
    );
  }

  async updateProviderCli(
    workspaceId: string,
    daemonId: string,
    provider: InstallableProviderId,
    force = false,
  ): Promise<ProviderVersionResult> {
    return this.request<ProviderVersionResult>(
      "cyborg:install_provider_cli",
      { workspaceId, daemonId, provider, action: "update", ...(force ? { force: true } : {}) },
      PROVIDER_INSTALL_TIMEOUT_MS,
    );
  }

  // Force a provider-snapshot re-probe on the target daemon and return the settled
  // statuses ("Re-check providers" self-repair in Settings → Daemon). Heals a stale
  // "pi unavailable" verdict without restarting the daemon.
  //
  // `providers` narrows the probe to those ids. Omit it for the daemon-wide
  // button; pass a single id for a per-row "Check again". The daemon resets each
  // listed provider to "loading" before probing, so a per-row click that sent no
  // list blanked and refilled the ENTIRE list — which is why that button looked
  // broken to users.
  async refreshProviders(
    workspaceId: string,
    daemonId?: string,
    providers?: string[],
  ): Promise<{ providers: { provider: string; status: string }[] }> {
    return this.request("cyborg:refresh_providers", {
      workspaceId,
      daemonId,
      ...(providers && providers.length > 0 ? { providers } : {}),
    });
  }

  // Remote daemon update (#663), per release line — see daemon-update-model.ts. Fields
  // past ok/restarting/error/command are absent from an older daemon.
  async updateDaemon(workspaceId: string, daemonId?: string): Promise<DaemonUpdateResponse> {
    return this.request("cyborg:update_daemon", { workspaceId, daemonId });
  }

  // "Repair install" on a macOS host: the relay opens the terminal itself, proves a shell
  // answers, and types its own reviewed script. Up to ~40s: a shell start plus its proof.
  async repairDesktopInstall(
    workspaceId: string,
    daemonId: string,
    confirmation: string,
  ): Promise<{ repairId: string; scriptId: string; terminalId: string }> {
    return this.request(
      "cyborg:repair_desktop_install",
      { workspaceId, daemonId, confirmation },
      REPAIR_INSTALL_TIMEOUT_MS,
    );
  }

  // Latest version on the daemon's OWN release line (desktop app feed or the
  // cyborg-cli version.txt). `channel` is absent from an older daemon, whose answer
  // came from npm's upstream @getpaseo/cli and must not be compared against.
  async daemonUpdateLatest(
    workspaceId: string,
    daemonId?: string,
  ): Promise<{
    ok: boolean;
    latest?: string | null;
    error?: string | null;
    channel?: "desktop" | "headless";
  }> {
    return this.request("cyborg:daemon_update_latest", { workspaceId, daemonId });
  }

  async updateCybo(
    workspaceId: string,
    cyboId: string,
    fields: {
      name?: string;
      description?: string | null;
      avatar?: string | null;
      role?: string | null;
      soul?: string;
      provider?: string;
      model?: string | null;
      llmAuthMode?: string;
      behaviorMode?: string;
      homeDaemonId?: string | null;
      autonomyLevel?: string | null;
      monthlySpendCap?: number | null;
      platformPermissions?: string[];
      toolGrants?: Record<string, unknown> | null;
      mcpServers?: Record<string, unknown> | null;
      // Danger toggle: run every tool without approval prompts (trusted
      // sandboxes only). Server support lands in a parallel lane — an older
      // server simply strips the field.
      bypassPermissions?: boolean;
      // Picking "CEO" as the job title sends this true; renaming away from
      // "CEO" sends false to relinquish it. The server enforces one CEO per
      // workspace.
      isDefault?: boolean;
    },
  ): Promise<UpdatedCybo> {
    const resp = await this.request<{ cybo: UpdatedCybo }>("cyborg:update_cybo", {
      workspaceId,
      cyboId,
      ...fields,
    });
    return resp.cybo;
  }

  /** Dry-run of moving a cybo to another machine (open sessions, schedules, provider). */
  async cyboMovePreview(
    workspaceId: string,
    cyboId: string,
    targetDaemonId: string,
  ): Promise<CyboMovePreviewResponse> {
    return this.request<CyboMovePreviewResponse>("cyborg:cybo_move_preview", {
      workspaceId,
      cyboId,
      targetDaemonId,
    });
  }

  async deleteCybo(workspaceId: string, cyboId: string): Promise<void> {
    await this.request("cyborg:delete_cybo", { workspaceId, cyboId });
  }

  // Single cybo WITH the full soul (the list omits it). isLocal=true means a
  // disk cybo not yet in the workspace DB.
  async fetchCybo(
    workspaceId: string,
    cyboId: string,
  ): Promise<(Cybo & { soul: string; isLocal?: boolean }) | null> {
    const resp = await this.request<{
      cybo: (Cybo & { soul: string; isLocal?: boolean }) | null;
    }>("cyborg:fetch_cybo", { workspaceId, cyboId });
    return resp.cybo;
  }

  // Snapshot a local (disk) cybo into the workspace DB (soul.md → DB column).
  async importCybo(
    workspaceId: string,
    slug: string,
  ): Promise<{ id: string; slug: string; name: string; provider: string; model?: string | null }> {
    const resp = await this.request<{
      cybo: { id: string; slug: string; name: string; provider: string; model?: string | null };
    }>("cyborg:import_cybo", { workspaceId, slug });
    return resp.cybo;
  }

  async spawnCybo(
    workspaceId: string,
    cyboIdOrSlug: string,
    cwd: string | undefined,
    // `ephemeral` spawns a FRESH, internal, non-persisted session. Required by
    // the board query: without it a direct-chat spawn (channelId undefined) is
    // deduped onto the opener's own live DM session with that cybo.
    // `workSessionId` + `prompt` (CYBORG-794): resume a COLD work session — spawn its
    // cybo against the session and deliver `prompt` straight to it, so the reply stays
    // in the session and nothing is posted to the anchor channel/thread.
    // `createStandaloneSession` (CYBORG-794): mint a FRESH standalone work session for
    // this spawn; the relay returns its `workSessionId` on the response below.
    opts?: {
      channelId?: string;
      daemonId?: string;
      ephemeral?: boolean;
      workSessionId?: string;
      prompt?: string;
      createStandaloneSession?: boolean;
      // COLD work-session composer permission mode (fix/worksession-live-stream-composer):
      // the mode pill's choice, governing the cold spawn's FIRST turn. "bypassPermissions"
      // → bypassed; any other value → NON-bypass (permissions prompt). Omitted → the
      // pre-existing default (cold work-session spawn starts bypassed). Server:
      // dispatcher.ts's resolveSpawnBypass.
      mode?: string;
      // The client's own id for `prompt` (cold work-session continue): the daemon stamps
      // it on the user's timeline row, so the optimistic copy collapses onto the echo.
      promptId?: string;
    },
    // The response wait only (the socket gates keep the default): a cold session's wake
    // answers when its agent is back, which took 87s on 2026-11-04 against a 15s default.
    responseTimeoutMs?: number,
  ): Promise<{
    agentId: string;
    cyboId: string;
    cyboSlug: string;
    provider: string;
    model?: string | null;
    // Set when a standalone work session was minted (createStandaloneSession).
    workSessionId?: string;
  }> {
    return this.request(
      "cyborg:spawn_cybo",
      { workspaceId, cyboIdOrSlug, cwd, ...opts },
      responseTimeoutMs,
    );
  }

  // ─── Provider credentials (knowledge/40 + 43) ──────────────────────
  //
  // Per-daemon, write-only API-key (and oauth/wellknown) store. These RPCs are
  // DAEMON_FORWARDed: the relay never reads the secret — it only routes the
  // envelope to the target daemon that holds the credential. The key is NEVER
  // echoed back: `set`/`remove` return `{ ok: true }`, `listProviderAuth` returns
  // METADATA ONLY (providerId, type, expires). All three require an explicit
  // `daemonId` — the credential lives on that machine.

  async setCyboCredential(
    workspaceId: string,
    daemonId: string,
    providerId: string,
    credential: ProviderCredentialPayload,
  ): Promise<{ ok: true }> {
    return this.request<{ ok: true }>("cyborg:set_cybo_credential", {
      workspaceId,
      daemonId,
      providerId,
      credential,
    });
  }

  async removeCyboCredential(
    workspaceId: string,
    daemonId: string,
    providerId: string,
  ): Promise<{ ok: true }> {
    return this.request<{ ok: true }>("cyborg:remove_cybo_credential", {
      workspaceId,
      daemonId,
      providerId,
    });
  }

  async listProviderAuth(workspaceId: string, daemonId: string): Promise<ProviderAuthMeta[]> {
    const resp = await this.request<{ credentials: ProviderAuthMeta[] }>(
      "cyborg:list_provider_auth",
      { workspaceId, daemonId },
    );
    return resp.credentials ?? [];
  }

  // ─── Import/Resume/Delete methods ───────────────────────────────

  // List the local provider sessions (Claude `~/.claude/projects`, Codex, …) that
  // are importable into this workspace — on-disk native sessions the daemon has
  // NOT already imported. Backs the session-import picker; importSession() below
  // snapshots a chosen one. `cwd`/`limit` scope + cap the scan (the daemon
  // defaults limit to 20). The daemon answers `fetch_recent_provider_sessions_response`.
  // `workspaceId` is the VIEWED workspace, same treatment as listProviders got in
  // CYBORG-682. This is a DAEMON_FORWARD frame, and it is the ONLY one left that
  // carries no daemonId either — so without it the relay has nothing to resolve
  // from and falls back to the caller's first workspace (their OLDEST membership,
  // now that getWorkspacesForUser is ordered). A member viewing their newer
  // workspace would get a daemon from the older one and see the wrong host's
  // transcripts, or none at all. Omitted → the relay keeps that fallback (the key
  // is dropped off the frame), so an unknown viewed workspace is never a garbage id.
  async fetchRecentProviderSessions(opts?: {
    cwd?: string;
    limit?: number;
    workspaceId?: string;
    daemonId?: string;
    query?: string;
    providers?: string[];
  }): Promise<RecentSession[]> {
    const resp = await this.request<{
      entries: RecentSession[];
      filteredAlreadyImportedCount?: number;
    }>("fetch_recent_provider_sessions_request", {
      ...(opts?.cwd ? { cwd: opts.cwd } : {}),
      ...(opts?.limit !== undefined ? { limit: opts.limit } : {}),
      ...(opts?.workspaceId ? { workspaceId: opts.workspaceId } : {}),
      ...(opts?.daemonId ? { daemonId: opts.daemonId } : {}),
      ...(opts?.query ? { query: opts.query } : {}),
      ...(opts?.providers?.length ? { providers: opts.providers } : {}),
    });
    return resp.entries ?? [];
  }

  async importSession(opts: {
    providerId?: string;
    providerHandleId?: string;
    sessionId?: string;
    cwd?: string;
  }): Promise<{ agentId: string; timelineSize: number }> {
    const resp = await this.request<{
      status: string;
      agentId: string;
      timelineSize: number;
      agent: Agent;
    }>("import_agent_request", { ...opts });
    return { agentId: resp.agentId, timelineSize: resp.timelineSize };
  }

  // Import a LOCAL provider transcript (Claude `~/.claude/projects`, …) into a
  // workspace via the NEW Cyborg path (cyborg:import_session). Distinct from the
  // legacy Paseo importSession() above (import_agent_request): this one resumes a
  // live agent AND writes a durable archived_sessions row that's re-resumable via
  // restoreSession() from any device. It's relay-forwarded to the daemon holding
  // the transcript with the same spawn-scope + daemonId-resolution as a restore,
  // so it works in cloud mode too. `daemonId` pins that daemon when known; omit it
  // and the relay resolves the sole online workspace daemon (like restoreSession).
  async importSessionCyborg(opts: {
    workspaceId: string;
    provider: string;
    providerHandleId: string;
    cwd?: string;
    channelId?: string;
    daemonId?: string;
  }): Promise<{ agentId: string; sessionId: string }> {
    const resp = await this.request<{ agentId: string; sessionId: string }>(
      "cyborg:import_session",
      {
        workspaceId: opts.workspaceId,
        provider: opts.provider,
        providerHandleId: opts.providerHandleId,
        ...(opts.cwd ? { cwd: opts.cwd } : {}),
        ...(opts.channelId ? { channelId: opts.channelId } : {}),
        ...(opts.daemonId ? { daemonId: opts.daemonId } : {}),
      },
      RESUME_SESSION_TIMEOUT_MS,
    );
    return { agentId: resp.agentId, sessionId: resp.sessionId };
  }

  // ─── Archive/Session methods ────────────────────────────────────

  async archiveAgent(
    workspaceId: string,
    agentId: string,
    onTaskNotice?: (notice: string) => void,
  ): Promise<string> {
    const resp = await this.request<{ sessionId: string; taskNotice?: string }>(
      "cyborg:archive_agent",
      { workspaceId, agentId },
    );
    // CYBORG-1414: the daemon says how many background tasks the archive stopped.
    if (resp.taskNotice) onTaskNotice?.(resp.taskNotice);
    return resp.sessionId;
  }

  async cloneAgent(
    workspaceId: string,
    agentId: string,
    title?: string | null,
  ): Promise<CloneAgentResult> {
    return await this.request<CloneAgentResult>("cyborg:clone_agent", {
      workspaceId,
      agentId,
      title: title ?? null,
    });
  }

  // ─── Provider subagents (Codex/OpenCode/Claude) ─────────────────
  //
  // Snapshot of a parent agent's provider-spawned subagents. Uses the cloud-
  // forwardable `cyborg:fetch_provider_subagents` RPC (keyed by the PARENT `agentId`,
  // like fetch_agent_timeline) instead of the Paseo-native
  // `agent.provider_subagents.list.request`: the native `agent.*` type is NOT in the
  // relay's forward set, so in cloud mode it never reached the OWNING daemon and a
  // REMOTE viewer always got `[]` (#1612 relay bridge). The cyborg RPC is forwarded to
  // the daemon that runs the parent (resolved from the PG binding) and gated as a
  // session read; the daemon answers from its in-memory ProviderSubagentStore. Works
  // identically on the DIRECT (local-daemon) path — the dispatcher handles it there too.
  async listProviderSubagents(
    workspaceId: string,
    parentAgentId: string,
  ): Promise<ProviderSubagentDescriptor[]> {
    const resp = await this.request<{ subagents?: ProviderSubagentDescriptor[] }>(
      "cyborg:fetch_provider_subagents",
      { workspaceId, agentId: parentAgentId },
    );
    return resp.subagents ?? [];
  }

  // CYBORG-1418: ONE subagent by id, archived or not (the list read hides archived rows).
  async getProviderSubagent(
    workspaceId: string,
    parentAgentId: string,
    subagentId: string,
  ): Promise<ProviderSubagentDescriptor | undefined> {
    const resp = await this.request<{ subagents?: ProviderSubagentDescriptor[] }>(
      "cyborg:fetch_provider_subagents",
      { workspaceId, agentId: parentAgentId, subagentId },
    );
    return resp.subagents?.find((s) => s.id === subagentId);
  }

  // Durably archive finished provider subagents off the track (W3): the relay stamps
  // archived_at and the list read excludes archived rows, so it survives a reload. `subagentId`
  // archives one; omitting it archives every finished child. Returns how many rows were stamped.
  async archiveProviderSubagents(input: {
    workspaceId: string;
    parentAgentId: string;
    subagentId?: string;
    trigger?: "auto" | "manual";
    // The row is running but stalled: the relay cancels it, then archives it.
    cancelStalled?: boolean;
  }): Promise<number> {
    const resp = await this.request<{ archived?: number }>("cyborg:archive_provider_subagents", {
      workspaceId: input.workspaceId,
      agentId: input.parentAgentId,
      ...(input.subagentId ? { subagentId: input.subagentId } : {}),
      ...(input.trigger ? { trigger: input.trigger } : {}),
      ...(input.cancelStalled ? { cancelStalled: true } : {}),
    });
    return resp.archived ?? 0;
  }

  // CYBORG-1416: the goal, background tasks and retry of a session, read back from the
  // daemon's fetch_agent_state (a reload has no push to wait for). null: the daemon is older or
  // the session is not the caller's.
  async fetchAgentActivity(workspaceId: string, agentId: string): Promise<AgentActivity | null> {
    return (await this.fetchAgentLiveState(workspaceId, agentId)).activity ?? null; // null: an older daemon sends none
  }

  // CYBORG-1516: `pendingPermissions` is the hosting daemon's list of the session's pending
  // requests. Absent: an older daemon, a session the caller may not answer, or none of theirs.
  async fetchAgentLiveState(workspaceId: string, agentId: string): Promise<AgentLiveState> {
    const resp = await this.request<{ agent?: AgentLiveState | null }>("cyborg:fetch_agent_state", {
      workspaceId,
      agentId,
    });
    return resp.agent ?? {};
  }

  // CYBORG-1416: end one background task from the subagents tray. false: it had already ended.
  async stopBackgroundTask(workspaceId: string, agentId: string, taskId: string): Promise<boolean> {
    const resp = await this.request<{ stopped?: boolean }>("cyborg:stop_background_task", {
      workspaceId,
      agentId,
      taskId,
    });
    return resp.stopped === true;
  }

  // One backlog page of a background process's output from fromSeq (omitted: the oldest buffered
  // line); `more` asks for the next page. Owner only: anyone else gets code "forbidden".
  async tailAgentProcess(
    workspaceId: string,
    agentId: string,
    processId: string,
    fromSeq?: number,
  ): Promise<AgentProcessTail> {
    return this.request<AgentProcessTail>("cyborg:tail_agent_process", {
      workspaceId,
      agentId,
      processId,
      ...(fromSeq !== undefined ? { fromSeq } : {}),
    });
  }

  // Live cyborg:agent_process_output frames for one process while attachId is subscribed.
  subscribeAgentProcess(
    workspaceId: string,
    agentId: string,
    processId: string,
    attachId: string,
  ): void {
    // No subscription without a sendable socket: syncAppStateToRelay re-subscribes after auth.
    if (!this.canSend()) return;
    this.send({
      type: "cyborg:subscribe_agent_process",
      workspaceId,
      agentId,
      processId,
      attachId,
    });
  }

  unsubscribeAgentProcess(
    workspaceId: string,
    agentId: string,
    processId: string,
    attachId: string,
  ): void {
    // The modal's cleanup runs on close: a dropped socket already dropped the subscription.
    if (!this.canSend()) return;
    this.send({
      type: "cyborg:unsubscribe_agent_process",
      workspaceId,
      agentId,
      processId,
      attachId,
    });
  }

  // ONE subagent's timeline page — the drill-down's hydration, and the exact twin
  // of listProviderSubagents above (same `cyborg:*`-instead-of-`agent.*` reason:
  // Paseo's native `agent.provider_subagents.timeline.get.request` is not in the
  // relay's forward set, so it never reached the owning daemon and our drill-down
  // had no fetch at all — it could render only what it caught on the live stream).
  // Keyed by the PARENT `agentId` + the child `subagentId`; forwarded to the daemon
  // that runs the parent and gated as a session read. An empty `rows` is a normal
  // answer, not an error: the daemon's subagent store is in-memory only, and the
  // relay backs it with the PG transcript mirror (live first, durable as fallback).
  //
  // `hasOlder` is the CUT signal — this read serves ONE tail page with no back-paging,
  // and the durable mirror is retention-bounded, so a long transcript can be served
  // incomplete. Absent (older daemon) reads as false, i.e. today's behavior.
  // `before` (the epoch + oldest seq of a previous page) asks for the page before it.
  async fetchProviderSubagentTimeline(
    workspaceId: string,
    parentAgentId: string,
    subagentId: string,
    before?: { epoch: string; seq: number },
  ): Promise<{
    provider: string | null;
    rows: { seq: number; item: unknown }[];
    hasOlder: boolean;
    epoch?: string;
  }> {
    const resp = await this.request<{
      provider?: string | null;
      rows?: { seq: number; item: unknown }[];
      hasOlder?: boolean;
      epoch?: string;
    }>("cyborg:fetch_provider_subagent_timeline", {
      workspaceId,
      agentId: parentAgentId,
      subagentId,
      ...(before ? { before } : {}),
    });
    return {
      provider: resp.provider ?? null,
      rows: resp.rows ?? [],
      hasOlder: resp.hasOlder === true,
      ...(resp.epoch ? { epoch: resp.epoch } : {}),
    };
  }

  // Detach a provider subagent so it leaves the parent's track and continues as a
  // standalone agent. `cyborg:detach_subagent { requestId, workspaceId, subagentId }`
  // → `cyborg:detach_subagent_response { status, subagentId, previousParentAgentId }`.
  // `previousParentAgentId` is null when the child was already top-level (no-op).
  async detachSubagent(
    workspaceId: string,
    subagentId: string,
  ): Promise<{ status: string; previousParentAgentId: string | null }> {
    const resp = await this.request<{
      status: string;
      previousParentAgentId: string | null;
    }>("cyborg:detach_subagent", { workspaceId, subagentId });
    return { status: resp.status, previousParentAgentId: resp.previousParentAgentId };
  }

  // Paginated archived list. `opts.limit`/`opts.cursor` page the list newest-first
  // (the cursor is the opaque token from a prior page's `nextCursor`); omitting
  // both returns the full list (legacy behavior). `nextCursor` is null on the last
  // page. Both fields are additive — old daemons that ignore them just return all.
  async listArchivedSessions(
    workspaceId: string,
    opts?: {
      limit?: number;
      cursor?: string;
      resumable?: boolean;
      query?: string;
      provider?: string;
    },
  ): Promise<{
    sessions: ArchivedSession[];
    nextCursor: string | null;
    noDaemonAnswered: boolean;
  }> {
    const resp = await this.request<{
      sessions: ArchivedSession[];
      nextCursor?: string | null;
      // ADDITIVE relay flag (task_3c5914ed): the fan-out reached daemons but
      // none answered — an empty page is a hung-daemon timeout, not an empty
      // archive. Absent (→ false) on older relays / daemon-answered responses.
      noDaemonAnswered?: boolean;
    }>("cyborg:list_archived_sessions", {
      workspaceId,
      ...(opts?.limit !== undefined ? { limit: opts.limit } : {}),
      ...(opts?.cursor ? { cursor: opts.cursor } : {}),
      ...(opts?.resumable ? { resumable: true } : {}),
      ...(opts?.query ? { query: opts.query } : {}),
      ...(opts?.provider ? { provider: opts.provider } : {}),
    });
    return {
      sessions: resp.sessions,
      nextCursor: resp.nextCursor ?? null,
      noDaemonAnswered: resp.noDaemonAnswered === true,
    };
  }

  // Restore an archived session. `opts.daemonId` routes/scopes the restore to the
  // daemon that owns the archived session (from listArchivedSessions); `opts.overrides`
  // (#593) boots the resumed agent on a chosen model/mode/thinking instead of the
  // archived config. Both optional — omitting them resumes exactly as before.
  async restoreSession(
    workspaceId: string,
    sessionId: string,
    opts?: { daemonId?: string; overrides?: ResumeOverrides },
  ): Promise<string> {
    const resp = await this.request<{ agentId: string }>(
      "cyborg:restore_session",
      {
        workspaceId,
        sessionId,
        ...(opts?.daemonId ? { daemonId: opts.daemonId } : {}),
        ...(opts?.overrides ? { overrides: opts.overrides } : {}),
      },
      RESUME_SESSION_TIMEOUT_MS,
    );
    return resp.agentId;
  }

  // ─── Project methods ────────────────────────────────────────────

  async createProject(
    workspaceId: string,
    name: string,
    color: string,
    // CYBORG-1082: one key per create-form session; a retry returns the project it made.
    idempotencyKey?: string,
  ): Promise<Project> {
    const resp = await this.request<{ project: Project }>("cyborg:create_project", {
      workspaceId,
      name,
      color,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    });
    return resp.project;
  }

  // `taskChannelId`: a task is about to be created from a message there; the reply then names
  // the project the server's create resolver files it into for this person (defaultProjectId).
  async fetchProjects(
    workspaceId: string,
    taskChannelId?: string,
  ): Promise<{
    projects: Project[];
    channelProjects: ChannelProject[];
    defaultProjectId?: string | null; // null: they can file nowhere; absent: the create seeds one
  }> {
    return this.request<{
      projects: Project[];
      channelProjects: ChannelProject[];
      defaultProjectId?: string | null; // null: none yet
    }>("cyborg:fetch_projects", {
      workspaceId,
      ...(taskChannelId ? { taskChannelId } : {}),
    });
  }

  async updateProject(
    workspaceId: string,
    projectId: string,
    name: string,
    color: string,
  ): Promise<void> {
    // workspaceId is REQUIRED by the relay handler (handleUpdateProject reads
    // inner.workspaceId); omitting it made every edit fail with "workspaceId
    // required" — same trap as setChannelProject / deleteProject.
    await this.request("cyborg:update_project", { workspaceId, projectId, name, color });
  }

  async deleteProject(workspaceId: string, projectId: string): Promise<void> {
    // workspaceId is REQUIRED by the relay handler (handleDeleteProject reads
    // inner.workspaceId); omitting it made every delete silently fail server-side
    // (the project reappeared on the next fetch_projects).
    await this.request("cyborg:delete_project", { workspaceId, projectId });
  }

  async setChannelProject(
    workspaceId: string,
    channelId: string,
    projectId: string | null,
  ): Promise<void> {
    // workspaceId is REQUIRED by the relay handler (it reads inner.workspaceId);
    // omitting it made every assignment fail with "workspaceId required".
    await this.request("cyborg:set_channel_project", { workspaceId, channelId, projectId });
  }

  // ─── Cycles catalog CRUD ────────────────────────────────────────
  // `projectId` is the CHAT project id (the same id fetchCycles takes); the relay
  // resolves it to the tasks_projects row and gates visibility. create/update return
  // the affected row so the caller can splice it into the local cycles list without
  // a refetch.

  async createCycle(
    projectId: string,
    input: {
      name: string;
      description?: string | null;
      startDate?: number | null;
      endDate?: number | null;
    },
  ): Promise<Cycle> {
    const resp = await this.request<{ cycle: Cycle }>("cyborg:create_cycle", {
      projectId,
      ...input,
    });
    return resp.cycle;
  }

  async updateCycle(
    cycleId: string,
    updates: {
      name?: string;
      description?: string | null;
      startDate?: number | null;
      endDate?: number | null;
      archived?: boolean;
    },
  ): Promise<Cycle> {
    const resp = await this.request<{ cycle: Cycle }>("cyborg:update_cycle", {
      cycleId,
      ...updates,
    });
    return resp.cycle;
  }

  async deleteCycle(cycleId: string): Promise<void> {
    await this.request("cyborg:delete_cycle", { cycleId });
  }

  // ─── Saved views catalog CRUD ───────────────────────────────────
  // `projectId` is the CHAT project id (the same id fetchTaskViews takes); the
  // relay resolves it to the tasks_projects row and gates visibility. create/
  // update return the affected row so the caller can splice it into the local
  // views list without a refetch. `filters` + `display` are OPAQUE serialized
  // JSON strings — the views route builds / parses them around these calls.

  async createTaskView(
    projectId: string,
    input: {
      name: string;
      layout?: string;
      groupBy?: string;
      subGroupBy?: string | null;
      orderBy?: string;
      orderDir?: string;
      filters?: string;
      display?: string;
    },
  ): Promise<TaskView> {
    const resp = await this.request<{ view: TaskView }>("cyborg:create_task_view", {
      projectId,
      ...input,
    });
    return resp.view;
  }

  async updateTaskView(
    viewId: string,
    updates: {
      name?: string;
      layout?: string;
      groupBy?: string;
      subGroupBy?: string | null;
      orderBy?: string;
      orderDir?: string;
      filters?: string;
      display?: string;
    },
  ): Promise<TaskView> {
    const resp = await this.request<{ view: TaskView }>("cyborg:update_task_view", {
      viewId,
      ...updates,
    });
    return resp.view;
  }

  async deleteTaskView(viewId: string): Promise<void> {
    await this.request("cyborg:delete_task_view", { viewId });
  }

  // ─── Modules catalog CRUD ───────────────────────────────────────

  async createModule(
    projectId: string,
    input: {
      name: string;
      description?: string | null;
      status?: string;
      lead?: string | null;
      startDate?: number | null;
      targetDate?: number | null;
    },
  ): Promise<Module> {
    const resp = await this.request<{ module: Module }>("cyborg:create_module", {
      projectId,
      ...input,
    });
    return resp.module;
  }

  async updateModule(
    moduleId: string,
    updates: {
      name?: string;
      description?: string | null;
      status?: string;
      lead?: string | null;
      startDate?: number | null;
      targetDate?: number | null;
      archived?: boolean;
    },
  ): Promise<Module> {
    const resp = await this.request<{ module: Module }>("cyborg:update_module", {
      moduleId,
      ...updates,
    });
    return resp.module;
  }

  async deleteModule(moduleId: string): Promise<void> {
    await this.request("cyborg:delete_module", { moduleId });
  }

  // ─── Task links (external URLs) ─────────────────────────────────
  // The relay/dispatcher resolves the task→project and gates visibility. add/fetch
  // take the taskId; remove takes the linkId. add/fetch return the row(s) so the
  // caller can splice into the local list without a refetch.

  async addTaskLink(
    taskId: string,
    input: { url: string; title?: string | null },
  ): Promise<TaskLink> {
    const resp = await this.request<{ link: TaskLink }>("cyborg:add_task_link", {
      taskId,
      ...input,
    });
    return resp.link;
  }

  async removeTaskLink(linkId: string): Promise<void> {
    await this.request("cyborg:remove_task_link", { linkId });
  }

  async fetchTaskLinks(taskId: string): Promise<TaskLink[]> {
    const resp = await this.request<{ links: TaskLink[] }>("cyborg:fetch_task_links", { taskId });
    return resp.links;
  }

  // ─── Task attachments (S3 asset rows) ───────────────────────────
  // The bytes upload via the existing presign route (POST /api/assets/presign →
  // S3 PUT); after the upload completes, call addTaskAttachment with the resulting
  // key + delivery url to persist the row. remove takes the attachmentId.

  async addTaskAttachment(
    taskId: string,
    input: {
      key: string;
      url: string;
      name: string;
      size: number;
      contentType?: string | null;
    },
  ): Promise<TaskAttachment> {
    const resp = await this.request<{ attachment: TaskAttachment }>("cyborg:add_task_attachment", {
      taskId,
      ...input,
    });
    return resp.attachment;
  }

  async removeTaskAttachment(attachmentId: string): Promise<void> {
    await this.request("cyborg:remove_task_attachment", { attachmentId });
  }

  async fetchTaskAttachments(taskId: string): Promise<TaskAttachment[]> {
    const resp = await this.request<{ attachments: TaskAttachment[] }>(
      "cyborg:fetch_task_attachments",
      { taskId },
    );
    return resp.attachments;
  }

  // ─── Channel management methods ─────────────────────────────────

  async updateChannel(
    workspaceId: string,
    channelId: string,
    updates: {
      name?: string;
      description?: string | null;
      isPrivate?: boolean;
      instructions?: string | null;
      // Cybo Autonomy (#964) — the channel's two dials. null explicitly clears
      // back to "unset" (resolveEffectiveAutonomy's behavior-neutral default);
      // omit the key to leave the stored value untouched.
      regime?: ChannelRegime | null;
      maxAutonomyLevel?: AutonomyLevel | null;
    },
    // null when the save applied but the caller may not READ the channel back: the
    // response is member-scoped because the record carries `instructions`, the channel's
    // AI prompt. A workspace owner who is not in a private channel saves it and gets this.
  ): Promise<Channel | null> {
    const resp = await this.request<{ channel: Channel | null }>("cyborg:update_channel", {
      workspaceId,
      channelId,
      ...updates,
    });
    return resp.channel ?? null;
  }

  async deleteChannel(workspaceId: string, channelId: string): Promise<void> {
    await this.request("cyborg:delete_channel", { workspaceId, channelId });
  }

  // Archive (soft-delete) vs the harder deleteChannel: preserves history, just
  // hides the channel from the active list. Admin-gated server-side. Returns the
  // updated channel so the caller can reconcile local state.
  async archiveChannel(
    workspaceId: string,
    channelId: string,
    archived: boolean,
  ): Promise<Channel | null> {
    const resp = await this.request<{ channel: Channel | null }>("cyborg:archive_channel", {
      workspaceId,
      channelId,
      archived,
    });
    return resp.channel;
  }

  // The caller's own role in a channel ("admin" | "member" | null) from the real
  // channel_roles table — replaces the old createdBy === myId admin heuristic.
  async getChannelRole(workspaceId: string, channelId: string): Promise<string | null> {
    const resp = await this.request<{ role: string | null }>("cyborg:get_channel_role", {
      workspaceId,
      channelId,
    });
    return resp.role;
  }

  // Lightweight count of messages-with-attachments in a channel, for the Files
  // tab badge. Separate from fetchChannelFiles so the badge doesn't pull a full
  // page of signed URLs just to show a number.
  async getChannelFileCount(workspaceId: string, channelId: string): Promise<number> {
    const resp = await this.request<{ count: number }>("cyborg:get_channel_file_count", {
      workspaceId,
      channelId,
    });
    return resp.count ?? 0;
  }

  // Subscribe this socket to a workspace's live broadcasts (needed when the
  // workspace was joined mid-session, e.g. via an invite, after the initial auth).
  async subscribeWorkspace(workspaceId: string): Promise<void> {
    await this.request("cyborg:subscribe_workspace", { workspaceId });
    this.noteHeldWorkspace(workspaceId);
  }

  async fetchChannelMembers(workspaceId: string, channelId: string): Promise<ChannelMember[]> {
    return (await this.fetchChannelMembersWithSlackGuests(workspaceId, channelId)).members;
  }

  // Same read; `slackGuests` is the relay's additive field (Slack-bridged channels only).
  async fetchChannelMembersWithSlackGuests(
    workspaceId: string,
    channelId: string,
  ): Promise<{ members: ChannelMember[]; slackGuests: ChannelSlackGuest[] }> {
    const resp = await this.request<{
      members: ChannelMember[];
      slackGuests?: ChannelSlackGuest[];
    }>("cyborg:fetch_channel_members", { workspaceId, channelId });
    return { members: resp.members, slackGuests: resp.slackGuests ?? [] };
  }

  async addChannelMember(workspaceId: string, channelId: string, userId: string): Promise<void> {
    await this.request("cyborg:add_channel_member", { workspaceId, channelId, userId });
  }

  // Browse: all public channels + the user's private ones, each flagged isMember
  // and carrying its member_count (additive; older relays omit it → 0).
  async listChannels(
    workspaceId: string,
  ): Promise<Array<Channel & { isMember: boolean; memberCount: number }>> {
    const resp = await this.request<{
      channels: Array<Channel & { isMember: boolean; memberCount: number }>;
    }>("cyborg:list_channels", { workspaceId });
    return resp.channels;
  }

  async removeChannelMember(workspaceId: string, channelId: string, userId: string): Promise<void> {
    await this.request("cyborg:remove_channel_member", { workspaceId, channelId, userId });
  }

  // ── Cybo channel membership (W3 server endpoint) ──
  // Minimal contract: the channel tracks which workspace cybos are members by id;
  // the UI resolves names/avatars from cyboState. Mirrors the human-member trio.
  async fetchChannelCybos(workspaceId: string, channelId: string): Promise<string[]> {
    const resp = await this.request<{ cyboIds: string[] }>("cyborg:fetch_channel_cybos", {
      workspaceId,
      channelId,
    });
    return resp.cyboIds ?? [];
  }

  // The same call, keeping the DISPLAY identity the response now carries beside the
  // ids. Separate method rather than a widened return type so no existing caller
  // changes shape. The only caller that needs this is the @-mention scope loader, and
  // only because a Cyborg7 Connect external cannot read `cyborg:fetch_cybos` (it hands
  // back every cybo's full configuration) — so for them this is the ONLY source of the
  // agent's name, and without a name the picker has an id it cannot render.
  async fetchChannelCybosWithIdentity(
    workspaceId: string,
    channelId: string,
  ): Promise<{
    cyboIds: string[];
    cybos: { id: string; slug: string; name: string; avatar: string | null }[];
    // The humans seated in this channel, id + name only (additive; absent from an older server).
    humans: { userId: string; name: string }[];
  }> {
    const resp = await this.request<{
      cyboIds?: string[];
      cybos?: { id: string; slug: string; name: string; avatar: string | null }[];
      humans?: { userId: string; name: string }[];
    }>("cyborg:fetch_channel_cybos", { workspaceId, channelId });
    return { cyboIds: resp.cyboIds ?? [], cybos: resp.cybos ?? [], humans: resp.humans ?? [] };
  }

  async addChannelCybo(workspaceId: string, channelId: string, cyboId: string): Promise<void> {
    await this.request("cyborg:add_channel_cybo", { workspaceId, channelId, cyboId });
  }

  async removeChannelCybo(workspaceId: string, channelId: string, cyboId: string): Promise<void> {
    await this.request("cyborg:remove_channel_cybo", { workspaceId, channelId, cyboId });
  }

  // ── Project cybo roles + daily brief (knowledge/82) ──
  // projectId is the CHAT project id (same one fetchProjects()/fetchChannelCybos
  // key off), not the Tasks-redesign project id.
  async fetchProjectRoles(workspaceId: string, projectId: string): Promise<ProjectCyboRole[]> {
    const resp = await this.request<{ roles: ProjectCyboRole[] }>("cyborg:fetch_project_roles", {
      workspaceId,
      projectId,
    });
    return resp.roles ?? [];
  }

  // instructions is optional: omit it to change the role without touching the
  // cybo's existing rules, or pass null explicitly to clear them.
  async setProjectRole(
    workspaceId: string,
    projectId: string,
    cyboId: string,
    role: string,
    instructions?: string | null,
  ): Promise<void> {
    await this.request("cyborg:set_project_role", {
      workspaceId,
      projectId,
      cyboId,
      role,
      instructions,
    });
  }

  async setProjectPm(workspaceId: string, projectId: string, cyboId: string): Promise<void> {
    await this.request("cyborg:set_project_pm", { workspaceId, projectId, cyboId });
  }

  async fetchProjectBrief(workspaceId: string, projectId: string): Promise<string | null> {
    const resp = await this.request<{ dailyBrief: string | null }>("cyborg:fetch_project_brief", {
      workspaceId,
      projectId,
    });
    return resp.dailyBrief ?? null;
  }

  async setProjectBrief(workspaceId: string, projectId: string, dailyBrief: string): Promise<void> {
    await this.request("cyborg:set_project_brief", { workspaceId, projectId, dailyBrief });
  }

  // ── Project autonomy LOOP config (Phase A, knowledge/82) ──
  // Kill switch + cadence/budget/turn bounds. Never spends anything on its
  // own — this is stored config only, read by a later runner phase.
  async fetchProjectAutonomy(
    workspaceId: string,
    projectId: string,
  ): Promise<ProjectAutonomySettings> {
    const resp = await this.request<{ settings: ProjectAutonomySettings }>(
      "cyborg:fetch_project_autonomy",
      { workspaceId, projectId },
    );
    return resp.settings;
  }

  async setProjectAutonomy(
    workspaceId: string,
    projectId: string,
    patch: Partial<
      Pick<
        ProjectAutonomySettings,
        "loopEnabled" | "cadence" | "monthlyBudgetUsd" | "maxTurnsPerRun"
      >
    >,
  ): Promise<void> {
    await this.request("cyborg:set_project_autonomy", { workspaceId, projectId, ...patch });
  }

  // ── L4 self-wake speech gate — the owner surface ──
  // "Where does this agent speak up on its own, and can it right now?" Each row
  // carries the LIVE gate verdict (`triggerState`), resolved server-side by the
  // SAME function the loop gates on — the UI never re-derives the rule.
  async listCyboLoopChannels(
    workspaceId: string,
    cyboId: string,
  ): Promise<{ channels: CyboLoopChannel[]; recentStarts: CyboLoopSelfStart[] }> {
    const resp = await this.request<{
      channels: CyboLoopChannel[];
      recentStarts: CyboLoopSelfStart[];
    }>("cyborg:list_cybo_loop_channels", { workspaceId, cyboId });
    return { channels: resp.channels ?? [], recentStarts: resp.recentStarts ?? [] };
  }

  // Mute (untilMs = a future epoch ms) / Unmute (null) this agent's self-wake in
  // ONE channel. Never touches the loop's own kill switch — see the server's
  // setCyboLoopMuted doc for why that distinction is load-bearing.
  async setCyboLoopMuted(
    workspaceId: string,
    cyboId: string,
    channelId: string,
    untilMs: number | null,
  ): Promise<void> {
    await this.request("cyborg:set_cybo_loop_muted", {
      workspaceId,
      cyboId,
      channelId,
      untilMs,
    });
  }

  // ── Agent-channel picker (knowledge/82's swarm-regime coordination channel) ──
  // Pick an existing workspace channel, or clear the pick (channelId: null) to
  // revert to the runner's own create-or-fallback default (pm-runner.ts's
  // ensureAgentChannel). Separate from setProjectAutonomy's patch — agentChannelId
  // is never one of its keys (see that method above).
  async setProjectAgentChannel(
    workspaceId: string,
    projectId: string,
    channelId: string | null,
  ): Promise<void> {
    await this.request("cyborg:set_project_agent_channel", { workspaceId, projectId, channelId });
  }

  // Provisions a dedicated "<project> · autonomy" channel (with the project's
  // current team as members) and sets it as the project's agent channel in one
  // round trip. Returns the new channel's id.
  async createProjectAgentChannel(workspaceId: string, projectId: string): Promise<string> {
    const resp = await this.request<{ channelId: string }>("cyborg:create_project_agent_channel", {
      workspaceId,
      projectId,
    });
    return resp.channelId;
  }

  // ── PM run ledger (knowledge/82's per-project progress ledger) ──
  // Read-only: no write RPC exists yet, the (future) PM orchestrator writes
  // server-internally.
  async fetchProjectActivity(
    workspaceId: string,
    projectId: string,
    limit?: number,
  ): Promise<PmRunLedgerEvent[]> {
    const resp = await this.request<{ events: PmRunLedgerEvent[] }>(
      "cyborg:fetch_project_activity",
      { workspaceId, projectId, limit },
    );
    return resp.events ?? [];
  }

  // ─── Shared Files (M-files) ─────────────────────────────────────
  // Cursor pagination, newest-first. `before` is the `nextCursor` returned by
  // the previous page (a messageId). The relay over-fetches so a message's
  // attachments never split across pages.

  async fetchChannelFiles(
    workspaceId: string,
    channelId: string,
    opts?: { before?: string; limit?: number },
  ): Promise<SharedFilesPage> {
    return this.request<SharedFilesPage>("cyborg:fetch_channel_files", {
      workspaceId,
      channelId,
      before: opts?.before,
      limit: opts?.limit,
    });
  }

  async fetchDmFiles(
    workspaceId: string,
    peerId: string,
    opts?: { before?: string; limit?: number },
  ): Promise<SharedFilesPage> {
    return this.request<SharedFilesPage>("cyborg:fetch_dm_files", {
      workspaceId,
      peerId,
      before: opts?.before,
      limit: opts?.limit,
    });
  }

  // ─── Web Push ───────────────────────────────────────────────────

  async getVapidKey(): Promise<string | null> {
    const resp = await this.request<{ publicKey: string | null }>("cyborg:get_vapid_key");
    return resp.publicKey;
  }

  async pushSubscribe(sub: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    userAgent?: string;
  }): Promise<void> {
    await this.request("cyborg:push_subscribe", { ...sub });
  }

  async pushUnsubscribe(endpoint: string): Promise<void> {
    await this.request("cyborg:push_unsubscribe", { endpoint });
  }

  // ─── Billing / license (Stripe) ─────────────────────────────────

  // Authoritative license state for the workspace's trial bar + activate gate,
  // PLUS the context-aware billing intent for `platform` (what action to offer +
  // copy). Replaces the old localStorage trial clock. Uses the WS RPC (already
  // authenticated on this socket); the HTTP GET /api/stripe/license is its
  // companion for non-WS callers. `platform` tells the relay which surface the
  // caller is on (it can't infer it) — defaults to "web" server-side if omitted.
  async fetchLicense(
    workspaceId: string,
    platform: BillingPlatformPayload = "web",
  ): Promise<{ license: LicensePayload; intent: BillingIntentPayload | null }> {
    const resp = await this.request<{
      workspaceId: string;
      license: LicensePayload;
      intent?: BillingIntentPayload;
    }>("cyborg:fetch_license", { workspaceId, platform });
    return { license: resp.license, intent: resp.intent ?? null };
  }

  // Owner-only: create a Stripe Checkout Session and return its hosted URL. The
  // caller redirects the browser there (window.location.href = url). Card entry
  // happens entirely on checkout.stripe.com — no card data touches our UI.
  // Throws on 403 (non-owner) / 503 (billing not configured) with the server's
  // error string so the modal can show an appropriate message.
  async startCheckout(workspaceId: string, interval: "month" | "year" = "month"): Promise<string> {
    return this.postBilling("/api/stripe/checkout", workspaceId, interval);
  }

  // Owner-only: open the Stripe billing portal (manage / cancel / update card /
  // invoices) and return its hosted URL for the browser to redirect to.
  async openBillingPortal(workspaceId: string): Promise<string> {
    return this.postBilling("/api/stripe/portal", workspaceId);
  }

  // Owner-only: the card on file + last invoices of the workspace's Stripe
  // customer. Throws on a refusal; a Stripe outage is a result (the relay logged it).
  async getBillingDetails(workspaceId: string): Promise<BillingDetailsResponse> {
    return await this.request<BillingDetailsResponse>("cyborg:get_billing_details", {
      workspaceId,
    });
  }

  // iOS only: ask the relay to reconcile the caller's RevenueCat entitlement
  // into the subscriptions table (across every workspace they own) and return
  // this workspace's fresh license. Used right after a StoreKit purchase/restore
  // (instant access, no waiting for the async webhook) and to self-heal a paused
  // license caused by a missed webhook. On any error/non-200 it returns `ok: false`
  // so callers can fall back to the optimistic state.
  async reconcileIap(workspaceId: string): Promise<IapReconcileResult> {
    try {
      const resp = await fetch(`${this.relayHttpBase}/api/iap/reconcile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.authToken}`,
        },
        body: JSON.stringify({ workspaceId }),
      });
      if (!resp.ok) {
        // A paid purchase that does not reconcile: 4xx = refusal, 5xx = fault.
        reportUiFailure(
          "billing.reconcile_iap",
          new Error(`IAP reconcile answered HTTP ${resp.status}`),
          { workspaceId, status: resp.status },
          {
            code: `http_${resp.status}`,
            failureKind: resp.status >= 400 && resp.status < 500 ? "refusal" : "fault",
          },
        );
        return { ok: false, reason: "http", status: resp.status };
      }
      const data = (await resp.json()) as { license?: LicensePayload };
      return { ok: true, license: data.license ?? null };
    } catch (err) {
      reportUiFailure("billing.reconcile_iap", err, { workspaceId });
      return { ok: false, reason: "exception" };
    }
  }

  // ─── License pool + allocation (per-workspace seat model, spec §4.3) ────────
  // These ride the same authenticated WS RPC as fetchLicense. The handlers live
  // in relay-standalone.ts (Unit E, in parallel); the request()/response shapes
  // here are the contract. Caller identity = the authed socket's userId, so the
  // pool fetch needs no workspaceId; allocate/deallocate are owner-scoped on the
  // relay (it 403s a non-owner via the same getWorkspaceOwnerId check Stripe uses).

  /**
   * The caller's seat entitlement + which workspaces currently spend a seat +
   * the relay's authoritative list of workspaces the caller owns. `pool` is null
   * when the caller has no pool (free/trial only) — the panel then shows the
   * tier picker / checkout CTA instead of toggles.
   */
  async fetchLicensePool(): Promise<LicensePoolResponse> {
    return this.request<LicensePoolResponse>("cyborg:fetch_license_pool", {});
  }

  /**
   * Spend one free seat on `workspaceId` (no purchase — consumes an owned seat).
   * Owner-only on the relay. Resolves with the workspace's fresh license + the
   * recomputed pool/allocations. A rejected mutation resolves with `error` set
   * (no_free_seat | not_owner | no_pool | already_active_other_rail) and an
   * unchanged pool/allocations — callers branch on `error`.
   */
  async allocateLicense(workspaceId: string): Promise<LicenseAllocationResponse> {
    return this.request<LicenseAllocationResponse>("cyborg:allocate_license", { workspaceId });
  }

  /** Free the seat on `workspaceId` (owner-only). Same response shape as allocate. */
  async deallocateLicense(workspaceId: string): Promise<LicenseAllocationResponse> {
    return this.request<LicenseAllocationResponse>("cyborg:deallocate_license", { workspaceId });
  }

  private async postBilling(
    path: string,
    workspaceId: string,
    interval?: "month" | "year",
  ): Promise<string> {
    const resp = await fetch(`${this.relayHttpBase}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.authToken}`,
      },
      // interval only rides the checkout call (yearly toggle); the portal call omits
      // it. undefined is dropped by JSON.stringify, so the body is unchanged there.
      body: JSON.stringify({ workspaceId, interval }),
    });
    if (!resp.ok) {
      const err = (await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))) as {
        error?: string;
      };
      throw new Error(err.error ?? `HTTP ${resp.status}`);
    }
    const { url } = (await resp.json()) as { url?: string };
    if (!url) throw new Error("no checkout url returned");
    return url;
  }

  revokeDeviceSession(sessionId: string): Promise<void> {
    return revokeDeviceSession(this.relayHttpBase, this.authToken, sessionId);
  }

  revokeCurrentDeviceSession(): Promise<void> {
    return revokeDeviceSession(this.relayHttpBase, this.authToken);
  }

  listAccountSessions() {
    return listAccountSessions(this.relayHttpBase, this.authToken);
  }

  startPairing() {
    return startPairing(this.relayHttpBase, this.authToken);
  }

  getPairing(id: string) {
    return getPairing(this.relayHttpBase, this.authToken, id);
  }

  decidePairing(id: string, decision: "approve" | "deny", matchCode?: string) {
    return decidePairing(this.relayHttpBase, this.authToken, id, decision, matchCode);
  }

  revokeOtherDeviceSessions() {
    return revokeOtherDeviceSessions(this.relayHttpBase, this.authToken);
  }

  revealDeviceSessionIp(sessionId: string) {
    return revealDeviceSessionIp(this.relayHttpBase, this.authToken, sessionId);
  }

  listSecurityEvents(query: SecurityEventsQuery) {
    return listSecurityEvents(this.relayHttpBase, this.authToken, query);
  }

  /** One account REST call (lib/*-api.ts) on this connection's relay origin and pass. */
  relayHttp<T>(run: (base: string, token: string) => Promise<T>): Promise<T> {
    return run(this.relayHttpBase, this.authToken);
  }

  // ─── Account emails (0125) ──────────────────────────────────────
  // Several verified addresses per account: sign in with any, one default.
  // REST (not WS): these are account-level, not
  // workspace-scoped, so they do not belong on a workspace socket. Every mutator
  // resolves to the refreshed list, so callers never re-fetch to learn the state.

  /** Every verified address on this account, default first. */
  listAccountEmails(): Promise<AccountEmail[]> {
    return listAccountEmails(this.relayHttpBase, this.authToken);
  }

  /** Mail a verification code to a new address. */
  startAddEmail(email: string): Promise<{ devCode?: string }> {
    return startAddEmail(this.relayHttpBase, this.authToken, email);
  }

  /** Redeem the code and attach the address. */
  verifyAddEmail(email: string, code: string): Promise<AccountEmail[]> {
    return verifyAddEmail(this.relayHttpBase, this.authToken, email, code);
  }

  /** Remove an address. The identity anchor cannot be removed. */
  removeAccountEmail(id: string): Promise<AccountEmail[]> {
    return removeAccountEmail(this.relayHttpBase, this.authToken, id);
  }

  /** Point the account default at one of my addresses. */
  setPrimaryAccountEmail(id: string): Promise<AccountEmail[]> {
    return setPrimaryAccountEmail(this.relayHttpBase, this.authToken, id);
  }

  // ─── GitHub App → Tasks issue sync (bind a repo to a Tasks-project) ──────
  // HTTP (not WS) — these hit the authed /api/github/* callbacks in routes/github.ts
  // (no WS analogue, same pattern as the Stripe REST helpers above).

  /** The repo bindings for a Tasks-project (drives the "connected to <owner/name>"
   *  state in the settings panel). `tasksProjectId` is the tasks_projects.id. */
  async fetchGithubRepoSyncs(tasksProjectId: string): Promise<GithubRepoSync[]> {
    const resp = await this.githubFetch(
      `/api/github/repo-syncs?tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
    );
    const { syncs } = (await resp.json()) as { syncs: GithubRepoSync[] };
    return syncs;
  }

  /** The repo bindings across an ENTIRE workspace (the detail page's "Project Issue
   *  Sync" list — every project's bindings). */
  async fetchGithubRepoSyncsForWorkspace(workspaceId: string): Promise<GithubRepoSync[]> {
    const resp = await this.githubFetch(
      `/api/github/repo-syncs?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    const { syncs } = (await resp.json()) as { syncs: GithubRepoSync[] };
    return syncs;
  }

  /** Bind a repository to a Tasks-project (the bindRepoSync RPC). Returns the new
   *  binding id. The repo identity (installationId/repoId/owner/name) comes from
   *  the picker; repoUrl is optional (defaulted server-side from owner/name).
   *  syncDirection ('inbound'|'bidirectional') + the issue state overrides are
   *  optional (Image #4); omitted → server defaults ('inbound', no override). */
  async bindGithubRepoSync(opts: {
    tasksProjectId: string;
    installationId: string;
    repoId: string;
    owner: string;
    name: string;
    repoUrl?: string;
    syncDirection?: "inbound" | "bidirectional";
    issueOpenStateId?: string | null;
    issueClosedStateId?: string | null;
  }): Promise<string> {
    const resp = await this.githubFetch("/api/github/repo-sync", {
      method: "POST",
      body: JSON.stringify(opts),
    });
    const { id } = (await resp.json()) as { id: string };
    return id;
  }

  /** Edit a binding's sync direction / issue state map (Image #5 edit). Only the
   *  supplied fields are changed. */
  async patchGithubRepoSync(
    id: string,
    patch: {
      syncDirection?: "inbound" | "bidirectional";
      issueOpenStateId?: string | null;
      issueClosedStateId?: string | null;
    },
  ): Promise<void> {
    await this.githubFetch(`/api/github/repo-sync/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  }

  /** Remove a repo binding by id. */
  async unbindGithubRepoSync(id: string): Promise<void> {
    await this.githubFetch(`/api/github/repo-sync/${encodeURIComponent(id)}`, { method: "DELETE" });
  }

  /** Every GitHub App installation this workspace authorized (the account/org picker
   *  + the detail page's connected-org row[s]). */
  async fetchGithubInstallations(workspaceId: string): Promise<GithubInstallation[]> {
    const resp = await this.githubFetch(
      `/api/github/installations?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    const { installations } = (await resp.json()) as { installations: GithubInstallation[] };
    return installations;
  }

  /** Claim a freshly-installed GitHub App for this workspace, recording the
   *  github_installations row the connected state reads from. Called by the integrations
   *  card after the post-install redirect (?github=installed&installation_id=…). The
   *  relay membership-checks the workspace, refuses to reassign an installation another
   *  workspace already owns, and verifies a new claim against GitHub before recording it. */
  async confirmGithubInstallation(workspaceId: string, installationId: string): Promise<void> {
    await this.githubFetch("/api/github/installations/confirm", {
      method: "POST",
      body: JSON.stringify({ workspaceId, installationId }),
    });
  }

  /** Confirm a Tasks/onboarding install handed back by /welcome/github, bound to its signed state. */
  confirmGithubInstallReturn(p: PendingGithubInstall): Promise<GithubConfirmOutcome> {
    return confirmGithubInstall(this.relayHttpBase, this.authToken, p);
  }

  /** Disconnect an installation from a workspace (drops its bindings too). */
  async disconnectGithubInstallation(workspaceId: string, installationId: string): Promise<void> {
    await this.githubFetch(
      `/api/github/installation/${encodeURIComponent(installationId)}` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
  }

  /** The PR-state → task-state mappings for a project (Image #3). */
  async fetchGithubPrMappings(tasksProjectId: string): Promise<GithubPrStateMapping[]> {
    const resp = await this.githubFetch(
      `/api/github/pr-mappings?tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
    );
    const { mappings } = (await resp.json()) as { mappings: GithubPrStateMapping[] };
    return mappings;
  }

  /** Upsert one PR-state mapping (one row per (project, prState)). Returns its id. */
  async upsertGithubPrMapping(opts: {
    tasksProjectId: string;
    prState: string;
    taskStateId?: string | null;
    skipBackward?: boolean;
  }): Promise<string> {
    const resp = await this.githubFetch("/api/github/pr-mappings", {
      method: "POST",
      body: JSON.stringify(opts),
    });
    const { id } = (await resp.json()) as { id: string };
    return id;
  }

  /** Remove a PR-state mapping by id. */
  async deleteGithubPrMapping(id: string): Promise<void> {
    await this.githubFetch(`/api/github/pr-mapping/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }

  /** The personal GitHub accounts connected for this workspace — drives the detail
   *  page's authoritative "Personal account connected" state (persists across
   *  reloads, not just the transient OAuth redirect note). The relay returns only
   *  safe fields, never the OAuth access token. */
  async fetchGithubUserConnections(
    workspaceId: string,
  ): Promise<{ connections: GithubUserConnection[] }> {
    const resp = await this.githubFetch(
      `/api/github/user-connections?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    return (await resp.json()) as { connections: GithubUserConnection[] };
  }

  /** Start the personal-account OAuth flow (Image #3 "Connect Personal Account").
   *  Returns the GitHub authorize URL to navigate the browser to, or
   *  `{ configured:false }` when GITHUB_OAUTH_* is unset (→ the UI shows a
   *  not-configured state). The caller does `window.location.href = url`. */
  async startGithubOAuth(
    workspaceId: string,
    returnTo?: string,
  ): Promise<{ configured: boolean; url?: string }> {
    const qs = new URLSearchParams({ workspaceId });
    if (returnTo) qs.set("return", returnTo);
    const resp = await this.githubFetch(`/api/github/oauth/start?${qs.toString()}`);
    return (await resp.json()) as { configured: boolean; url?: string };
  }

  /** The repos an installation can access (the picker source). `configured:false`
   *  means the GitHub App's live creds are absent (Phase 3 pending) → the UI falls
   *  back to a manual repo-entry form. `tasksProjectId` scopes the request to the
   *  caller's workspace so the server can authorize it (BOLA guard). */
  async fetchGithubInstallationRepos(
    installationId: string,
    tasksProjectId: string,
  ): Promise<{ configured: boolean; repos: GithubInstallationRepo[] }> {
    const resp = await this.githubFetch(
      `/api/github/installation-repos?installationId=${encodeURIComponent(installationId)}` +
        `&tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
    );
    return (await resp.json()) as { configured: boolean; repos: GithubInstallationRepo[] };
  }

  /** The GitHub App's public config: its slug and the ready-to-use install URL
   *  (github.com/apps/<slug>/installations/new?state=<workspaceId>). Pass the current
   *  `workspaceId` so the server embeds it as the `state` GitHub echoes back to the
   *  callback (→ the user lands on that workspace's integration detail page).
   *  `configured:false` / `slug:null` / `installUrl:null` means the App isn't
   *  registered yet, so the UI disables the Connect button with a clear hint. */
  async fetchGithubAppConfig(
    workspaceId?: string,
    returnTo?: OAuthReturnName,
  ): Promise<{ configured: boolean; slug: string | null; installUrl: string | null }> {
    const path = workspaceId
      ? `/api/github/config?workspaceId=${encodeURIComponent(workspaceId)}${returnTo ? `&returnTo=${returnTo}` : ""}`
      : "/api/github/config";
    const resp = await this.githubFetch(path);
    return (await resp.json()) as {
      configured: boolean;
      slug: string | null;
      installUrl: string | null;
    };
  }

  // Shared fetch for the /api/github/* callbacks: bearer auth + JSON content type +
  // a thrown {error} on a non-2xx (same convention as postBilling above).
  private async githubFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const resp = await fetch(`${this.relayHttpBase}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.authToken}`,
        ...init.headers,
      },
    });
    if (!resp.ok) {
      const err = (await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))) as {
        error?: string;
      };
      throw new Error(err.error ?? `HTTP ${resp.status}`);
    }
    return resp;
  }

  // ─── Slack customer-comms bridge (OAuth install + channel links) ──────
  // HTTP (not WS) — these hit the authed /api/slack/* callbacks in routes/slack-oauth.ts
  // (requireAuth + isMember). Mirror the github* helpers exactly.

  /** The Slack app's public config: the credential gate + the ready-to-use install URL
   *  (the Slack v2 OAuth authorize URL, carrying a signed `state` for `workspaceId`).
   *  `configured:false` / `installUrl:null` means the relay's Slack secrets aren't set
   *  yet, so the UI shows a clear "not configured" state. Pass the current `workspaceId`
   *  so the server embeds it (signed) and the post-install redirect lands here.
   *
   *  `returnTo` is a NAME the relay looks up in its own table, never a path — see
   *  RETURN_PATHS in routes/slack-oauth.ts. "onboarding" lands the install window on a
   *  static "you can close this" page instead of booting the workspace SPA inside it.
   *  Omit it and the redirect is unchanged (the Slack integration detail page). */
  async fetchSlackConfig(
    workspaceId?: string,
    returnTo?: SlackReturnTarget,
  ): Promise<{ configured: boolean; installUrl: string | null }> {
    // Without a workspaceId the server mints no state at all, so returnTo would be
    // meaningless — keep the bare path exactly as before.
    const query = new URLSearchParams({ workspaceId: workspaceId ?? "" });
    if (returnTo) query.set("returnTo", returnTo);
    const path = workspaceId ? `/api/slack/config?${query.toString()}` : "/api/slack/config";
    const resp = await this.slackFetch(path);
    return (await resp.json()) as { configured: boolean; installUrl: string | null };
  }

  /** The settings page's authorize URL for THE personal consent — reading the person's own
   *  channels, posting under their name, and their DMs, asked for once.
   *
   *  THERE USED TO BE THREE OF THESE, one per capability, on the reasoning that Slack grants
   *  are additive and cannot be narrowed later. What that produced was three screens for one
   *  connection and two Reconnect buttons that refreshed nothing, because a narrower
   *  authorize cannot fetch a scope only the wider set contains. The relay now serves one
   *  scope set from one handler (/api/slack/user-config and this alias share it), so the ask
   *  cannot drift between entry points. The scope set and the storing marker are still
   *  decided relay-side, inside an HMAC-signed state only an authed route mints.
   *
   *  TAKING THIS CONSENT AUTHORIZES NOTHING TO RUN UNATTENDED. It says what the token MAY do.
   *  Whether an agent may spend it with nobody watching is a separate fact decided by whether
   *  the turn is firing under a schedule this person created.
   *
   *  `returnTo` is the same NAME-not-a-path parameter fetchSlackConfig takes — see
   *  RETURN_PATHS in routes/slack-oauth.ts. */
  async fetchSlackUserDmConfig(
    workspaceId: string,
    returnTo?: SlackReturnTarget,
  ): Promise<{ configured: boolean; installUrl: string | null }> {
    const query = new URLSearchParams({ workspaceId });
    if (returnTo) query.set("returnTo", returnTo);
    const resp = await this.slackFetch(`/api/slack/user-dm-config?${query.toString()}`);
    return (await resp.json()) as { configured: boolean; installUrl: string | null };
  }

  /** Does the CALLER have a stored personal Slack connection, and is it live.
   *
   *  Always about the caller — there is no userId parameter, because whether another member
   *  holds a live personal Slack credential is not a fact this surface should be able to
   *  answer. `revokedAt` set with connected:false means Slack ended the grant (reconnect);
   *  connected:false with no revokedAt means they never connected. Never the token. */
  async fetchSlackUserConnection(workspaceId: string): Promise<SlackUserConnection> {
    const resp = await this.slackFetch(
      `/api/slack/user-connection?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    return (await resp.json()) as SlackUserConnection;
  }

  /** Disconnect the CALLER's own personal Slack connection: revoked at Slack, then deleted
   *  here. `revoked:false` means Slack did not confirm the revoke — the local credential is
   *  gone either way, which is the right order when the alternative is leaving it at rest. */
  async disconnectSlackUserConnection(workspaceId: string): Promise<{ revoked: boolean }> {
    const resp = await this.slackFetch(
      `/api/slack/user-connection?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
    return (await resp.json()) as { revoked: boolean };
  }

  /** DESIGNATE the agent that may answer in Slack under the CALLER's own name, and the
   *  conversations that designation covers. Self only, like the two above: the relay keys the
   *  row by (workspace, authenticated user) and takes no userId, because consenting on somebody
   *  else's behalf here would mean arming an agent on a colleague's private conversations.
   *
   *  `cyboId: null` turns it off, and the relay drops the conversation list with it — so pass
   *  an empty `channelIds` when clearing, or the echo below would name rooms that were never
   *  stored. The relay also trims, dedupes and caps the ids, which is why the response is what
   *  it ACTUALLY stored rather than an `ok` — render that, never the value you sent.
   *
   *  `scope: "everywhere"` (0219) says the designation covers every conversation this person is
   *  in instead of the list — the widest thing this route can be asked for, which is why it is a
   *  named value rather than a sentinel in `channelIds` or the absence of one. The relay
   *  allowlists exactly that one word and reads anything else as the list, and it clears the
   *  mode with the agent, so the echo is again the only honest thing to render. Send
   *  `channelIds` along even when the mode is everywhere: the relay keeps the list so turning
   *  the mode off returns somebody to their conversations rather than to none.
   *
   *  `signature` (0222) is OMITTED unless the footer is what changed — the relay writes that
   *  setting only when one of its keys is present, so leaving it off is the only way to move the
   *  agent or the rooms without resetting a chosen wording. Bad wording earns a 400. */
  async setSlackUserAutoReply(
    workspaceId: string,
    cyboId: string | null,
    channelIds: string[],
    scope: "everywhere" | null = null,
    signature?: { signature: string | null; signatureEnabled: boolean },
  ): Promise<{
    ok: true;
    autoReplyCyboId: string | null;
    autoReplyChannelIds: string[];
    autoReplyScope?: "everywhere" | null;
    autoReplySignature?: string | null;
    autoReplySignatureEnabled?: boolean | null;
  }> {
    const resp = await this.slackFetch("/api/slack/user-connection/auto-reply", {
      method: "POST",
      body: JSON.stringify({ workspaceId, cyboId, channelIds, scope, ...signature }),
    });
    return (await resp.json()) as {
      ok: true;
      autoReplyCyboId: string | null;
      autoReplyChannelIds: string[];
      autoReplyScope?: "everywhere" | null;
      autoReplySignature?: string | null;
      autoReplySignatureEnabled?: boolean | null;
    };
  }

  /** Switch "post my messages to Slack as me" for the CALLER (0280). Throws the relay's reason
   *  (e.g. "not_proven") when it cannot be switched on. */
  async setSlackPostAsMe(workspaceId: string, enabled: boolean): Promise<SlackPostAsMe> {
    const resp = await this.slackFetch("/api/slack/user-connection/post-as-me", {
      method: "POST",
      body: JSON.stringify({ workspaceId, enabled }),
    });
    return ((await resp.json()) as { postAsMe: SlackPostAsMe }).postAsMe;
  }

  /** Every Slack installation this workspace authorized (the detail page's connected
   *  workspace row[s]). The relay returns only safe fields, never the bot access token. */
  async fetchSlackInstallations(workspaceId: string): Promise<SlackInstallation[]> {
    const resp = await this.slackFetch(
      `/api/slack/installations?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    const { installations } = (await resp.json()) as { installations: SlackInstallation[] };
    return installations;
  }

  /** Disconnect a Slack installation from this workspace (drops its channel links too,
   *  via the 0045 cascade). Workspace-scoped + BOLA-guarded server-side.
   *
   *  `revoked` is whether Slack's own `auth.revoke` succeeded — i.e. whether the app's
   *  access to that Slack workspace actually ended, rather than only being forgotten here.
   *  The local disconnect happens EITHER WAY (the relay never lets a Slack failure keep
   *  somebody connected), so `false` is not an error to retry: it means the app may still
   *  be installed in Slack and somebody has to remove it there. `undefined` is an older
   *  relay, which made no attempt and makes no claim. */
  async disconnectSlack(
    workspaceId: string,
    installationId: string,
  ): Promise<{ revoked?: boolean }> {
    const resp = await this.slackFetch(
      `/api/slack/installation/${encodeURIComponent(installationId)}` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
    return (await resp.json().catch(() => ({}))) as { revoked?: boolean };
  }

  /** Slack sync: the Slack team's people and their standing here (owner/admin only). */
  async slackInviteCandidates(
    workspaceId: string,
    refresh = false,
    channelId?: string, // a channel's Members tab: also who is in its bridged Slack channel
  ): Promise<SlackInviteCandidatesResponse> {
    return this.request<SlackInviteCandidatesResponse>(
      "cyborg:slack_invite_candidates",
      {
        workspaceId,
        ...(refresh ? { refresh: true } : {}),
        ...(channelId ? { channelId } : {}),
      },
      refresh ? 60_000 : undefined,
    );
  }

  /** Slack sync: invite these Slack people by their Slack address (owner/admin only).
   *  `channelIds`: the Cyborg channels they land in, besides the mirrors of the Slack
   *  channels they are already in. */
  async slackInviteBulk(
    workspaceId: string,
    teamId: string,
    slackUserIds: string[],
    channelIds: string[] = [],
    resend = false, // a row's Resend: the relay sends the email again to someone already invited
  ): Promise<{ results: SlackInviteBulkResult[]; retryAfterSec?: number }> {
    return this.request(
      "cyborg:slack_invite_bulk",
      {
        workspaceId,
        teamId,
        slackUserIds,
        ...(channelIds.length > 0 ? { channelIds } : {}),
        ...(resend ? { resend } : {}),
      },
      60_000,
    );
  }

  /** This workspace's Slack↔Cyborg channel links (the detail page's links list). */
  async fetchSlackChannelLinks(workspaceId: string): Promise<SlackChannelLink[]> {
    const resp = await this.slackFetch(
      `/api/slack/channel-links?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    const { links } = (await resp.json()) as { links: SlackChannelLink[] };
    return links;
  }

  /** Which of this workspace's links mirror a PRIVATE Slack channel into a PUBLIC Cyborg
   *  channel — i.e. a private conversation readable by the whole workspace. Owner/admin
   *  only; the relay answers 403 to anyone else. Links created before the privacy column
   *  existed are resolved against Slack on demand and capped per request, so `unresolved`
   *  may be non-zero and re-running settles the next batch. */
  async fetchSlackLinkPrivacyAudit(workspaceId: string): Promise<SlackLinkPrivacyAudit> {
    const resp = await this.slackFetch(
      `/api/slack/channel-links/privacy-audit?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    return (await resp.json()) as SlackLinkPrivacyAudit;
  }

  /** The Slack channels this installation can see, for the channel picker. `truncated`
   *  says the server's cap stopped the walk, so the list is the first `limit` channels
   *  and not the whole workspace. Workspace-scoped + BOLA-guarded server-side. */
  async fetchSlackConversations(
    workspaceId: string,
    installationId: string,
  ): Promise<{ channels: SlackConversation[]; truncated: boolean; limit: number }> {
    const resp = await this.slackFetch(
      `/api/slack/conversations?workspaceId=${encodeURIComponent(workspaceId)}` +
        `&installationId=${encodeURIComponent(installationId)}`,
    );
    return (await resp.json()) as {
      channels: SlackConversation[];
      truncated: boolean;
      limit: number;
    };
  }

  /** The conversations THIS person's own Slack account is in, asked of Slack when called
   *  (0219) — the private channels they are in included, which is the whole reason it exists
   *  (the workspace install holds a BOT token, and a private channel nobody invited the bot
   *  into does not exist as far as it is concerned), and their 1:1 and group DMs, which is what
   *  lets a DM be enrolled by name instead of by pasting a raw `D0C22MNBGKG`.
   *
   *  IT USED TO BE A CACHE, and the two things that followed were both reported as product
   *  bugs: it was filled once during the OAuth callback, so it answered EMPTY for anyone who
   *  opened a settings page later, and it never held a DM at all. There is no cache now — a
   *  DM opened five minutes ago is exactly the value a TTL would hide — so callers that open
   *  a picker repeatedly should keep their own freshness window, as the settings page does.
   *
   *  Same shape as fetchSlackConversations (so one picker reads either) minus the
   *  installationId — the list is keyed by the CALLER server-side, not by an install row —
   *  plus `connected`, and per row the additive `kind`/`slackUserId`. A row's `name` is ""
   *  when there is no human label for it (an unresolved DM): render the id, never the
   *  `slackUserId`.
   *
   *  `connected:false` is EVERY way of having no usable personal Slack — never connected, a
   *  grant Slack ended, one that never carried the listing scope, one that will not decrypt —
   *  because the way out of all four is the same Connect button. It is NOT what a Slack
   *  failure answers: that throws (the route is a 502), so a probe that could not be answered
   *  must not be reported as "not connected". */
  async fetchSlackUserConversations(workspaceId: string): Promise<{
    connected: boolean;
    channels: SlackConversation[];
    truncated: boolean;
    limit: number;
  }> {
    const resp = await this.slackFetch(
      `/api/slack/user-conversations?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    return (await resp.json()) as {
      connected: boolean;
      channels: SlackConversation[];
      truncated: boolean;
      limit: number;
    };
  }

  /** Link a Slack channel to a Cyborg channel. Returns the new link id, whether Cyborg
   *  added itself to the Slack channel just now, and a `warning` naming the manual step
   *  still owed when it could not. The server BOLA-guards that both the installation and
   *  the cyborg channel belong to the workspace, AND refuses a channel it cannot reach —
   *  a link the app was never invited to is a bridge that silently never fires. */
  async linkSlackChannel(opts: {
    workspaceId: string;
    installationId: string;
    cyborgChannelId: string;
    slackChannelId: string;
    slackTeamId: string;
    /** Confirm re-pointing this workspace's existing bridge (bridge_move_requires_replace). */
    replace?: boolean;
    /** Take the link over from another workspace you administer (link_owned_elsewhere). */
    moveFromOtherWorkspace?: boolean;
  }): Promise<{ id: string; joined: boolean; warning?: string }> {
    const resp = await this.slackFetch("/api/slack/channel-links", {
      method: "POST",
      body: JSON.stringify(opts),
    });
    return (await resp.json()) as { id: string; joined: boolean; warning?: string };
  }

  /** Unlink a Slack↔Cyborg channel link (workspace-scoped + BOLA-guarded server-side). */
  async unlinkSlackChannel(workspaceId: string, id: string): Promise<void> {
    await this.slackFetch(
      `/api/slack/channel-link/${encodeURIComponent(id)}` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
  }

  // Shared fetch for the /api/slack/* callbacks: bearer auth + JSON content type + a
  // thrown {error} on a non-2xx (identical convention to githubFetch above).
  private async slackFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const resp = await fetchOrTransportError(`${this.relayHttpBase}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.authToken}`,
        ...init.headers,
      },
    });
    if (!resp.ok) {
      const err = (await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))) as {
        error?: string;
        code?: string;
        current?: { cyborgChannelId: string; name: string } | null;
      };
      throw new ApiCodedError(err.error ?? `HTTP ${resp.status}`, err.code, err.current);
    }
    return resp;
  }

  // ─── Jira Cloud → Tasks integration (bind a Jira project to a Tasks-project) ──────
  // HTTP (not WS) — these hit the authed /api/jira/* routes in routes/jira.ts
  // (requireAuth + isMember). Mirror the github* helpers exactly.

  /** The Jira app's public config: the credential gate + the ready-to-use Atlassian
   *  authorize URL (carrying a signed `state` for `workspaceId`). `configured:false` /
   *  `authorizeUrl:null` means the relay's Jira OAuth secrets aren't set yet. Pass the
   *  current `workspaceId` so the server embeds it (signed) and the post-connect
   *  redirect lands on this workspace's Jira detail page. */
  async fetchJiraConfig(
    workspaceId?: string,
    returnTo?: OAuthReturnName,
  ): Promise<{ configured: boolean; authorizeUrl: string | null }> {
    const path = workspaceId
      ? `/api/jira/config?workspaceId=${encodeURIComponent(workspaceId)}${returnTo ? `&returnTo=${returnTo}` : ""}`
      : "/api/jira/config";
    const resp = await this.jiraFetch(path);
    return (await resp.json()) as { configured: boolean; authorizeUrl: string | null };
  }

  /** Every Jira installation this workspace authorized (the connected-site row[s] + the
   *  bind picker's install source). The relay STRIPS every token. */
  async fetchJiraInstallations(workspaceId: string): Promise<JiraInstallation[]> {
    const resp = await this.jiraFetch(
      `/api/jira/installations?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    const { installations } = (await resp.json()) as { installations: JiraInstallation[] };
    return installations;
  }

  /** Disconnect a Jira installation from this workspace (its project bindings cascade
   *  away). Workspace-scoped + BOLA-guarded server-side. */
  async disconnectJira(workspaceId: string, installationId: string): Promise<void> {
    await this.jiraFetch(
      `/api/jira/installation/${encodeURIComponent(installationId)}` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
  }

  /** The Jira project bindings for a Tasks-project (the detail page's bindings list).
   *  `tasksProjectId` is the bare chat project id or the `tp_` id (server resolves both). */
  async fetchJiraProjectSyncs(tasksProjectId: string): Promise<JiraProjectSync[]> {
    const resp = await this.jiraFetch(
      `/api/jira/project-syncs?tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
    );
    const { syncs } = (await resp.json()) as { syncs: JiraProjectSync[] };
    return syncs;
  }

  /** Bind a Cyborg tasks-project to a Jira project. The server seeds status mappings by
   *  category affinity and (best-effort) registers the inbound webhook — the returned
   *  `webhook` says whether it auto-registered or needs a manual paste. */
  async bindJiraProjectSync(opts: {
    tasksProjectId: string;
    installationId: string;
    externalProjectId: string;
    externalProjectName?: string;
    externalUrl?: string;
    syncDirection?: "inbound" | "outbound" | "bidirectional";
  }): Promise<JiraBindResult> {
    const resp = await this.jiraFetch("/api/jira/project-syncs", {
      method: "POST",
      body: JSON.stringify(opts),
    });
    return (await resp.json()) as JiraBindResult;
  }

  /** Unbind a Jira project binding (workspace-scoped + BOLA-guarded server-side). */
  async unbindJiraProjectSync(workspaceId: string, id: string): Promise<void> {
    await this.jiraFetch(
      `/api/jira/project-sync/${encodeURIComponent(id)}` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
  }

  /** A binding's live source-status catalog + current mappings (the mapping editor).
   *  `tasksProjectId` scopes the request so the server can resolve + authorize the
   *  binding (BOLA). `statusError` is a best-effort note if the live fetch failed. */
  async fetchJiraStatuses(id: string, tasksProjectId: string): Promise<JiraStatusesResult> {
    const resp = await this.jiraFetch(
      `/api/jira/project-syncs/${encodeURIComponent(id)}/statuses` +
        `?tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
    );
    return (await resp.json()) as JiraStatusesResult;
  }

  /** Save a binding's source-status → task-state mappings (replaces/upserts the set).
   *  Jira reads `tasksProjectId` from the QUERY string (unlike ClickUp's body). */
  async saveJiraStatusMappings(
    id: string,
    tasksProjectId: string,
    mappings: IntegrationStatusMappingInput[],
  ): Promise<{ ok: boolean; count: number }> {
    const resp = await this.jiraFetch(
      `/api/jira/project-syncs/${encodeURIComponent(id)}/status-mappings` +
        `?tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
      { method: "PUT", body: JSON.stringify({ mappings }) },
    );
    return (await resp.json()) as { ok: boolean; count: number };
  }

  /** Run a paged import (backfill) of the bound Jira project's issues into the Tasks
   *  project via the shared sync engine. `capped` → re-run to continue. */
  async importJiraProjectSync(id: string, tasksProjectId: string): Promise<JiraImportResult> {
    const resp = await this.jiraFetch(
      `/api/jira/project-syncs/${encodeURIComponent(id)}/import` +
        `?tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
      { method: "POST" },
    );
    return (await resp.json()) as JiraImportResult;
  }

  // Shared fetch for the /api/jira/* routes: bearer auth + JSON content type + a thrown
  // {error} on a non-2xx (identical convention to githubFetch above).
  private async jiraFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const resp = await fetch(`${this.relayHttpBase}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.authToken}`,
        ...init.headers,
      },
    });
    if (!resp.ok) {
      const err = (await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))) as {
        error?: string;
      };
      throw new Error(err.error ?? `HTTP ${resp.status}`);
    }
    return resp;
  }

  // ─── ClickUp → Tasks integration (bind a ClickUp List to a Tasks-project) ──────
  // HTTP (not WS) — these hit the authed /api/clickup/* routes in routes/clickup.ts
  // (requireAuth + isMember). Mirror the jira* helpers exactly; the only shape
  // differences are the List picker (a `path` breadcrumb), the raw List id as the
  // external key, and the boolean `webhookRegistered` on bind.

  /** The ClickUp app's public config: the credential gate + the ready-to-use authorize
   *  URL (carrying a signed `state` for `workspaceId`). `configured:false` /
   *  `authorizeUrl:null` means the relay's ClickUp OAuth secrets aren't set yet. */
  async fetchClickUpConfig(
    workspaceId?: string,
    returnTo?: OAuthReturnName,
  ): Promise<{ configured: boolean; authorizeUrl: string | null }> {
    const path = workspaceId
      ? `/api/clickup/config?workspaceId=${encodeURIComponent(workspaceId)}${returnTo ? `&returnTo=${returnTo}` : ""}`
      : "/api/clickup/config";
    const resp = await this.clickupFetch(path);
    return (await resp.json()) as { configured: boolean; authorizeUrl: string | null };
  }

  /** Every ClickUp installation this workspace authorized (the connected-workspace row[s]
   *  + the bind picker's install source). The relay STRIPS the token + webhook secrets. */
  async fetchClickUpInstallations(workspaceId: string): Promise<ClickUpInstallation[]> {
    const resp = await this.clickupFetch(
      `/api/clickup/installations?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    const { installations } = (await resp.json()) as { installations: ClickUpInstallation[] };
    return installations;
  }

  /** Disconnect a ClickUp installation from this workspace. Workspace-scoped +
   *  BOLA-guarded server-side. */
  async disconnectClickUp(workspaceId: string, installationId: string): Promise<void> {
    await this.clickupFetch(
      `/api/clickup/installation/${encodeURIComponent(installationId)}` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
  }

  /** The ClickUp List bindings for a Tasks-project (the detail page's bindings list). */
  async fetchClickUpProjectSyncs(tasksProjectId: string): Promise<ClickUpProjectSync[]> {
    const resp = await this.clickupFetch(
      `/api/clickup/project-syncs?tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
    );
    const { syncs } = (await resp.json()) as { syncs: ClickUpProjectSync[] };
    return syncs;
  }

  /** Bind a Cyborg tasks-project to a ClickUp List. The server seeds status mappings by
   *  category affinity and (best-effort) registers the inbound webhook — `webhookRegistered`
   *  says whether inbound sync is now wired. */
  async bindClickUpProjectSync(opts: {
    tasksProjectId: string;
    installationId: string;
    externalProjectId: string;
    externalProjectName?: string;
    syncDirection?: "inbound" | "outbound" | "bidirectional";
  }): Promise<ClickUpBindResult> {
    const resp = await this.clickupFetch("/api/clickup/project-syncs", {
      method: "POST",
      body: JSON.stringify(opts),
    });
    return (await resp.json()) as ClickUpBindResult;
  }

  /** Unbind a ClickUp List binding (workspace-scoped + BOLA-guarded server-side). */
  async unbindClickUpProjectSync(workspaceId: string, id: string): Promise<void> {
    await this.clickupFetch(
      `/api/clickup/project-sync/${encodeURIComponent(id)}` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
  }

  /** A binding's live source-status catalog + current mappings (the mapping editor).
   *  `tasksProjectId` scopes the request so the server resolves + authorizes the binding. */
  async fetchClickUpStatuses(id: string, tasksProjectId: string): Promise<ClickUpStatusesResult> {
    const resp = await this.clickupFetch(
      `/api/clickup/project-syncs/${encodeURIComponent(id)}/statuses` +
        `?tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
    );
    return (await resp.json()) as ClickUpStatusesResult;
  }

  /** Save a binding's source-status → task-state mappings (replaces/upserts the set).
   *  ClickUp reads `tasksProjectId` from the BODY (unlike Jira's query string). */
  async saveClickUpStatusMappings(
    id: string,
    tasksProjectId: string,
    mappings: IntegrationStatusMappingInput[],
  ): Promise<{ mappings: ClickUpStatusMapping[] }> {
    const resp = await this.clickupFetch(
      `/api/clickup/project-syncs/${encodeURIComponent(id)}/status-mappings`,
      { method: "PUT", body: JSON.stringify({ tasksProjectId, mappings }) },
    );
    return (await resp.json()) as { mappings: ClickUpStatusMapping[] };
  }

  /** Run a paged import (backfill) of the bound ClickUp List's tasks into the Tasks
   *  project via the shared sync engine. */
  async importClickUpProjectSync(id: string, tasksProjectId: string): Promise<ClickUpImportResult> {
    const resp = await this.clickupFetch(
      `/api/clickup/project-syncs/${encodeURIComponent(id)}/import` +
        `?tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
      { method: "POST" },
    );
    return (await resp.json()) as ClickUpImportResult;
  }

  // Shared fetch for the /api/clickup/* routes: bearer auth + JSON content type + a thrown
  // {error} on a non-2xx (identical convention to githubFetch above).
  private async clickupFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const resp = await fetch(`${this.relayHttpBase}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.authToken}`,
        ...init.headers,
      },
    });
    if (!resp.ok) {
      const err = (await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))) as {
        error?: string;
      };
      throw new Error(err.error ?? `HTTP ${resp.status}`);
    }
    return resp;
  }

  // ─── Linear → Tasks integration (bind a Linear Team to a Tasks-project) ──────
  // HTTP (not WS) — these hit the authed /api/linear/* routes in routes/linear.ts
  // (requireAuth + isMember). Mirror the jira* helpers exactly; the only shape
  // differences are the Team picker and the raw Linear team id as the
  // external key (Linear has one app-level webhook: no registration outcome on bind).

  /** The Linear app's public config: the credential gate + the ready-to-use authorize
   *  URL (carrying a signed `state` for `workspaceId`). `configured:false` /
   *  `authorizeUrl:null` means the relay's Linear OAuth secrets aren't set yet. */
  async fetchLinearConfig(
    workspaceId?: string,
    returnTo?: OAuthReturnName,
  ): Promise<{ configured: boolean; authorizeUrl: string | null }> {
    const path = workspaceId
      ? `/api/linear/config?workspaceId=${encodeURIComponent(workspaceId)}${returnTo ? `&returnTo=${returnTo}` : ""}`
      : "/api/linear/config";
    const resp = await this.linearFetch(path);
    return (await resp.json()) as { configured: boolean; authorizeUrl: string | null };
  }

  /** Every Linear installation this workspace authorized (the connected-workspace row[s]
   *  + the bind picker's install source). The relay STRIPS the token + webhook secrets. */
  async fetchLinearInstallations(workspaceId: string): Promise<LinearInstallation[]> {
    const resp = await this.linearFetch(
      `/api/linear/installations?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    const { installations } = (await resp.json()) as { installations: LinearInstallation[] };
    return installations;
  }

  /** Disconnect a Linear installation from this workspace. Workspace-scoped +
   *  BOLA-guarded server-side. */
  async disconnectLinear(workspaceId: string, installationId: string): Promise<void> {
    await this.linearFetch(
      `/api/linear/installation/${encodeURIComponent(installationId)}` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
  }

  /** The Linear Teams a connected installation can reach (the bind-time picker source).
   *  Each entry's `id` is the raw Linear team id bind stores; `path` is the team key.
   *   Degrades to [] on a provider error so the picker falls back to manual. */
  async fetchLinearTeams(
    installationId: string,
    workspaceId: string,
  ): Promise<LinearTeamPickerEntry[]> {
    const resp = await this.linearFetch(
      `/api/linear/installations/${encodeURIComponent(installationId)}/teams` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    const { teams } = (await resp.json()) as { teams: LinearTeamPickerEntry[] };
    return teams;
  }

  /** The Linear Team bindings for a Tasks-project (the detail page's bindings list). */
  async fetchLinearProjectSyncs(tasksProjectId: string): Promise<LinearProjectSync[]> {
    const resp = await this.linearFetch(
      `/api/linear/project-syncs?tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
    );
    const { syncs } = (await resp.json()) as { syncs: LinearProjectSync[] };
    return syncs;
  }

  /** Bind a Cyborg tasks-project to a Linear Team. The server seeds status mappings by
   *  category affinity. No per-binding webhook: Linear's one app-level webhook covers it. */
  async bindLinearProjectSync(opts: {
    tasksProjectId: string;
    installationId: string;
    externalProjectId: string;
    externalProjectName?: string;
    syncDirection?: "inbound" | "outbound" | "bidirectional";
  }): Promise<LinearBindResult> {
    const resp = await this.linearFetch("/api/linear/project-syncs", {
      method: "POST",
      body: JSON.stringify(opts),
    });
    return (await resp.json()) as LinearBindResult;
  }

  /** Unbind a Linear Team binding (workspace-scoped + BOLA-guarded server-side). */
  async unbindLinearProjectSync(workspaceId: string, id: string): Promise<void> {
    await this.linearFetch(
      `/api/linear/project-sync/${encodeURIComponent(id)}` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
  }

  /** A binding's live source-status catalog + current mappings (the mapping editor).
   *  `tasksProjectId` scopes the request so the server resolves + authorizes the binding. */
  async fetchLinearStatuses(id: string, tasksProjectId: string): Promise<LinearStatusesResult> {
    const resp = await this.linearFetch(
      `/api/linear/project-syncs/${encodeURIComponent(id)}/statuses` +
        `?tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
    );
    return (await resp.json()) as LinearStatusesResult;
  }

  /** Save a binding's source-status → task-state mappings (replaces/upserts the set).
   *  Linear reads `tasksProjectId` from the BODY (unlike Jira's query string). */
  async saveLinearStatusMappings(
    id: string,
    tasksProjectId: string,
    mappings: IntegrationStatusMappingInput[],
  ): Promise<{ mappings: LinearStatusMapping[] }> {
    const resp = await this.linearFetch(
      `/api/linear/project-syncs/${encodeURIComponent(id)}/status-mappings`,
      { method: "PUT", body: JSON.stringify({ tasksProjectId, mappings }) },
    );
    return (await resp.json()) as { mappings: LinearStatusMapping[] };
  }

  // Shared fetch for the /api/linear/* routes: bearer auth + JSON content type + a thrown
  // ApiCodedError carrying the relay's `code` (e.g. needs_reconnect) on a non-2xx.
  private async linearFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const resp = await fetch(`${this.relayHttpBase}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.authToken}`,
        ...init.headers,
      },
    });
    if (!resp.ok) {
      const err = (await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))) as {
        error?: string;
        code?: string;
      };
      throw new ApiCodedError(err.error ?? `HTTP ${resp.status}`, err.code);
    }
    return resp;
  }

  // ─── Asana → Tasks integration (bind an Asana project to a Tasks-project) ──────
  // HTTP (not WS): the authed /api/asana/* routes (routes/asana.ts and
  // routes/asana-project-sync.ts). Same shapes as the linear* helpers; the external key is
  // the Asana project gid. Import runs through the shared task-sync job.

  /** The Asana app's public config: the credential gate + the ready-to-use authorize
   *  URL (carrying a signed `state` for `workspaceId`). `configured:false` /
   *  `authorizeUrl:null` means the relay's Asana OAuth secrets aren't set yet. */
  async fetchAsanaConfig(
    workspaceId?: string,
    returnTo?: OAuthReturnName,
  ): Promise<{ configured: boolean; authorizeUrl: string | null }> {
    const path = workspaceId
      ? `/api/asana/config?workspaceId=${encodeURIComponent(workspaceId)}${returnTo ? `&returnTo=${returnTo}` : ""}`
      : "/api/asana/config";
    const resp = await this.asanaFetch(path);
    return (await resp.json()) as { configured: boolean; authorizeUrl: string | null };
  }

  /** Every Asana installation this workspace authorized (the connected-workspace row[s]
   *  + the bind picker's install source). The relay STRIPS the tokens. */
  async fetchAsanaInstallations(workspaceId: string): Promise<AsanaInstallation[]> {
    const resp = await this.asanaFetch(
      `/api/asana/installations?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    const { installations } = (await resp.json()) as { installations: AsanaInstallation[] };
    return installations;
  }

  /** Disconnect an Asana installation from this workspace. Workspace-scoped +
   *  BOLA-guarded server-side. */
  async disconnectAsana(workspaceId: string, installationId: string): Promise<void> {
    await this.asanaFetch(
      `/api/asana/installation/${encodeURIComponent(installationId)}` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
  }

  /** The Asana project bindings for a Tasks-project (the detail page's bindings list). */
  async fetchAsanaProjectSyncs(tasksProjectId: string): Promise<AsanaProjectSync[]> {
    const resp = await this.asanaFetch(
      `/api/asana/project-syncs?tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
    );
    const { syncs } = (await resp.json()) as { syncs: AsanaProjectSync[] };
    return syncs;
  }

  /** Bind a Cyborg tasks-project to an Asana project. The server seeds one status mapping
   *  per section and registers the project's webhook. */
  async bindAsanaProjectSync(opts: {
    tasksProjectId: string;
    installationId: string;
    externalProjectId: string;
    externalProjectName?: string;
    syncDirection?: "inbound" | "outbound" | "bidirectional";
  }): Promise<AsanaBindResult> {
    const resp = await this.asanaFetch("/api/asana/project-syncs", {
      method: "POST",
      body: JSON.stringify(opts),
    });
    return (await resp.json()) as AsanaBindResult;
  }

  /** Unbind an Asana project binding (workspace-scoped + BOLA-guarded server-side). */
  async unbindAsanaProjectSync(workspaceId: string, id: string): Promise<void> {
    await this.asanaFetch(
      `/api/asana/project-sync/${encodeURIComponent(id)}` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
  }

  /** A binding's live source-status catalog + current mappings (the mapping editor).
   *  `tasksProjectId` scopes the request so the server resolves + authorizes the binding. */
  async fetchAsanaStatuses(id: string, tasksProjectId: string): Promise<AsanaStatusesResult> {
    const resp = await this.asanaFetch(
      `/api/asana/project-syncs/${encodeURIComponent(id)}/statuses` +
        `?tasksProjectId=${encodeURIComponent(tasksProjectId)}`,
    );
    return (await resp.json()) as AsanaStatusesResult;
  }

  /** Save a binding's source-status → task-state mappings (replaces/upserts the set).
   *  Asana reads `tasksProjectId` from the BODY (unlike Jira's query string). */
  async saveAsanaStatusMappings(
    id: string,
    tasksProjectId: string,
    mappings: IntegrationStatusMappingInput[],
  ): Promise<{ mappings: AsanaStatusMapping[] }> {
    const resp = await this.asanaFetch(
      `/api/asana/project-syncs/${encodeURIComponent(id)}/status-mappings`,
      { method: "PUT", body: JSON.stringify({ tasksProjectId, mappings }) },
    );
    return (await resp.json()) as { mappings: AsanaStatusMapping[] };
  }

  // Shared fetch for the /api/asana/* routes: bearer auth + JSON content type + a thrown
  // ApiCodedError carrying the relay's `code` (e.g. needs_reconnect) on a non-2xx.
  private async asanaFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const resp = await fetch(`${this.relayHttpBase}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.authToken}`,
        ...init.headers,
      },
    });
    if (!resp.ok) {
      const err = (await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))) as {
        error?: string;
        code?: string;
      };
      throw new ApiCodedError(err.error ?? `HTTP ${resp.status}`, err.code);
    }
    return resp;
  }

  // Task sync wizard (CYBORG-1219), /api/task-sync/*. Returns the raw response:
  // task-sync-api.ts owns parsing and the typed error that keeps the server's `code`.
  async taskSyncFetch(path: string, init: RequestInit = {}): Promise<Response> {
    return fetch(`${this.relayHttpBase || CLOUD_RELAY_HTTP_BASE}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.authToken}`,
        ...init.headers,
      },
    });
  }

  // ─── Everhour, read-only (CYBORG-1068) ──────
  // HTTP (not WS) against /api/everhour/*. Status is readable by any member; the key
  // PUT/DELETE are owner/admin only. A non-2xx throws the server's `error` string.

  /** Whether this workspace has an Everhour key, and which Everhour account it reads as. */
  async fetchEverhourStatus(workspaceId: string): Promise<EverhourStatus> {
    const resp = await this.everhourFetch(
      `/api/everhour/status?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    return (await resp.json()) as EverhourStatus;
  }

  /** Store or replace the workspace's Everhour API key. The key is never returned. */
  async saveEverhourKey(workspaceId: string, apiKey: string): Promise<EverhourConnectedStatus> {
    const resp = await this.everhourFetch("/api/everhour/key", {
      method: "PUT",
      body: JSON.stringify({ workspaceId, apiKey }),
    });
    return (await resp.json()) as EverhourConnectedStatus;
  }

  /** Remove the workspace's Everhour API key. */
  async disconnectEverhour(workspaceId: string): Promise<void> {
    await this.everhourFetch(`/api/everhour/key?workspaceId=${encodeURIComponent(workspaceId)}`, {
      method: "DELETE",
    });
  }

  // Same convention as jiraFetch / clickupFetch: absolute relayHttpBase (a relative
  // "/api/..." breaks on desktop), bearer auth, and a thrown {error} on a non-2xx.
  private async everhourFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const resp = await fetch(`${this.relayHttpBase}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.authToken}`,
        ...init.headers,
      },
    });
    if (!resp.ok) {
      const err = (await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))) as {
        error?: string;
      };
      throw new Error(err.error ?? `HTTP ${resp.status}`);
    }
    return resp;
  }

  // ─── Microsoft Teams connect (server: routes/teams.ts) ──────
  // HTTP (not WS) — these hit the authed /api/teams/* routes (requireAuth + isMember).
  // Mirror the jira*/clickUp* helpers exactly. The first four are the READ-ONLY connect
  // half — authorize an Entra account and list what that one person can see. The
  // channel-link three below are the settings card's writes (CYBORG-974).

  /** The Teams app's public config: the credential gate + the ready-to-use Entra
   *  authorize URL (carrying a signed `state` for `workspaceId`). `configured:false` /
   *  `authorizeUrl:null` means the relay's TEAMS_OAUTH_* secrets aren't set yet. */
  async fetchTeamsConfig(
    workspaceId?: string,
    returnTo?: OAuthReturnName,
  ): Promise<{ configured: boolean; authorizeUrl: string | null }> {
    const path = workspaceId
      ? `/api/teams/config?workspaceId=${encodeURIComponent(workspaceId)}${returnTo ? `&returnTo=${returnTo}` : ""}`
      : "/api/teams/config";
    const resp = await this.teamsFetch(path);
    return (await resp.json()) as { configured: boolean; authorizeUrl: string | null };
  }

  /** Every Teams installation this workspace authorized. The relay STRIPS every token. */
  async fetchTeamsInstallations(workspaceId: string): Promise<TeamsInstallation[]> {
    const resp = await this.teamsFetch(
      `/api/teams/installations?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    const { installations } = (await resp.json()) as { installations: TeamsInstallation[] };
    return installations;
  }

  /** Disconnect a Teams installation from this workspace. Workspace-scoped +
   *  BOLA-guarded server-side. */
  async disconnectTeams(workspaceId: string, installationId: string): Promise<void> {
    await this.teamsFetch(
      `/api/teams/installation/${encodeURIComponent(installationId)}` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
  }

  /** The teams the connected person is a member of (Graph GET /me/joinedTeams).
   *  THROWS on a provider error — see fetchTeamsChannels for why. */
  async fetchTeamsJoinedTeams(workspaceId: string): Promise<TeamsJoinedTeam[]> {
    const resp = await this.teamsFetch(
      `/api/teams/joined-teams?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    const { teams } = (await resp.json()) as { teams: TeamsJoinedTeam[] };
    return teams;
  }

  /** The channels the connected person can see (Graph GET /teams/{id}/channels). With
   *  `teamId` → that one team; without → every joined team, flattened and capped
   *  (`truncated` says the cap was hit).
   *
   *  DELIBERATELY THROWS: this feeds the screen whose entire job is "here are your real
   *  channels", where an empty list caused by an outage is indistinguishable from an
   *  account with nothing in it. The server refuses too
   *  (502 + a code) rather than answering []. */
  async fetchTeamsChannels(workspaceId: string, teamId?: string): Promise<TeamsChannelList> {
    const resp = await this.teamsFetch(
      `/api/teams/channels?workspaceId=${encodeURIComponent(workspaceId)}` +
        (teamId ? `&teamId=${encodeURIComponent(teamId)}` : ""),
    );
    return (await resp.json()) as TeamsChannelList;
  }

  /** This workspace's Teams↔Cyborg channel links (server: GET /api/teams/channel-links,
   *  the provider-scoped twin of /api/slack/channel-links — caller-scoped, so it lists
   *  only links whose Cyborg channel the caller can already see).
   *
   *  The wire row is the SHARED slack_channel_links row (see TeamsChannelLink), so the
   *  channel/team ids arrive under the table's Slack-era column names. Read both: a route
   *  that renames them for the `teams` provider is equally correct, and this one mapping
   *  is the only place in the UI that should ever have to know. */
  async fetchTeamsChannelLinks(workspaceId: string): Promise<TeamsChannelLink[]> {
    const resp = await this.teamsFetch(
      `/api/teams/channel-links?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    const { links } = (await resp.json()) as {
      links: Array<
        Omit<TeamsChannelLink, "teamsChannelId" | "teamsTeamId"> & {
          teamsChannelId?: string;
          teamsTeamId?: string;
          slackChannelId?: string;
          slackTeamId?: string;
        }
      >;
    };
    return links.map((l) => ({
      id: l.id,
      workspaceId: l.workspaceId,
      installationId: l.installationId,
      cyborgChannelId: l.cyborgChannelId,
      teamsChannelId: l.teamsChannelId ?? l.slackChannelId ?? "",
      teamsTeamId: l.teamsTeamId ?? l.slackTeamId ?? "",
      syncDirection: l.syncDirection,
      createdBy: l.createdBy,
      createdAt: l.createdAt,
    }));
  }

  /** Bridge a Teams channel to a Cyborg channel. Workspace-scoped + BOLA-guarded
   *  server-side (the install, the Cyborg channel and the Teams channel must all belong
   *  to this workspace).
   *
   *  NO TEAM ID. The link row's team column holds the TENANT — routes/teams-bot.ts drops
   *  every inbound activity whose `channelData.tenant.id` does not equal it — and a Teams
   *  team is a Microsoft 365 group, a different GUID. The relay takes the tenant from the
   *  `tid` claim it captured at connect, so there is nothing correct for a caller to send.
   *
   *  Unlike Slack there is no `joined` in the answer, because there is nothing for Cyborg
   *  to join: the bot reaches a Teams channel only once the Cyborg7 app is INSTALLED in
   *  that team, which is a person's action in Teams and not an API call we can make. And
   *  it cannot be checked either — hence `verified:false` on every successful link, with
   *  `warning` carrying the relay's own sentence about it. Render the warning; a link with
   *  no status is the truth here, a green one would be invented. */
  async linkTeamsChannel(opts: {
    workspaceId: string;
    installationId: string;
    cyborgChannelId: string;
    teamsChannelId: string;
    replace?: boolean;
    moveFromOtherWorkspace?: boolean;
  }): Promise<{ id: string; verified?: boolean; warning?: string }> {
    const resp = await this.teamsFetch("/api/teams/channel-links", {
      method: "POST",
      body: JSON.stringify(opts),
    });
    return (await resp.json()) as { id: string; verified?: boolean; warning?: string };
  }

  /** Remove a Teams↔Cyborg channel link. Workspace-scoped + BOLA-guarded server-side. */
  async unlinkTeamsChannel(workspaceId: string, linkId: string): Promise<void> {
    await this.teamsFetch(
      `/api/teams/channel-link/${encodeURIComponent(linkId)}` +
        `?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: "DELETE" },
    );
  }

  // Shared fetch for the /api/teams/* routes: bearer auth + JSON content type + a thrown
  // {error} on a non-2xx (identical convention to jiraFetch / clickupFetch above).
  // relayHttpBase is ABSOLUTE on purpose — a document-relative "/api/..." silently returns
  // index.html on the desktop's cyborg://app origin (see the httpBase getter).
  private async teamsFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const resp = await fetch(`${this.relayHttpBase}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.authToken}`,
        ...init.headers,
      },
    });
    if (!resp.ok) {
      const err = (await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))) as {
        error?: string;
        message?: string;
        code?: string;
        current?: { cyborgChannelId: string; name: string } | null;
      };
      // `message` FIRST: the programmatic-install routes (routes/teams-install.ts) answer a
      // refusal with a sentence written for the person reading the card — "your administrator
      // has not approved this yet" rather than "install failed". Dropping it here would
      // flatten every named dead end back into an HTTP status, which is the whole bug that
      // ticket exists to prevent.
      throw new ApiCodedError(
        err.message ?? err.error ?? `HTTP ${resp.status}`,
        err.code,
        err.current,
      );
    }
    return resp;
  }

  // ─── Microsoft Teams PROGRAMMATIC INSTALL (server: routes/teams-install.ts) ──────
  // One admin approval per customer, then a button forever (CYBORG-978). The zip upload
  // stays as the fallback for a tenant whose administrator refuses.

  /** The admin-consent link to hand a Microsoft 365 administrator, plus the scope list it
   *  asks for so the card can say what is being approved. `url:null` with a `state` means we
   *  cannot build one yet (no tenant on the connection). `returnTo`: a sign-in window's return
   *  name, for the link opened here (never the one copied for an administrator). */
  async fetchTeamsAdminConsent(
    workspaceId: string,
    returnTo?: string,
  ): Promise<{
    configured: boolean;
    url: string | null;
    scopes: string[];
    state?: TeamsConsentState;
    message?: string;
  }> {
    const qs = new URLSearchParams({ workspaceId });
    if (returnTo) qs.set("returnTo", returnTo);
    const resp = await fetch(`${this.relayHttpBase}/api/teams/admin-consent?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${this.authToken}` },
    });
    // 409 (`no_tenant`) is a STATE with a sentence, not a transport failure — read it.
    return (await resp.json()) as {
      configured: boolean;
      url: string | null;
      scopes: string[];
      state?: TeamsConsentState;
      message?: string;
    };
  }

  /** The LIVE consent state. This is what the settings card polls: an administrator normally
   *  approves in their own browser, so our redirect never reaches the watching person and the
   *  relay has to ask Microsoft instead. */
  async fetchTeamsConsentState(
    workspaceId: string,
  ): Promise<{ state: TeamsConsentState; message: string; ready: boolean }> {
    const resp = await this.teamsFetch(
      `/api/teams/admin-consent/state?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    return (await resp.json()) as { state: TeamsConsentState; message: string; ready: boolean };
  }

  /** Publish the Cyborg7 app into the tenant catalog and install it into one team. Publishing
   *  is a no-op when it is already there, and a team that already has the app is a success. */
  async installTeamsApp(workspaceId: string, teamId: string): Promise<TeamsInstallAppResult> {
    const resp = await this.teamsFetch("/api/teams/install-app", {
      method: "POST",
      body: JSON.stringify({ workspaceId, teamId }),
    });
    return (await resp.json()) as TeamsInstallAppResult;
  }

  /** A year of daily token usage for ONE cybo — the agent profile's heatmap.
   *  Sourced from the per-cybo ledger, so it reflects only that agent's spend.
   *  An empty `dailyActivity` is normal: a new cybo, or usage predating the
   *  forward-only ledger. */
  getCyboTokenUsage(
    workspaceId: string,
    cyboId: string,
  ): Promise<{ cyboId: string; dailyActivity: Array<{ day: string; count: number }> }> {
    return this.request("cyborg:cybo_token_usage", { workspaceId, cyboId });
  }

  // ─── User status (P2 Item 6) ────────────────────────────────────

  // Persist + sync the caller's custom status for a workspace. Null emoji+text
  // clears it. Awaited: a relay refusal (cyborg:error) rejects, so the caller can undo.
  async setUserStatus(
    workspaceId: string,
    emoji: string | null,
    text: string | null,
    expiresAt: number | null,
  ): Promise<void> {
    await this.request<{ ok: boolean }>("cyborg:set_user_status", {
      workspaceId,
      emoji,
      text,
      expiresAt,
    });
  }

  // P2 Item 6: toggle the caller's manual "away" presence. Fire-and-forget; the
  // relay re-broadcasts presence (incl. awayUserIds) so co-members update live.
  // The relay persists this to user_presence so the manual away choice survives a
  // reconnect. `ephemeral` is retained on the wire for backward-compatibility with
  // older relays (it now always sends false → durable manual away only).
  setPresence(away: boolean, ephemeral = false): void {
    this.send({ type: "cyborg:set_presence", away, ephemeral });
  }

  // CYBORG-1483: Do Not Disturb, persisted by the relay so it silences push (dnd_until).
  setDnd(dnd: boolean): void {
    this.send({ type: "cyborg:set_presence", dnd });
  }

  async fetchUserStatuses(workspaceId: string): Promise<UserStatusEntry[]> {
    const resp = await this.request<{ statuses: UserStatusEntry[] }>("cyborg:fetch_user_statuses", {
      workspaceId,
    });
    return resp.statuses;
  }

  // ─── Saved messages (#609 — personal bookmarks) ─────────────────
  // Toggle a personal bookmark on/off (the analog of pinMessage, but private).
  // The save_message_broadcast echoes back to my other devices.
  /**
   * Bookmark a message. `snapshot` is required ONLY for a harness session turn:
   * it has no persisted `messages` row, so without the turn's own text the
   * server has nothing to render and refuses the save (0127).
   */
  async saveMessage(
    workspaceId: string,
    messageId: string,
    saved: boolean,
    snapshot?: { text: string; fromName?: string | null; agentId?: string | null },
  ): Promise<void> {
    await this.request("cyborg:save_message", {
      workspaceId,
      messageId,
      saved,
      ...(snapshot
        ? { text: snapshot.text, fromName: snapshot.fromName, agentId: snapshot.agentId }
        : {}),
    });
  }

  // My saved messages in this workspace, newest-saved first (full message rows).
  async listSaved(workspaceId: string): Promise<Message[]> {
    const resp = await this.request<{ messages: Message[] }>("cyborg:list_saved", { workspaceId });
    return resp.messages;
  }

  // ─── Extension: handle agent_stream messages ────────────────────

  protected override handleExtensionMessage(
    type: string,
    payload: Record<string, unknown> | undefined,
  ): boolean {
    if (type === "agent_stream") {
      // Handled via cyborg:agent_stream from message-router broadcast;
      // processing both would double-deliver every event.
      return true;
    }

    // Provider-subagent relay bridge (#1612): the OWNING daemon forwards its
    // AgentManager `provider_subagent` store events wrapped in a cyborg:agent_stream
    // envelope (message-router.forwardProviderSubagentToRelay) so a REMOTE guest —
    // which has no daemon session emitting `agent.provider_subagents.update` — still
    // receives them live. Demux the inner event back to the `provider_subagent` store
    // update (the SAME event a DIRECT client gets from the Paseo-native frame), so the
    // ProviderSubagentStore consumes one funnel regardless of transport. A normal
    // agent_stream frame (no provider_subagent inner) falls through to the passthrough.
    if (type === "cyborg:agent_stream") {
      const ev = payload?.event as { type?: string; update?: unknown } | undefined;
      if (ev?.type === "provider_subagent") {
        this.emit("provider_subagent", ev.update as never);
        return true;
      }
    }

    // Straight-through broadcasts: each `cyborg:<wire>` frame re-emits as its typed
    // CyborgEventMap event with no transform. Table-driven (not a branch per type)
    // so adding a passthrough — e.g. #995's `cyborg:audit_event` — is one map row,
    // and the method's cyclomatic complexity stays flat.
    const passthrough = SIMPLE_PASSTHROUGH[type];
    if (passthrough) {
      this.emit(passthrough, payload as never);
      return true;
    }

    // Terminal stream + start/attach acks (#654/#673) → fan out to terminalSocket()
    // subscribers. The *_response acks aren't request()-correlated here because the
    // transport sends start/attach via the raw socket, not request(). attach_terminal_response
    // MUST be here: without it the transport's attach() never resolves, times out (15s), and
    // falls back to a fresh start() — dropping the live pty on every tab-switch re-attach.
    // cyborg:terminal_snapshot (knowledge/51 Phase 0/1) is the screen self-heal frame
    // Paseo emits on every (re)subscribe — it MUST be whitelisted here too, or the
    // repaint never reaches TerminalView (the exact class of omission that caused #789;
    // a regression test asserts its presence — terminal-resubscribe.integration.test.ts).
    if (
      type === "cyborg:terminal_output" ||
      type === "cyborg:terminal_exit" ||
      type === "cyborg:terminal_snapshot" ||
      type === "cyborg:start_terminal_response" ||
      type === "cyborg:attach_terminal_response" ||
      // revive() resolves on this ack.
      type === "cyborg:revive_terminal_response" ||
      // The workspace terminal DIRECTORY push (terminal CLI-UI unification): the
      // daemon broadcasts the full snapshot on every start/exit so out-of-band
      // (CLI / other-client) terminals surface in the sidebar live. Fanned out to
      // onTerminalsChanged subscribers via terminalSocket().
      type === "cyborg:terminals_changed" ||
      // The per-user terminal-alias change push: a rename/clear on one of the
      // user's devices, fanned out to the rest via onTerminalAliasChanged.
      type === "cyborg:terminal_alias_changed" ||
      // Same for session aliases (e.g. renamed from the CLI): onSessionAliasChanged.
      type === "cyborg:session_alias_changed" ||
      // A session's shared name, renamed by its owner: onSessionNameChanged.
      type === "cyborg:session_name_changed"
    ) {
      this.fanOutTerminalFrame(type, payload ?? {});
      return true;
    }
    // A raw-socket terminal frame the relay refused (a subscribe to an offline daemon) is
    // answered by cyborg:error with its requestId: the transport waits on it. Still a broadcast.
    if (type === "cyborg:error") this.fanOutTerminalFrame(type, payload ?? {});

    return false;
  }

  // Snapshot before iterating: a handler may unsubscribe itself (mutating
  // the set), and one throwing handler must not starve the rest of the frame.
  private fanOutTerminalFrame(type: string, payload: Record<string, unknown>): void {
    for (const handler of Array.from(this.terminalListeners.get(type) ?? [])) {
      try {
        handler(payload);
      } catch (err) {
        if (shouldReport(`terminal.listener_threw:${type}`, LISTENER_REPORT_WINDOW_MS)) {
          reportUiFailure("terminal.listener_threw", err, { frameType: type });
        }
      }
    }
  }
}

// [lane:w3-episodes-usage] W3-14: one note a message used, as this viewer may see it.
export interface MessageMemoryUseWire {
  noteId: string;
  cyboId: string;
  venue: string | null;
  slug: string | null;
  /** the relay's address of the note; sent where the slug is */
  handle?: string;
  /** null: the note was forgotten */
  title: string | null;
  forgotten: boolean;
  sourceRoom: { kind: "channel" | "dm" | "none"; name: string | null };
  modified: string;
  /** the viewer owns the note or asked the turn */
  canManage: boolean;
}
